// ActiveRallyVideo.tsx
import * as React from "react";
import { createPortal } from "react-dom";
import {
  Box,
  Button,
  Typography,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Paper,
  Divider,
  TextField,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReplayIcon from "@mui/icons-material/Replay";
import CropFreeIcon from "@mui/icons-material/CropFree";
import LoopIcon from "@mui/icons-material/CropFree";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import LaunchIcon from "@mui/icons-material/Launch";
import MovieCreationOutlinedIcon from "@mui/icons-material/MovieCreationOutlined";

import type {
  KitchenArrivalRow,
  PbvShot,
  ShotNumberOverlay,
} from "./RallyBoardPopup";
import { ShotNumberOverlayLayer } from "./RallyPopup";
import {
  FocusedReasonStakeForTeam,
  PlayerIndexMap,
  RenderPlayerIdentity,
  Team01,
  Team_0,
  Team_1,
  shotMatchesFocusedFilter,
  teamOfPlayerId,
  teamRgba,
} from "./GameStoryDialog_UPDATED";
import {
  VideoOverlayControls,
  type VideoPlaybackRate,
} from "./VideoOverlayControls";
import {
  createVideoPlaybackId,
  pauseOtherActiveVideos,
  registerActiveVideoPlayback,
} from "./activeVideoPlaybackCoordinator";
import { CondensedRallyTimeline_ContextOnly } from "./CondensedRallyTimeline_ContextOnly";
import { FirefightClip } from "./GameStory_firefights";

import {
  ServePaddleIcon,
  buildPressureByRallyIndexFromTimelineRows,
  scorePressureForTeam,
  clamp01,
} from "./CondensedRallyTimeline";

import * as ReactDOM from "react-dom";
import { ClipFocus } from "../../shared/types/askAiBasicQuestionTypes";
// import {
//   buildRallyFingerprintsFromRows,
//   findSimilarRallies,
//   SimilarityMode,
//   SimilarRallyResult,
//   SimilarityReason,
//   RallyFingerprint,
// } from "./findSimilarRallies";

import { useWorldToScreenFromCv } from "./useWorldToScreenFromCv";
import {
  sampleQuadraticWorldTrajectory,
  useApprox3DTrajectoryProjectionFromCv,
} from "./trajectoryProjectionApprox3d";

import {
  // buildBallUvPolylineForTimeRange,
  // buildBallUvCirclesForTimeRange,
  buildBallUvPolylineForRally,
  extractBallUvSamplesForAbsoluteShotWindow,
  extractBallUvSamplesForRally,
  extractBallUvSamplesForRallyShotWindow,
} from "./buildBallUvPolyineFromCv";

import type { CvJson } from "../../shared/types/cvJson";
import { PounceOverlay } from "./GameStory_pounceWindows";
import { TRIAGE_TOOLTIP_POPPER_SX } from "./GameStory_TriagePopup";
import { FAST_SHOT_MPH_THRESHOLD } from "./GameStory_thresholds";

export type ActiveRallyLensKey =
  | "off"
  | "candidateSpeedups"
  | "candidateBodybags"
  | "possiblePopups"
  | "paddleReadiness"
  | "leftSideCoverage";

export type ActiveRallyLensCourtPlayer = {
  playerId: number;
  role: "reviewed" | "hitter" | "partner";
  x: number;
  y: number;
  team?: 0 | 1 | null;
};

export type ActiveRallyLensMoment = {
  key: string;
  lens: Exclude<ActiveRallyLensKey, "off">;
  rallyNumber?: number;
  shotNumber: number;
  timeSec: number;
  loopStartSec?: number;
  loopEndSec?: number;
  title: string;
  detail?: string;
  reviewedPlayerId?: number;
  hitterPlayerId?: number;
  partnerPlayerId?: number;
  reviewedTeam?: 0 | 1 | null;
  rallyWinnerTeamId?: 0 | 1 | null;
  partnerSideDistanceFt?: number | null;
  shotPath?: Array<{ x: number; y: number }>;
  shotPaths?: Array<{
    shotNumber: number;
    playerId?: number;
    team?: 0 | 1 | null;
    startSec?: number;
    endSec?: number;
    points: Array<{ x: number; y: number }>;
  }>;
  courtPlayers?: ActiveRallyLensCourtPlayer[];
};

type NextBallReadStage = "read" | "revealing" | "revealed";

type NextBallReadRep = {
  rallyIndex: number | null;
  shotNumber: number;
  shotType: string | null;
  shotSec: number;
  nextShotSec: number | null;
  readSec: number;
  revealEndSec: number;
  overlayIndex: number;
  total: number;
  source?: "rally" | "random-kitchen";
};

type ActiveNextBallRead = {
  stage: NextBallReadStage;
  rep: NextBallReadRep;
};

type NextBallReadSeed = ActiveNextBallRead & {
  nonce: number;
};

function nextBallReadLeadSec(shotType: string | null | undefined): number {
  const normalized = (shotType ?? "").toLowerCase();
  if (normalized.includes("dink") || normalized.includes("drop")) return 0.2;
  if (normalized.includes("serve") || normalized.includes("return")) return 0.3;
  if (normalized.includes("drive") || normalized.includes("speed")) return 0.16;
  return 0.25;
}

function isKitchenNextBallReadCandidate(rep: NextBallReadRep): boolean {
  if (rep.nextShotSec == null || rep.shotNumber < 5) return false;

  const normalized = (rep.shotType ?? "").toLowerCase();
  if (normalized.includes("serve") || normalized.includes("return")) return false;

  const timeToNextShot = rep.nextShotSec - rep.shotSec;
  if (timeToNextShot < 0.18 || timeToNextShot > 3.75) return false;

  return (
    normalized.includes("dink") ||
    normalized.includes("volley") ||
    normalized.includes("drop") ||
    normalized.includes("reset") ||
    normalized.includes("speed") ||
    normalized.includes("drive") ||
    normalized.includes("attack") ||
    normalized.includes("counter") ||
    normalized.includes("block") ||
    normalized.includes("lift")
  );
}

function getNextBallReadRowDurationSec(row: KitchenArrivalRow): number {
  const startMs = row.segmentStartMs ?? row.startMs ?? 0;
  const endMs = row.segmentEndMs ?? row.endMs ?? startMs;
  return Math.max(0, (endMs - startMs) / 1000);
}

function buildNextBallReadRepsForRow(
  row: KitchenArrivalRow | null,
  durationSecOverride?: number | null,
  source: NextBallReadRep["source"] = "rally",
): NextBallReadRep[] {
  const d =
    durationSecOverride != null && durationSecOverride > 0
      ? durationSecOverride
      : row
        ? getNextBallReadRowDurationSec(row)
        : 0;
  if (!row || d <= 0) return [];

  const overlays = [...(row.shotNumberOverlays ?? [])]
    .map((overlay) => ({
      overlay,
      shotMs: getOverlayMsRel(
        overlay,
        row.segmentStartMs,
        row.segmentEndMs,
      ),
    }))
    .filter(({ shotMs }) => Number.isFinite(shotMs) && shotMs >= 0)
    .sort((a, b) => a.shotMs - b.shotMs);

  return overlays.map(({ overlay, shotMs }, index) => {
    const nextShotMs = overlays[index + 1]?.shotMs ?? null;
    const shotSec = shotMs / 1000;
    const nextShotSec = nextShotMs != null ? nextShotMs / 1000 : null;
    const leadSec = nextBallReadLeadSec(overlay.shotType);
    const revealEndSec = clamp(
      nextShotSec != null ? nextShotSec - 0.07 : shotSec + 1.25,
      shotSec + 0.25,
      Math.max(shotSec + 0.35, d),
    );

    return {
      rallyIndex: row.rallyIndex ?? null,
      shotNumber: overlay.shotNumber,
      shotType: overlay.shotType,
      shotSec,
      nextShotSec,
      readSec: clamp(shotSec - leadSec, 0, Math.max(0, d)),
      revealEndSec,
      overlayIndex: index,
      total: overlays.length,
      source,
    };
  });
}

function activeRallyLensLabel(lens: ActiveRallyLensKey): string {
  if (lens === "candidateSpeedups") return "Speedups";
  if (lens === "candidateBodybags") return "Jammed / bodybag";
  if (lens === "possiblePopups") return "Possible popups";
  if (lens === "paddleReadiness") return "Paddle readiness";
  if (lens === "leftSideCoverage") return "Left-side coverage";
  return "Lens";
}

function activeRallyLensCountLabel(count: number): string {
  return count === 1 ? "1 moment" : `${count} moments`;
}

function activeRallyLensLongLabel(lens: ActiveRallyLensKey): string {
  if (lens === "candidateSpeedups") return "Candidate speedups";
  if (lens === "candidateBodybags") return "Jammed / Bodybag Candidate";
  if (lens === "possiblePopups") return "Possible Popup Candidate";
  if (lens === "paddleReadiness") return "Paddle Readiness Moment";
  if (lens === "leftSideCoverage") return "Left-Side Coverage Moment";
  return "Rally lens";
}

function activeRallyLensShotActionLabel(lens: ActiveRallyLensKey): string {
  if (lens === "candidateSpeedups") return "What happened on this shot";
  if (lens === "candidateBodybags") return "Why this clip was surfaced";
  if (lens === "possiblePopups") return "Why this trajectory was surfaced";
  if (lens === "paddleReadiness") return "Readiness frame";
  if (lens === "leftSideCoverage") return "Coverage read";
  return "Shot context";
}

function lensRoleColor(role: ActiveRallyLensCourtPlayer["role"]): string {
  if (role === "reviewed") return "#29b6f6";
  if (role === "hitter") return "#ffb74d";
  return "#81c784";
}

function clampCourtPct(value: number, max: number): number {
  if (!Number.isFinite(value) || max <= 0) return 50;
  return Math.max(0, Math.min(100, (value / max) * 100));
}

function lensPathPoint(point: { x: number; y: number }): { x: number; y: number } {
  return {
    x: clampCourtPct(point.x, 20),
    y: clampCourtPct(point.y, 44),
  };
}

function lensPathPointAtProgress(
  points: Array<{ x: number; y: number }>,
  progress: number,
): { x: number; y: number } | null {
  if (!points.length) return null;
  if (points.length === 1) return lensPathPoint(points[0]!);

  const normalized = points.map(lensPathPoint);
  const lengths: number[] = [];
  let total = 0;
  for (let i = 1; i < normalized.length; i += 1) {
    const prev = normalized[i - 1]!;
    const next = normalized[i]!;
    const length = Math.hypot(next.x - prev.x, next.y - prev.y);
    lengths.push(length);
    total += length;
  }
  if (total <= 0) return normalized[normalized.length - 1]!;

  const target = Math.max(0, Math.min(1, progress)) * total;
  let walked = 0;
  for (let i = 1; i < normalized.length; i += 1) {
    const segment = lengths[i - 1] ?? 0;
    if (walked + segment >= target) {
      const prev = normalized[i - 1]!;
      const next = normalized[i]!;
      const local = segment <= 0 ? 0 : (target - walked) / segment;
      return {
        x: prev.x + (next.x - prev.x) * local,
        y: prev.y + (next.y - prev.y) * local,
      };
    }
    walked += segment;
  }

  return normalized[normalized.length - 1]!;
}

function lensPathPointsThroughProgress(
  points: Array<{ x: number; y: number }>,
  progress: number,
): Array<{ x: number; y: number }> {
  if (!points.length) return [];
  const normalized = points.map(lensPathPoint);
  if (normalized.length === 1) return normalized;

  const clampedProgress = Math.max(0, Math.min(1, progress));
  const lengths: number[] = [];
  let total = 0;
  for (let i = 1; i < normalized.length; i += 1) {
    const prev = normalized[i - 1]!;
    const next = normalized[i]!;
    const length = Math.hypot(next.x - prev.x, next.y - prev.y);
    lengths.push(length);
    total += length;
  }
  if (total <= 0) return [normalized[0]!, normalized[normalized.length - 1]!];

  const target = clampedProgress * total;
  const traced: Array<{ x: number; y: number }> = [normalized[0]!];
  let walked = 0;
  for (let i = 1; i < normalized.length; i += 1) {
    const segment = lengths[i - 1] ?? 0;
    const prev = normalized[i - 1]!;
    const next = normalized[i]!;
    if (walked + segment < target) {
      traced.push(next);
      walked += segment;
      continue;
    }
    const local = segment <= 0 ? 0 : (target - walked) / segment;
    traced.push({
      x: prev.x + (next.x - prev.x) * local,
      y: prev.y + (next.y - prev.y) * local,
    });
    return traced;
  }

  return traced;
}

function ActiveRallyLensMiniCourt(props: {
  moment: ActiveRallyLensMoment | null;
  playheadSec: number;
  showTrajectory?: boolean;
  compact?: boolean;
}) {
  const players = props.moment?.courtPlayers ?? [];
  const fallbackShotPaths =
    props.moment?.shotPath && props.moment.shotPath.length >= 2
      ? [
          {
            shotNumber: props.moment.shotNumber,
            playerId: props.moment.hitterPlayerId,
            team: props.moment.reviewedTeam,
            startSec: props.moment.loopStartSec,
            endSec: props.moment.loopEndSec,
            points: props.moment.shotPath,
          },
        ]
      : [];
  const shotPaths =
    props.showTrajectory === false
      ? []
      : props.moment?.shotPaths?.filter((path) => path.points.length >= 2) ??
        fallbackShotPaths;
  const activePath =
    shotPaths.find(
      (path) =>
        path.startSec != null &&
        path.endSec != null &&
        props.playheadSec >= path.startSec &&
        props.playheadSec <= path.endSec,
    ) ??
    shotPaths.find((path) => path.shotNumber === props.moment?.shotNumber) ??
    shotPaths[0] ??
    null;
  const activePathProgress =
    activePath?.startSec != null &&
    activePath?.endSec != null &&
    activePath.endSec > activePath.startSec
      ? (props.playheadSec - activePath.startSec) /
        (activePath.endSec - activePath.startSec)
      : activePath
        ? 1
        : 0;
  const activeBallPoint = activePath
    ? lensPathPointAtProgress(activePath.points, activePathProgress)
    : null;
  const activePathPoints =
    activePath != null
      ? lensPathPointsThroughProgress(activePath.points, activePathProgress)
      : [];
  const activePathPolylinePoints = activePathPoints
    .map((point) => `${point.x},${point.y}`)
    .join(" ");
  const activePathIsOpponent =
    props.moment?.reviewedTeam != null &&
    activePath?.team != null &&
    activePath.team !== props.moment.reviewedTeam;
  return (
    <Box
      sx={{
        position: "relative",
        width: props.compact ? 96 : 132,
        height: props.compact ? 76 : 104,
        flex: "0 0 auto",
        border: "1px solid rgba(255,255,255,0.30)",
        borderRadius: 1,
        bgcolor: "rgba(255,255,255,0.035)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: 0,
          bottom: 0,
          borderLeft: "1px solid rgba(255,255,255,0.18)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: "50%",
          borderTop: "2px solid rgba(255,255,255,0.36)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: `${(15 / 44) * 100}%`,
          borderTop: "1px solid rgba(255,255,255,0.16)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: `${(29 / 44) * 100}%`,
          borderTop: "1px solid rgba(255,255,255,0.16)",
        }}
      />
      {activePath && activePathPoints.length >= 2 ? (
        <Box
          component="svg"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
          }}
        >
          <polyline
            points={activePathPolylinePoints}
            fill="none"
            stroke={
              activePathIsOpponent
                ? "rgba(255,255,255,0.82)"
                : "rgba(255,214,102,0.92)"
            }
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {activeBallPoint ? (
            <circle
              cx={activeBallPoint.x}
              cy={activeBallPoint.y}
              r="3.1"
              fill="rgba(255,255,255,0.98)"
              stroke="rgba(0,0,0,0.52)"
              strokeWidth="0.9"
            />
          ) : null}
        </Box>
      ) : null}
      {players.length > 0 ? (
        players.map((player) => (
          <Box
            key={`${player.role}-${player.playerId}`}
            title={`player ${player.playerId} ${player.role}`}
            sx={{
              position: "absolute",
              left: `${clampCourtPct(player.x, 20)}%`,
              top: `${clampCourtPct(player.y, 44)}%`,
              transform: "translate(-50%, -50%)",
              width: props.compact
                ? player.role === "reviewed"
                  ? 12
                  : 10
                : player.role === "reviewed"
                  ? 15
                  : 12,
              height: props.compact
                ? player.role === "reviewed"
                  ? 12
                  : 10
                : player.role === "reviewed"
                  ? 15
                  : 12,
              borderRadius: "50%",
              bgcolor: lensRoleColor(player.role),
              border: "2px solid rgba(255,255,255,0.80)",
              boxShadow:
                player.role === "reviewed"
                  ? "0 0 0 3px rgba(41,182,246,0.22), 0 0 14px rgba(41,182,246,0.36)"
                  : "0 2px 8px rgba(0,0,0,0.30)",
            }}
          />
        ))
      ) : (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 1,
            textAlign: "center",
            fontSize: 10.5,
            color: "rgba(255,255,255,0.58)",
          }}
        >
          court position not available
        </Box>
      )}
    </Box>
  );
}

function ActiveRallyLensPlayerIdentity(props: {
  playerId: number | null | undefined;
  label: string;
  color: string;
  insightsToCvPlayerIndex: PlayerIndexMap;
  renderPlayerIdentity?: RenderPlayerIdentity;
}) {
  if (typeof props.playerId !== "number") return null;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.45,
        minWidth: 0,
        px: 0.45,
        py: 0.25,
        borderRadius: 1,
        bgcolor: "rgba(255,255,255,0.06)",
        border: `1px solid ${props.color}`,
      }}
    >
      <Box
        component="span"
        sx={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          bgcolor: props.color,
          flex: "0 0 auto",
        }}
      />
      {props.label ? (
        <Typography
          component="span"
          sx={{
            fontSize: 10,
            fontWeight: 900,
            color: "rgba(255,255,255,0.76)",
            whiteSpace: "nowrap",
          }}
        >
          {props.label}
        </Typography>
      ) : null}
      {props.renderPlayerIdentity ? (
        <Box
          sx={{
            color: "white",
            "& *": {
              color: "white",
            },
          }}
        >
          {props.renderPlayerIdentity({
            playerId: props.playerId,
            labelOnly: false,
            size: 30,
            borderRadius: 5,
            fontSize: 12,
            fontColor: "white",
          })}
        </Box>
      ) : (
        <Typography
          component="span"
          sx={{ fontSize: 12, fontWeight: 950, color: "white" }}
        >
          p{props.playerId}
        </Typography>
      )}
    </Box>
  );
}

function ActiveRallyLensPanel(props: {
  lens: ActiveRallyLensKey;
  moments: ActiveRallyLensMoment[];
  currentMoment: ActiveRallyLensMoment | null;
  nextMoment: ActiveRallyLensMoment | null;
  selectedMomentKey: string | null;
  onSeek: (moment: ActiveRallyLensMoment) => void;
  isInFullPopup: boolean;
  placement?: "default" | "embeddedBottom";
  playheadSec: number;
  insightsToCvPlayerIndex: PlayerIndexMap;
  renderPlayerIdentity?: RenderPlayerIdentity;
}) {
  const selectedMoment =
    props.moments.find((moment) => moment.key === props.selectedMomentKey) ??
    null;
  const displayMoment = selectedMoment ?? props.currentMoment ?? props.nextMoment;
  if (props.lens === "off" || props.moments.length === 0 || !displayMoment) {
    return null;
  }

  const currentIndex = props.moments.findIndex(
    (moment) => moment.key === displayMoment.key,
  );
  const isLeftSideCoverageLens = props.lens === "leftSideCoverage";
  const isPaddleReadinessLens = props.lens === "paddleReadiness";
  const reviewedRoleLabel =
    isLeftSideCoverageLens ? "Left-side" : "reviewed";
  const partnerRoleLabel =
    isLeftSideCoverageLens ? "Right-side" : "partner";
  const roleChips = isLeftSideCoverageLens
    ? [
        {
          playerId: displayMoment.reviewedPlayerId,
          label:
            displayMoment.hitterPlayerId === displayMoment.reviewedPlayerId
              ? `${reviewedRoleLabel} / hitter`
              : reviewedRoleLabel,
          color: lensRoleColor("reviewed"),
        },
        {
          playerId: displayMoment.partnerPlayerId,
          label:
            displayMoment.hitterPlayerId === displayMoment.partnerPlayerId
              ? `${partnerRoleLabel} / hitter`
              : partnerRoleLabel,
          color: lensRoleColor("partner"),
        },
        displayMoment.hitterPlayerId !== displayMoment.reviewedPlayerId &&
        displayMoment.hitterPlayerId !== displayMoment.partnerPlayerId
          ? {
              playerId: displayMoment.hitterPlayerId,
              label: "hitter",
              color: lensRoleColor("hitter"),
            }
          : null,
      ].filter(Boolean)
    : [
        {
          playerId: displayMoment.reviewedPlayerId,
          label: reviewedRoleLabel,
          color: lensRoleColor("reviewed"),
        },
        {
          playerId: displayMoment.hitterPlayerId,
          label: "hitter",
          color: lensRoleColor("hitter"),
        },
        {
          playerId: displayMoment.partnerPlayerId,
          label: partnerRoleLabel,
          color: lensRoleColor("partner"),
        },
      ];
  const leftSideTeamLabel =
    displayMoment.reviewedTeam === 0 || displayMoment.reviewedTeam === 1
      ? `Team ${displayMoment.reviewedTeam}`
      : null;
  const rallyWinnerLabel =
    displayMoment.rallyWinnerTeamId === 0 ||
    displayMoment.rallyWinnerTeamId === 1
      ? `Winner Team ${displayMoment.rallyWinnerTeamId}`
      : null;
  const sideLabelForPlayer = (
    playerId: number | null | undefined,
  ): string | null => {
    if (typeof playerId !== "number") return null;
    const player = displayMoment.courtPlayers?.find(
      (p) => p.playerId === playerId,
    );
    if (!player || typeof player.y !== "number") return null;
    return player.y >= 22 ? "near" : "far";
  };
  const sideLabelForTeam = (team: 0 | 1 | null | undefined): string | null => {
    if (team !== 0 && team !== 1) return null;
    const player = displayMoment.courtPlayers?.find((p) => p.team === team);
    if (!player || typeof player.y !== "number") return null;
    return player.y >= 22 ? "near" : "far";
  };
  const reviewedPlayerSide = sideLabelForPlayer(displayMoment.reviewedPlayerId);
  const leftSideTeamWithSide =
    leftSideTeamLabel && reviewedPlayerSide
      ? `${leftSideTeamLabel} (${reviewedPlayerSide})`
      : leftSideTeamLabel;
  const rallyWinnerWithSide =
    rallyWinnerLabel && sideLabelForTeam(displayMoment.rallyWinnerTeamId)
      ? `${rallyWinnerLabel} (${sideLabelForTeam(displayMoment.rallyWinnerTeamId)})`
      : rallyWinnerLabel;
  const partnerSideDistanceLabel =
    typeof displayMoment.partnerSideDistanceFt === "number" &&
    Number.isFinite(displayMoment.partnerSideDistanceFt)
      ? `Partner-side ${displayMoment.partnerSideDistanceFt.toFixed(1)} ft`
      : null;
  const embeddedBottom = props.placement === "embeddedBottom";

  return (
    <Paper
      data-demo="active-rally-lens-panel"
      data-rally-lens={props.lens}
      elevation={6}
      sx={{
        position: "absolute",
        left: embeddedBottom ? "50%" : props.isInFullPopup ? 18 : 12,
        top: embeddedBottom
          ? "auto"
          : isLeftSideCoverageLens
            ? props.isInFullPopup
              ? 124
              : 18
            : isPaddleReadinessLens
              ? props.isInFullPopup
                ? 128
                : 46
            : props.isInFullPopup
              ? 58
              : 46,
        bottom: embeddedBottom ? 68 : "auto",
        transform: embeddedBottom ? "translateX(-50%)" : "none",
        zIndex: 135,
        width: isLeftSideCoverageLens
          ? embeddedBottom
            ? 430
            : props.isInFullPopup
            ? 500
            : 410
          : props.isInFullPopup
            ? isPaddleReadinessLens
              ? 360
              : 470
            : isPaddleReadinessLens
              ? 330
              : 390,
        maxWidth: "calc(100% - 24px)",
        p: 1,
        display: "flex",
        gap: 1,
        borderRadius: 2,
        color: "white",
        bgcolor: "rgba(6,16,22,0.82)",
        border: "1px solid rgba(80,200,255,0.42)",
        boxShadow: "0 10px 30px rgba(0,0,0,0.42)",
        backdropFilter: "blur(9px)",
        pointerEvents: "auto",
      }}
    >
      <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
        {!isLeftSideCoverageLens ? (
          <Typography
            sx={{
              fontSize: 10,
              lineHeight: 1.1,
              letterSpacing: 1.1,
              fontWeight: 900,
              color: "#80d8ff",
              textTransform: "uppercase",
            }}
          >
            {isPaddleReadinessLens
              ? "READINESS FRAME"
              : activeRallyLensLongLabel(props.lens)}
            {currentIndex >= 0
              ? ` ${currentIndex + 1}/${props.moments.length}`
              : ` ${props.moments.length}`}
          </Typography>
        ) : null}
        {isLeftSideCoverageLens ? (
          <Box
            sx={{
              mt: 0,
              display: "flex",
              alignItems: "baseline",
              gap: 0.65,
              flexWrap: "nowrap",
              minWidth: 0,
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: embeddedBottom ? 14 : props.isInFullPopup ? 17 : 15,
                lineHeight: 1.15,
                fontWeight: 950,
                color: "white",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayMoment.title}
              {leftSideTeamWithSide ? ` ${leftSideTeamWithSide}` : ""}
            </Typography>
          </Box>
        ) : null}
        <Box
          sx={{
            mt: 0.5,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexWrap: "wrap",
          }}
        >
          {roleChips.map((chip) => (
            <ActiveRallyLensPlayerIdentity
              key={`${chip?.label}-${chip?.playerId}`}
              playerId={chip?.playerId}
              label={chip?.label ?? ""}
              color={chip?.color ?? "rgba(255,255,255,0.4)"}
              insightsToCvPlayerIndex={props.insightsToCvPlayerIndex}
              renderPlayerIdentity={props.renderPlayerIdentity}
            />
          ))}
        </Box>
        {!isLeftSideCoverageLens && !isPaddleReadinessLens ? (
          <>
            <Typography
              sx={{
                mt: 0.5,
                fontSize: 10.5,
                lineHeight: 1.2,
                color: "rgba(255,255,255,0.66)",
              }}
            >
              Dot colors match roles on the mini court: blue reviewed, orange
              hitter, green partner.
            </Typography>
            <Typography
              sx={{
                mt: 0.55,
                fontSize: 10,
                lineHeight: 1.1,
                letterSpacing: 0.7,
                fontWeight: 900,
                color: "rgba(128,216,255,0.88)",
                textTransform: "uppercase",
              }}
            >
              {activeRallyLensShotActionLabel(props.lens)}
            </Typography>
          </>
        ) : null}
        {isLeftSideCoverageLens ? null : (
          <Typography
            sx={{
              mt: 0.25,
              fontSize: props.isInFullPopup ? 16 : 14,
              lineHeight: 1.15,
              fontWeight: 900,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {displayMoment.title}
          </Typography>
        )}
        {!isLeftSideCoverageLens && displayMoment.detail ? (
          <Typography
            sx={{
              mt: 0.35,
              fontSize: isPaddleReadinessLens ? 13 : 12,
              lineHeight: 1.25,
              color: isPaddleReadinessLens
                ? "rgba(255,255,255,0.86)"
                : "rgba(255,255,255,0.76)",
              display: "-webkit-box",
              WebkitLineClamp: isPaddleReadinessLens ? 1 : 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {displayMoment.detail}
          </Typography>
        ) : null}
        {!isLeftSideCoverageLens && !isPaddleReadinessLens ? (
          <Box
            sx={{
              mt: 0.75,
              display: "flex",
              gap: 0.5,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {props.moments.slice(0, 10).map((moment, index) => (
              <Button
                key={moment.key}
                size="small"
                variant={moment.key === displayMoment.key ? "contained" : "outlined"}
                onClick={() => props.onSeek(moment)}
                sx={{
                  minWidth: 26,
                  height: 22,
                  px: 0.6,
                  py: 0,
                  color: "white",
                  borderColor: "rgba(128,216,255,0.55)",
                  bgcolor:
                    moment.key === displayMoment.key
                      ? "rgba(41,182,246,0.78)"
                      : "rgba(255,255,255,0.06)",
                  fontSize: 11,
                  fontWeight: 900,
                }}
              >
                {index + 1}
              </Button>
            ))}
            {props.moments.length > 10 ? (
              <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.62)" }}>
                +{props.moments.length - 10}
              </Typography>
            ) : null}
          </Box>
        ) : null}
        {isLeftSideCoverageLens ? (
          <Typography
            sx={{
              mt: 0.7,
              fontSize: 11,
              lineHeight: 1.1,
              fontWeight: 900,
              color: "rgba(255,255,255,0.64)",
            }}
          >
            {displayMoment.rallyNumber != null
              ? `Rally ${displayMoment.rallyNumber}`
              : "Rally"}
            {" · "}Shot {displayMoment.shotNumber}
            {rallyWinnerWithSide ? ` · ${rallyWinnerWithSide}` : ""}
            {partnerSideDistanceLabel ? ` · ${partnerSideDistanceLabel}` : ""}
          </Typography>
        ) : null}
      </Box>
    </Paper>
  );
}


function bestAiAnswerText(t: AiTurn): string {
  if (!t) return "";

  if (t.status === "error" && typeof t.error === "string" && t.error.trim()) {
    return t.error.trim();
  }

  if (typeof t.responseText === "string" && t.responseText.trim()) {
    return t.responseText.trim();
  }

  const r = t.responseJson ?? null;

  if (r && typeof r === "object") {
    if (typeof r.answerToQuestion === "string" && r.answerToQuestion.trim()) {
      return r.answerToQuestion.trim();
    }

    if (
      typeof r?.rallyOverview?.headline === "string" &&
      r.rallyOverview.headline.trim()
    ) {
      return r.rallyOverview.headline.trim();
    }

    if (
      typeof r?.rallyOverview?.keyTurningPoint === "string" &&
      r.rallyOverview.keyTurningPoint.trim()
    ) {
      return `Turning point: ${r.rallyOverview.keyTurningPoint.trim()}`;
    }
  }

  return "";
}

export function LoopScrubberIconLoop(props: { size: number; color?: string }) {
  const { size, color = "rgba(255,255,255,0.92)" } = props;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      style={{ display: "block" }}
    >
      {/* Timeline bar */}
      <path
        d="M4 15H20"
        stroke={color}
        strokeWidth={1.8}
        strokeLinecap="round"
      />

      {/* Range endpoints */}
      <path
        d="M7 11V19"
        stroke={color}
        strokeWidth={1.8}
        strokeLinecap="round"
      />
      <path
        d="M17 11V19"
        stroke={color}
        strokeWidth={1.8}
        strokeLinecap="round"
      />

      {/* Playhead */}
      <path
        d="M12 9V21"
        stroke={color}
        strokeWidth={1.8}
        strokeLinecap="round"
      />

      {/* Loop arrow above (small, doesn’t dominate) */}
      <path
        d="M9 6.8h6.2c1.1 0 2 .9 2 2v.2"
        stroke={color}
        strokeWidth={1.6}
        strokeLinecap="round"
        opacity={0.95}
      />
      <path
        d="M17.2 9.2l-1.6-1.4m1.6 1.4l-1.3 1.7"
        stroke={color}
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={0.95}
      />

      {/* Little “handle” dots (subtle cue of draggable UI) */}
      <circle cx="7" cy="15" r="1" fill={color} opacity={0.9} />
      <circle cx="17" cy="15" r="1" fill={color} opacity={0.9} />
    </svg>
  );
}

export function LoopBracketTimelineIcon(props: {
  size?: number; // set 22–26 for a 30x30 IconButton
  color?: string; // e.g. "rgba(255,255,255,0.92)"
  strokeWidth?: number; // 2.0–2.4 looks good
}) {
  const {
    size = 24,
    color = "rgba(255,255,255,0.92)",
    strokeWidth = 2.2,
  } = props;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      style={{ display: "block" }}
    >
      {/* Brackets (chunky, readable) */}
      <path
        d="M6.2 7.2H4.6V16.8H6.2"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17.8 7.2H19.4V16.8H17.8"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Timeline track */}
      <path
        d="M8.2 12H15.8"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />

      {/* Subtle ticks (help it read as “timeline/scrubber”) */}
      {/* <path
        d="M9.2 10.4V13.6M12 10.0V14.0M14.8 10.4V13.6"
        stroke={color}
        strokeWidth={Math.max(1.6, strokeWidth - 0.6)}
        strokeLinecap="round"
        opacity={0.9}
      /> */}

      {/* Tiny playhead dot (optional but nice) */}
      {/* <circle cx="12" cy="12" r="1.35" fill={color} opacity={0.95} /> */}
    </svg>
  );
}
export function LoopHandlesIcon({ size = 18, color = "white" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24">
      <polygon points="6,12 9,10 9,14" fill={color} />
      <polygon points="18,12 15,10 15,14" fill={color} />
      <line x1="9" y1="12" x2="15" y2="12" stroke={color} strokeWidth="2" />
    </svg>
  );
}

export function LoopScrubberIcon(props: { size: number; color?: string }) {
  const { size, color = "rgba(255,255,255,0.92)" } = props;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      style={{ display: "block" }}
    >
      {/* Timeline bar */}
      <path
        d="M4 14.5H20"
        stroke={color}
        strokeWidth={1.8}
        strokeLinecap="round"
      />

      {/* Range endpoints (A/B) */}
      <path
        d="M7 10.2V18.8"
        stroke={color}
        strokeWidth={1.8}
        strokeLinecap="round"
      />
      <path
        d="M17 10.2V18.8"
        stroke={color}
        strokeWidth={1.8}
        strokeLinecap="round"
      />

      {/* Small caps on endpoints (helps read as “markers”) */}
      <path
        d="M6 10.2H8"
        stroke={color}
        strokeWidth={1.8}
        strokeLinecap="round"
      />
      <path
        d="M16 10.2H18"
        stroke={color}
        strokeWidth={1.8}
        strokeLinecap="round"
      />

      {/* Playhead */}
      <path
        d="M12 8V20"
        stroke={color}
        strokeWidth={1.8}
        strokeLinecap="round"
        opacity={0.95}
      />
      <circle cx="12" cy="14.5" r="2.1" stroke={color} strokeWidth={1.6} />
    </svg>
  );
}
// export const Team_0: Team01 = 0;
// export const Team_1: Team01 = 1;

function pointDroughtPressureForTeam(p: any, team: 0 | 1): number {
  const n = p?.pointDroughtByTeamBefore?.[team];
  // Normalize: 0..6+ => 0..1
  return clamp01((typeof n === "number" ? n : 0) / 6);
}

function rallyWinDroughtPressureForTeam(p: any, team: 0 | 1): number {
  const n = p?.rallyWinDroughtByTeamBefore?.[team];
  // Normalize: 0..6+ => 0..1
  return clamp01((typeof n === "number" ? n : 0) / 6);
}

function pointDroughtCount(p: any, team: 0 | 1): number {
  const n = p?.pointDroughtByTeamBefore?.[team];
  return typeof n === "number" ? n : 0;
}

function rallyWinDroughtCount(p: any, team: 0 | 1): number {
  const n = p?.rallyWinDroughtByTeamBefore?.[team];
  return typeof n === "number" ? n : 0;
}

function lastPointRallyIndexForTeam(p: any, team: 0 | 1): number | null {
  const n = p?.lastPointRallyIndexByTeamBefore?.[team];
  return typeof n === "number" ? n : null;
}

function lastRallyWinRallyIndexForTeam(p: any, team: 0 | 1): number | null {
  const n = p?.lastRallyWinRallyIndexByTeamBefore?.[team];
  return typeof n === "number" ? n : null;
}

function PressureTopCenter_DIDNOTWORK(props: {
  p: any | null;
  isInFullPopup?: boolean;
}) {
  const { p, isInFullPopup } = props;

  // Assumes clamp01 exists in your file. If not:
  // const clamp01 = (x: number) => Math.max(0, Math.min(1, x));

  const t0 = teamRgba(Team_0, 1);
  const t1 = teamRgba(Team_1, 1);

  // Signals (ONLY these two)
  const pp0 = p ? clamp01(scorePressureForTeam(p, 0)) : 0; // point pressure magnitude [0..1]
  const pp1 = p ? clamp01(scorePressureForTeam(p, 1)) : 0;

  const pd0 = p ? clamp01(pointDroughtPressureForTeam(p, 0)) : 0; // drought [0..1]
  const pd1 = p ? clamp01(pointDroughtPressureForTeam(p, 1)) : 0;

  // Presence: always there, but subtle when nothing is happening
  const total = pp0 + pp1 + pd0 + pd1;
  const hudAlpha = clamp01(0.22 + Math.pow(clamp01(total / 1.1), 0.6) * 0.78);

  // Emphasize the team under more pressure (still only based on these two signals)
  const under0 = clamp01(pp0 + pd0 * 0.75);
  const under1 = clamp01(pp1 + pd1 * 0.75);
  const pressuredTeam: 0 | 1 = under0 >= under1 ? 0 : 1;
  const colOpacity = (team: 0 | 1) =>
    (team === pressuredTeam ? 1 : 0.35) * hudAlpha;

  // Colors
  const red = "rgba(255,107,107,1)";
  const green = "rgba(61,220,132,1)";
  const desert = "rgba(201,164,106,1)";

  // Layout constants
  const colW = isInFullPopup ? 44 : 40;
  const rowH = isInFullPopup ? 14 : 12;
  const gapCols = 10;

  // Pip geometry
  const pipW = 10;
  const pipH = 5;

  // For POINT: diverging vertical position around centerline.
  // We don't have a signed value from scorePressureForTeam, so we infer "who is pressured"
  // by comparing the two teams: higher pp => under pressure.
  const pointSignFor = (team: 0 | 1) => {
    if (pp0 === pp1) return 0;
    const isUnder = team === 0 ? pp0 > pp1 : pp1 > pp0;
    return isUnder ? -1 : +1; // under pressure => down (red), relief => up (green)
  };

  function PointPip(props: { team: 0 | 1; mag: number }) {
    const { team, mag } = props;

    const sign = pointSignFor(team);
    const m = clamp01(mag);

    // y offset from center: 0..(rowH/2 - pipH)
    const maxOffset = Math.max(2, Math.floor(rowH / 2) - pipH);
    const dy = Math.round(Math.pow(m, 0.75) * maxOffset) * sign;

    const yCenter = Math.floor(rowH / 2) - Math.floor(pipH / 2);
    const y = yCenter + dy;

    const color = sign < 0 ? red : green;
    const op = clamp01(0.25 + Math.pow(m, 0.6) * 0.75);

    return (
      <Box
        sx={{
          position: "relative",
          width: colW,
          height: rowH,
        }}
      >
        {/* center baseline */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: Math.floor(rowH / 2),
            height: 1,
            bgcolor: "rgba(255,255,255,0.22)",
          }}
        />

        {/* pip */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: y,
            margin: "0 auto",
            width: pipW,
            height: pipH,
            borderRadius: 99,
            bgcolor: color,
            opacity: op,
            border: "1px solid rgba(0,0,0,0.35)",
            boxShadow: m > 0.6 ? "0 0 10px rgba(0,0,0,0.35)" : "none",
          }}
        />
      </Box>
    );
  }

  function DroughtPip(props: { mag: number }) {
    const { mag } = props;
    const m = clamp01(mag);

    // Only moves downward from the top (more drought pressure -> lower)
    const topPad = 1;
    const maxDrop = rowH - pipH - topPad;
    const y = topPad + Math.round(Math.pow(m, 0.75) * maxDrop);

    const op = clamp01(0.18 + Math.pow(m, 0.7) * 0.82);

    return (
      <Box sx={{ position: "relative", width: colW, height: rowH }}>
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: y,
            margin: "0 auto",
            width: pipW,
            height: pipH,
            borderRadius: 99,
            bgcolor: desert,
            opacity: op,
            border: "1px solid rgba(0,0,0,0.35)",
          }}
        />
      </Box>
    );
  }

  const labelSx = {
    fontSize: 10,
    fontWeight: 800,
    lineHeight: "10px",
    color: "rgba(255,255,255,0.92)",
    textShadow: "0 1px 2px rgba(0,0,0,0.85)",
    width: 10,
    textAlign: "center",
  } as const;

  function TeamCol(props: {
    team: 0 | 1;
    teamColor: string;
    pp: number;
    pd: number;
  }) {
    const { team, teamColor, pp, pd } = props;
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          alignItems: "flex-start",
        }}
      >
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: 99,
            bgcolor: teamColor,
            opacity: 0.9,
          }}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Box sx={labelSx}>P</Box>
          <PointPip team={team} mag={pp} />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Box sx={labelSx}>D</Box>
          <DroughtPip mag={pd} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "absolute",
        top: isInFullPopup ? 10 : 8,
        left: isInFullPopup ? 10 : 8,
        zIndex: 36,
        pointerEvents: "none",
        opacity: hudAlpha,
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: `${gapCols}px`,
          alignItems: "flex-start",
          px: 1,
          py: 0.75,
          borderRadius: 2,
          bgcolor: "rgba(0,0,0,0.30)",
          border: "1px solid rgba(255,255,255,0.10)",
        }}
      >
        <Box sx={{ opacity: colOpacity(0) }}>
          <TeamCol team={0} teamColor={t0} pp={pp0} pd={pd0} />
        </Box>

        <Box sx={{ opacity: colOpacity(1) }}>
          <TeamCol team={1} teamColor={t1} pp={pp1} pd={pd1} />
        </Box>
      </Box>
    </Box>
  );
}

function VideoPopout(props: {
  titleNode?: React.ReactNode;
  renderPlayerIdentity?: RenderPlayerIdentity;
  resolvePlayerAvatarSrc?: (playerId: number | null | undefined) => string | undefined;
  open: boolean;
  title: string;
  seedTimeSec: number;
  seedPaused: boolean;
  seedLoopRegion?: {
    enabled: boolean;
    aSec: number;
    bSec: number;
    nonce: number;
  };
  onClose: (
    tSec: number,
    paused: boolean,
    loop?: { enabled: boolean; aSec: number; bSec: number },
    nextBallRead?: ActiveNextBallRead | null,
  ) => void;
  // onClose: (tSec: number, paused: boolean) => void;

  // give it the same inputs the small video has
  packageId: string;
  aiContextLabel: string;
  row: KitchenArrivalRow;
  rows: KitchenArrivalRow[];
  activeShotNumber: number | null;
  beginningScore?: [number, number] | null;
  endingScore?: [number, number] | null;
  showCondensedTimeline: boolean;
  fontColor: string;
  controlsPlacement: "top" | "bottom" | "left";
  firefightsClips: FirefightClip[];
  firefightRallySet: Set<number>;
  showRateControls?: boolean;
  highlightActiveShotTrajectory?: boolean;
  showUltraSlowRate?: boolean;
  showTitle?: boolean;
  showScoreEtc?: boolean;
  autoPlayOnRallyChange?: boolean;
  showAi?: boolean;
  showLoopScrubber?: boolean;
  defaultLoopUiOpen?: boolean;
  onAskAi?: (payload: ClipFocus) => void;
  onClipFocusChange?: (focus: ClipFocus | undefined) => void;
  focusedFilter: FocusedReasonStakeForTeam | null;
  focusedPlayerId?: number | null;
  cvJson?: CvJson | null;
  insightsToCvPlayerIndex: PlayerIndexMap;
  disableShotStepControls?: boolean;
  showShotSpaceButton?: boolean;
  onOpenShotSpace?: () => void;
  rallyLensMoments?: ActiveRallyLensMoment[];
  initialRallyLens?: ActiveRallyLensKey;
  initialRallyLensMomentKey?: string | null;
  showRallyLensControls?: boolean;
  initialNextBallReadSeed?: NextBallReadSeed;
  onOpenRallyFromSimilarPopup?: (rallyIndex: number) => void;

  pounceOverlay?: PounceOverlay | null;
}) {
  const {
    open,
    title,
    seedTimeSec,
    seedPaused,
    onClose,
    controlsPlacement,
    showAi,
    showLoopScrubber,
    packageId,
    row,
    rows,
    activeShotNumber,
    beginningScore,
    endingScore,
    firefightsClips,
    firefightRallySet,
    onClipFocusChange,
    focusedFilter,
    aiContextLabel,
    seedLoopRegion,
    focusedPlayerId,
    cvJson,
    insightsToCvPlayerIndex,
    disableShotStepControls,
    showShotSpaceButton,
    onOpenShotSpace,
    renderPlayerIdentity,
    pounceOverlay,
    titleNode,
    rallyLensMoments = [],
    initialRallyLens,
    initialRallyLensMomentKey,
    showRallyLensControls = false,
    initialNextBallReadSeed,
    onOpenRallyFromSimilarPopup,
  } = props;
  const initialNextBallReadNonce = initialNextBallReadSeed?.nonce ?? null;
  const initialNextBallReadStage = initialNextBallReadSeed?.stage ?? null;
  const initialNextBallReadRep = initialNextBallReadSeed?.rep ?? null;

  const innerLoopRef = React.useRef<LoopRegion | null>(null);
  const innerNextBallReadRef = React.useRef<ActiveNextBallRead | null>(
    initialNextBallReadStage && initialNextBallReadRep
      ? {
          stage: initialNextBallReadStage,
          rep: initialNextBallReadRep,
        }
      : null,
  );

  const innerVideoRef =
    React.useRef<React.RefObject<HTMLVideoElement | null> | null>(null);

  const [seedNonce, setSeedNonce] = React.useState(0);

  // bump nonce each time we open so the seed applies again
  React.useEffect(() => {
    if (open) setSeedNonce((n) => n + 1);
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    innerNextBallReadRef.current = initialNextBallReadStage && initialNextBallReadRep
      ? {
          stage: initialNextBallReadStage,
          rep: initialNextBallReadRep,
        }
      : null;
  }, [
    initialNextBallReadNonce,
    initialNextBallReadRep,
    initialNextBallReadStage,
    open,
  ]);

  // position/size
  const [rect, setRect] = React.useState(() => ({
    x: 12,
    y: 12,
    w: Math.min(window.innerWidth - 24, 1600),
    h: Math.min(window.innerHeight - 24, 900),
  }));

  // drag
  const draggingRef = React.useRef<null | { dx: number; dy: number }>(null);

  React.useEffect(() => {
    if (!open) return;

    const onMove = (e: MouseEvent) => {
      const d = draggingRef.current;
      if (!d) return;
      setRect((r) => ({ ...r, x: e.clientX - d.dx, y: e.clientY - d.dy }));
    };
    const onUp = () => (draggingRef.current = null);

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [open]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <Box
      data-demo="active-rally-video-popout"
      sx={{
        position: "fixed",
        left: rect.x,
        top: rect.y,
        width: rect.w,
        height: rect.h,
        zIndex: 9999,
        bgcolor: "rgba(10,10,10,0.92)",
        border: "1px solid rgba(255,255,255,0.16)",
        borderRadius: 2,
        boxShadow: "0 20px 60px rgba(0,0,0,0.55)",
        overflow: "hidden",
        resize: "both",
        ...DEMO_RECORDING_FOCUS_SUPPRESSION_SX,
      }}
    >
      {/* header / drag handle */}
      <Box
        onMouseDown={(e: any) => {
          // only left click
          if (e.button !== 0) return;
          draggingRef.current = {
            dx: e.clientX - rect.x,
            dy: e.clientY - rect.y,
          };
          e.preventDefault();
          e.stopPropagation();
        }}
        sx={{
          height: 40,
          px: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end", //space-between",
          cursor: "move",
          userSelect: "none",
          bgcolor: "rgba(0,0,0,0.45)",
          borderBottom: "1px solid rgba(255,255,255,0.10)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            maxWidth: "70%",
            minWidth: 0,
            // flex: 1,
            pr: 1,
            fontSize: 16,
            color: "rgba(255,255,255,0.9)",
            overflow: "hidden",
            textAlign: "center",
          }}
        >
          {titleNode ?? title}
        </Box>

        <Button
          data-demo="active-rally-video-popout-close"
          size="small"
          variant="outlined"
          // onClick={() => {
          //   const v = innerVideoRef.current?.current;
          //   const t = v?.currentTime ?? seedTimeSec ?? 0;
          //   const p = v?.paused ?? seedPaused ?? true;
          //   onClose(t, p);
          // }}
          onClick={() => {
            const v = innerVideoRef.current?.current;
            const t = v?.currentTime ?? seedTimeSec ?? 0;
            const p = v?.paused ?? seedPaused ?? true;
            onClose(
              t,
              p,
              innerLoopRef.current ?? undefined,
              innerNextBallReadRef.current,
            );
          }}
          sx={{
            minWidth: 0,
            px: 1.2,
            py: 0.3,
            borderColor: "rgba(255,255,255,0.25)",
            color: "rgba(255,255,255,0.9)",
          }}
        >
          Close
        </Button>
      </Box>

      {/* video */}
      <Box sx={{ width: "100%", height: "calc(100% - 40px)", p: 1 }}>
        <ActiveRallyVideo
          renderPlayerIdentity={renderPlayerIdentity}
          resolvePlayerAvatarSrc={props.resolvePlayerAvatarSrc}
          pounceOverlay={pounceOverlay}
          aiContextLabel={aiContextLabel}
          insightsToCvPlayerIndex={insightsToCvPlayerIndex}
          focusedFilter={focusedFilter}
          packageId={packageId}
          onVideoRef={(r) => (innerVideoRef.current = r)}
          row={row}
          rows={rows}
          activeShotNumber={activeShotNumber}
          beginningScore={beginningScore}
          endingScore={endingScore}
          showCondensedTimeline={false}
          fontColor={props.fontColor}
          controlsPlacement={controlsPlacement}
          firefightsClips={firefightsClips}
          firefightRallySet={firefightRallySet}
          showRateControls={props.showRateControls}
          showUltraSlowRate={props.showUltraSlowRate}
          showTitle={props.showTitle}
          showScoreEtc={props.showScoreEtc}
          showShotSpaceButton={showShotSpaceButton}
          onOpenShotSpace={onOpenShotSpace}
          autoPlayOnRallyChange={props.autoPlayOnRallyChange}
          showAi={showAi}
          aiDrawerMode="split"
          showLoopScrubber={showLoopScrubber}
          defaultLoopUiOpen={props.defaultLoopUiOpen}
          //onAskAi={props.onAskAi}
          //disablePopout
          showPopoutButton={true}
          seedPlayback={{
            timeSec: seedTimeSec ?? 0,
            paused: seedPaused,
            nonce: seedNonce,
          }}
          seedLoopRegion={seedLoopRegion}
          onLoopRegionChange={(next) => {
            innerLoopRef.current = next;
          }}
          isInFullPopup={true}
          onClipFocusChange={onClipFocusChange}
          focusedPlayerId={focusedPlayerId}
          cvJson={cvJson}
          rallyLensMoments={rallyLensMoments}
          initialRallyLens={initialRallyLens}
          initialRallyLensMomentKey={initialRallyLensMomentKey}
          showRallyLensControls={showRallyLensControls}
          initialNextBallReadSeed={initialNextBallReadSeed}
          onNextBallReadChange={(next) => {
            innerNextBallReadRef.current = next;
          }}
          onOpenRallyFromSimilarPopup={onOpenRallyFromSimilarPopup}
          disableShotStepControls={disableShotStepControls}
        />
      </Box>
    </Box>,
    document.body,
  );
}
function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

const NEUTRAL = "rgba(255,255,255,0.25)";

const DEMO_RECORDING_FOCUS_SUPPRESSION_SX = {
  "body[data-pbv-demo-recording='true'] &": {
    "& button:focus, & button:focus-visible, & .Mui-focusVisible": {
      outline: "none",
      boxShadow: "none",
    },
  },
};

function teamColor(t: 0 | 1): string {
  const TEAM0 = teamRgba(Team_0, 0.9);
  const TEAM1 = teamRgba(Team_1, 0.85);

  return t === 0 ? TEAM0 : TEAM1;
}

function findOverlayByShotNumber(
  overlays: ShotNumberOverlay[] | undefined,
  shotNumber: number,
) {
  return (overlays ?? []).find((o) => o.shotNumber === shotNumber) ?? null;
}

type LoopRegion = {
  enabled: boolean;
  aSec: number; // start (relative to this video src)
  bSec: number; // end
};

type AiRangeMode = "loop" | "full";

function fmtMsRel(msRel: number) {
  const s = Math.max(0, msRel) / 1000;
  const mm = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  const tenths = Math.floor((s - Math.floor(s)) * 10);
  return `${mm}:${String(ss).padStart(2, "0")}.${tenths}`;
}

function fmtSec(sec: number) {
  const s = Math.max(0, sec);
  const mm = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  const ms = Math.floor((s - Math.floor(s)) * 1000);
  const tenths = Math.floor(ms / 100);
  return `${mm}:${String(ss).padStart(2, "0")}.${tenths}`;
}

function useRafThrottle<T extends any[]>(fn: (...args: T) => void) {
  const rafRef = React.useRef<number | null>(null);
  const lastArgs = React.useRef<T | null>(null);

  const throttled = React.useCallback(
    (...args: T) => {
      lastArgs.current = args;
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        if (lastArgs.current) fn(...lastArgs.current);
      });
    },
    [fn],
  );

  React.useEffect(() => {
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return throttled;
}

// Small helper component to attach window listeners while dragging.
function ScrubGlobalHandlers(props: {
  onMove: (clientX: number) => void;
  onUp: () => void;
}) {
  const { onMove, onUp } = props;
  const onMoveRef = React.useRef(onMove);
  const onUpRef = React.useRef(onUp);

  React.useLayoutEffect(() => {
    onMoveRef.current = onMove;
    onUpRef.current = onUp;
  }, [onMove, onUp]);

  React.useEffect(() => {
    const mm = (e: MouseEvent) => onMoveRef.current(e.clientX);
    const pm = (e: PointerEvent) => onMoveRef.current(e.clientX);
    const mu = () => onUpRef.current();

    window.addEventListener("mousemove", mm);
    window.addEventListener("mouseup", mu);
    window.addEventListener("pointermove", pm);
    window.addEventListener("pointerup", mu);
    window.addEventListener("pointercancel", mu);

    return () => {
      window.removeEventListener("mousemove", mm);
      window.removeEventListener("mouseup", mu);
      window.removeEventListener("pointermove", pm);
      window.removeEventListener("pointerup", mu);
      window.removeEventListener("pointercancel", mu);
    };
  }, []);

  return null;
}

export type AiTurnStatus = "pending" | "done" | "error";

export type AiTurn = {
  id: string;
  createdAtMs: number;
  question: string;
  focus: ClipFocus; // what you already compute
  status: AiTurnStatus;
  responseJson?: any; // raw response (or extracted turn.result)
  responseText?: string; // keep it simple; you can store full JSON later
  error?: string;
  contextLabel: string;
};

export function buildErrorTurn(args: {
  id?: string;
  createdAtMs?: number;
  question: string;
  focus: ClipFocus;
  contextLabel: string;
  message: string;
}): AiTurn {
  return {
    id: args.id ?? mkTurnId(),
    createdAtMs: args.createdAtMs ?? Date.now(),
    question: args.question,
    focus: args.focus,
    status: "error",
    error: args.message,
    responseText: args.message,
    contextLabel: args.contextLabel,
  };
}



export function mkTurnId() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export function turnsStorageKey(packageId: string, rallyIndex: number) {
  return `poc2.aiTurns.v1.${packageId}.rally.${rallyIndex}`;
}

// --------------------------------------------
// Global AI turns index (across all contexts)
// --------------------------------------------

export type AiTurnGlobal = AiTurn & {
  packageId: string;
  rallyIndex: number;
};

export function globalTurnsStorageKey() {
  return `poc2.aiTurnsGlobal.v1`;
}

export function readGlobalTurns(): AiTurnGlobal[] {
  try {
    const raw = localStorage.getItem(globalTurnsStorageKey());
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as AiTurnGlobal[]) : [];
  } catch {
    return [];
  }
}


export function writeGlobalTurns(next: AiTurnGlobal[]) {
  try {
    const CAP = 600;
    const trimmed = next.slice(0, CAP);
    localStorage.setItem(globalTurnsStorageKey(), JSON.stringify(trimmed));

    window.dispatchEvent(
      new CustomEvent("poc2-ai-turns-updated")
    );
  } catch {
    // ignore
  }
}


export function upsertGlobalTurn(t: AiTurnGlobal) {
  const cur = readGlobalTurns();
  const idx = cur.findIndex(
    (x) =>
      x.id === t.id &&
      x.packageId === t.packageId &&
      x.rallyIndex === t.rallyIndex,
  );

  let next: AiTurnGlobal[];
  if (idx >= 0) {
    next = [...cur];
    next[idx] = { ...next[idx], ...t };
  } else {
    next = [t, ...cur];
  }

  // Keep newest-first globally (createdAtMs desc)
  next.sort((a, b) => (b.createdAtMs ?? 0) - (a.createdAtMs ?? 0));
  writeGlobalTurns(next);
}

export function deleteGlobalTurn(
  packageId: string,
  rallyIndex: number,
  turnId: string,
) {
  const cur = readGlobalTurns();
  const next = cur.filter(
    (x) =>
      !(
        x.packageId === packageId &&
        x.rallyIndex === rallyIndex &&
        x.id === turnId
      ),
  );
  writeGlobalTurns(next);
}

export const ASK_AI_END_POINT = "/api/ai-ask-rally-basic-question";
export const EXPORT_HTML_END_POINT = "/api/export-clip-html-new";
export const EXPORT_SOCIAL_MP4_END_POINT = "/api/export-clip-social-mp4";

function exportIconForFocusedReason(
  reason: FocusedReasonStakeForTeam["reason"] | undefined,
): string | undefined {
  switch (reason) {
    case "missedServe":
      return "🎾";
    case "missedReturn":
      return "↩";
    case "missed3rdDrop":
      return "3↓";
    case "missed3rdDrive":
      return "3↗";
    case "faultNet":
      return "⊘";
    case "faultOut":
      return "↗";
    case "unforcedError":
      return "!";
    case "forcedFault":
      return "⚡";
    case "popupExploited":
      return "↗";
    default:
      return undefined;
  }
}

function slimShotOverlaysForSocialExport(overlays: unknown): any[] | undefined {
  if (!Array.isArray(overlays)) return undefined;
  return overlays.map((overlay: any) => ({
    shotNumber: overlay?.shotNumber,
    playerId: overlay?.playerId ?? overlay?.player_id ?? overlay?.rawShot?.player_id,
    atMsRel: overlay?.atMsRel,
    showAtMsRel: overlay?.showAtMsRel,
    startMsRel: overlay?.startMsRel,
    start_ms: overlay?.start_ms ?? overlay?.rawShot?.start_ms,
    hideAtMsRel: overlay?.hideAtMsRel,
    exportBadgeText: overlay?.exportBadgeText,
    exportBadgeTone: overlay?.exportBadgeTone,
    exportBadgeIcons: overlay?.exportBadgeIcons,
    exportBadgeRightIcons: overlay?.exportBadgeRightIcons,
  }));
}

export function renderAiAnswer(t: AiTurn) {
  if (t.status === "pending") {
    return <span>Thinking…</span>;
  }
  // if (t.status === "error") {
  //   return <span>{t.error ?? "Error"}</span>;
  // }

  if (t.status === "error") {
// console.error({t});

  return (
    <span style={{ whiteSpace: "pre-wrap", color: "red", }}>
      {t.error ?? t.responseText ?? "Error"}
    </span>
  );
}


  const r = t.responseJson ?? null;

  if (!r || typeof r !== "object") {
    return (
      <span style={{ whiteSpace: "pre-wrap" }}>{t.responseText ?? "—"}</span>
    );
  }

  const headline = r?.rallyOverview?.headline as string | undefined;
  const keyTurningPoint = r?.rallyOverview?.keyTurningPoint as
    | string
    | undefined;
  const answer = r?.answerToQuestion as string | undefined;
  const conf =
    typeof r?.overallConfidence === "number" ? r.overallConfidence : undefined;
  const winnerSide = r?.winnerSide as string | undefined;

  const eventSeq = Array.isArray(r?.rallyOverview?.eventSequence)
    ? (r.rallyOverview.eventSequence as string[])
    : [];

  // const uncertainty = Array.isArray(r?.rallyOverview?.uncertaintyNotes)
  //   ? (r.rallyOverview.uncertaintyNotes as string[])
  //   : [];

  // const pbv = Array.isArray(r?.rallyOverview?.shotInsightsFromPbv)
  //   ? (r.rallyOverview.shotInsightsFromPbv as string[])
  //   : [];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        //gap: 0.75,
        color: "white",
        fontSize: 13, // "12px !important",
        lineHeight: 1.25,
      }}
    >
      {answer && (
        <Box sx={{}}>
          <strong>Answer:</strong> {answer}
        </Box>
      )}

      {headline && <Box sx={{}}>{headline}</Box>}

      {keyTurningPoint && (
        <Box sx={{}}>
          <strong>Turning point:</strong> {keyTurningPoint}
        </Box>
      )}

      {eventSeq.length > 0 && (
        <Box sx={{ pl: 1.5 }}>
          {eventSeq.slice(0, 6).map((s, i) => (
            <Box key={i} sx={{ fontSize: 12 }}>
              • {s}
            </Box>
          ))}
        </Box>
      )}

      {(winnerSide || conf != null) && (
        <Box sx={{}}>
          {winnerSide ? `Winner: ${winnerSide}` : ""}
          {winnerSide && conf != null ? " • " : ""}
          {conf != null ? `Confidence: ${Math.round(conf * 100)}%` : ""}
        </Box>
      )}
    </Box>
  );
}

export const MPH_MAX_FOR_TICK = 60;

export function getOverlayMph(ov: ShotNumberOverlay): number | null {
  return ov?.rawShot?.resulting_ball_movement?.speed ?? null;
}

const TIMELINE_WHITE_TICKS_H = 10;

function getOverlayMsRel(
  ov: ShotNumberOverlay,
  segmentStartMs: number | null | undefined,
  segmentEndMs: number | null | undefined,
): number {
  const segStart = finiteNumber(segmentStartMs);
  const segEnd = finiteNumber(segmentEndMs);
  const segmentDurationMs =
    segStart != null && segEnd != null && segEnd > segStart
      ? segEnd - segStart
      : null;
  const candidates = [
    finiteNumber(ov?.atMsRel),
    finiteNumber((ov as any)?.rawShot?.start_ms),
    finiteNumber((ov as any)?.rawShot?.start_ms_rel),
    finiteNumber((ov as any)?.trajectoryStart?.ms),
    finiteNumber(
      (ov as any)?.rawShot?.resulting_ball_movement?.trajectory?.start?.ms,
    ),
  ].filter((value): value is number => value != null);

  if (!candidates.length) return 0;

  for (const candidate of candidates) {
    if (
      segStart != null &&
      segmentDurationMs != null &&
      candidate >= segStart - 1500 &&
      candidate <= segStart + segmentDurationMs + 3000
    ) {
      return Math.max(0, candidate - segStart);
    }
  }

  for (const candidate of candidates) {
    if (
      segmentDurationMs == null ||
      (candidate >= -1500 && candidate <= segmentDurationMs + 3000)
    ) {
      return Math.max(0, candidate);
    }
  }

  const first = candidates[0];
  if (segStart != null && first >= segStart - 1500) {
    return Math.max(0, first - segStart);
  }

  return Math.max(0, first);
}

export const TIME_LINE_FAST_SHOT_MPH = 30;

function TimelineWhiteTicks(props: {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  overlays?: ShotNumberOverlay[] | null;
  segmentStartMs?: number | null;
  segmentEndMs?: number | null;
  heightPx?: number;
  mphCutoff?: number; // <= NEW
  alignToTrackRef?: React.RefObject<HTMLElement | null>;
  alignWithinRef?: React.RefObject<HTMLElement | null>;
  showLoopScrubber?: boolean;
  loopUiOpen?: boolean;
  loopEnabled?: boolean;
  loopASec?: number;
  loopBSec?: number;
  rallyKey: string | number;
}) {
  const {
    videoRef,
    overlays,
    segmentStartMs,
    segmentEndMs,
    heightPx = TIMELINE_WHITE_TICKS_H,
    mphCutoff = TIME_LINE_FAST_SHOT_MPH, //30,

    alignToTrackRef,
    alignWithinRef,
    showLoopScrubber,
    loopUiOpen,
    loopEnabled,
    loopASec,
    loopBSec,
    rallyKey,
  } = props;

  const progressRef = React.useRef<HTMLDivElement | null>(null);

  const clamp = (x: number, lo: number, hi: number) =>
    Math.max(lo, Math.min(hi, x));

  const [trackOffsets, setTrackOffsets] = React.useState<{
    left: number;
    right: number;
  }>({
    left: 0,
    right: 0,
  });

  React.useLayoutEffect(() => {
    let raf1 = 0;
    let raf2 = 0;

    const update = () => {
      const trackEl = alignToTrackRef?.current;
      const containerEl = alignWithinRef?.current;

      if (!trackEl || !containerEl) {
        setTrackOffsets({ left: 0, right: 0 });
        return;
      }

      const trackRect = trackEl.getBoundingClientRect();
      const containerRect = containerEl.getBoundingClientRect();

      const left = Math.max(0, trackRect.left - containerRect.left);
      const right = Math.max(0, containerRect.right - trackRect.right);

      setTrackOffsets({ left, right });
    };

    raf1 = window.requestAnimationFrame(update);
    raf2 = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(update);
    });

    window.addEventListener("resize", update);

    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
      window.removeEventListener("resize", update);
    };
  }, [
    alignToTrackRef,
    alignWithinRef,
    showLoopScrubber,
    loopUiOpen,
    loopEnabled,
    loopASec,
    loopBSec,
    rallyKey,
  ]);

  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const sync = () => {
      const progressEl = progressRef.current;
      if (!progressEl) return;

      const pct =
        Number.isFinite(v.duration) && v.duration > 0
          ? clamp(v.currentTime / v.duration, 0, 1)
          : 0;

      progressEl.style.width = `${pct * 100}%`;
    };

    const onTimeUpdate = () => sync();
    const onSeeking = () => sync();
    const onSeeked = () => sync();
    const onLoadedMetadata = () => sync();
    const onLoadedData = () => sync();
    const onEnded = () => sync();

    v.addEventListener("timeupdate", onTimeUpdate);
    v.addEventListener("seeking", onSeeking);
    v.addEventListener("seeked", onSeeked);
    v.addEventListener("loadedmetadata", onLoadedMetadata);
    v.addEventListener("loadeddata", onLoadedData);
    v.addEventListener("ended", onEnded);

    sync();

    return () => {
      v.removeEventListener("timeupdate", onTimeUpdate);
      v.removeEventListener("seeking", onSeeking);
      v.removeEventListener("seeked", onSeeked);
      v.removeEventListener("loadedmetadata", onLoadedMetadata);
      v.removeEventListener("loadeddata", onLoadedData);
      v.removeEventListener("ended", onEnded);
    };
  }, [videoRef, rallyKey]);

  const ticks = React.useMemo(() => {
    const ovs = Array.isArray(overlays) ? overlays : [];
    if (!ovs.length) return [] as { pct: number; mph: number | null }[];

    const firstAt =
      typeof ovs[0]?.atMsRel === "number" && Number.isFinite(ovs[0].atMsRel)
        ? ovs[0].atMsRel
        : 0;

    const segStart = typeof segmentStartMs === "number" ? segmentStartMs : 0;
    const segEnd =
      typeof segmentEndMs === "number" && Number.isFinite(segmentEndMs)
        ? segmentEndMs
        : null;

    let durMs: number | null =
      segEnd != null && segEnd > segStart ? segEnd - segStart : null;

    if (!durMs || durMs <= 0) {
      let maxRel = 0;
      for (const ov of ovs) {
        const candidates = [
          ov.hideAtMsRel,
          ov.fadeStartMsRel,
          ov.fullFromMsRel,
          ov.showFromMsRel,
          ov.atMsRel,
        ].filter(
          (v): v is number => typeof v === "number" && Number.isFinite(v),
        );

        for (const v of candidates) {
          const norm = v - firstAt;
          if (norm > maxRel) maxRel = norm;
        }
      }
      durMs = maxRel > 0 ? maxRel : null;
    }

    if (!durMs || durMs <= 0) {
      return [] as { pct: number; mph: number | null }[];
    }

    const out: { pct: number; mph: number | null }[] = [];
    for (const ov of ovs) {
      const rawMs =
        typeof ov.atMsRel === "number"
          ? ov.atMsRel
          : getOverlayMsRel(ov, segmentStartMs, segmentEndMs);

      if (!Number.isFinite(rawMs)) continue;

      const msRel = rawMs - firstAt;
      const pct = clamp(msRel / durMs, 0, 1);
      const mph = getOverlayMph(ov);
      out.push({ pct, mph });
    }

    out.sort((a, b) => a.pct - b.pct);
    return out;
  }, [overlays, segmentStartMs, segmentEndMs, rallyKey]);

  // const [playPct, setPlayPct] = React.useState(0);

  // React.useEffect(() => {
  //   let raf: number | null = null;
  //   const tick = () => {
  //     const v = videoRef.current;
  //     if (v && Number.isFinite(v.duration) && v.duration > 0) {
  //       const pct = clamp(v.currentTime / v.duration, 0, 1);
  //       setPlayPct(pct);
  //     }
  //     raf = requestAnimationFrame(tick);
  //   };
  //   raf = requestAnimationFrame(tick);
  //   return () => {
  //     if (raf != null) cancelAnimationFrame(raf);
  //   };
  // }, [videoRef]);

  // const [playPct, setPlayPct] = React.useState(0);

  // React.useEffect(() => {
  //   const v = videoRef.current;
  //   if (!v) return;

  //   const sync = () => {
  //     if (Number.isFinite(v.duration) && v.duration > 0) {
  //       setPlayPct(clamp(v.currentTime / v.duration, 0, 1));
  //     } else {
  //       setPlayPct(0);
  //     }
  //   };

  //   const onTimeUpdate = () => sync();
  //   const onSeeking = () => sync();
  //   const onSeeked = () => sync();
  //   const onLoadedMetadata = () => sync();
  //   const onLoadedData = () => sync();
  //   const onEnded = () => sync();

  //   v.addEventListener("timeupdate", onTimeUpdate);
  //   v.addEventListener("seeking", onSeeking);
  //   v.addEventListener("seeked", onSeeked);
  //   v.addEventListener("loadedmetadata", onLoadedMetadata);
  //   v.addEventListener("loadeddata", onLoadedData);
  //   v.addEventListener("ended", onEnded);

  //   sync();

  //   return () => {
  //     v.removeEventListener("timeupdate", onTimeUpdate);
  //     v.removeEventListener("seeking", onSeeking);
  //     v.removeEventListener("seeked", onSeeked);
  //     v.removeEventListener("loadedmetadata", onLoadedMetadata);
  //     v.removeEventListener("loadeddata", onLoadedData);
  //     v.removeEventListener("ended", onEnded);
  //   };
  // }, [videoRef, rallyKey]);

  const durationSec = React.useMemo(() => {
    const segStart =
      typeof segmentStartMs === "number" && Number.isFinite(segmentStartMs)
        ? segmentStartMs
        : 0;
    const segEnd =
      typeof segmentEndMs === "number" && Number.isFinite(segmentEndMs)
        ? segmentEndMs
        : null;

    if (segEnd != null && segEnd > segStart) {
      return (segEnd - segStart) / 1000;
    }

    const ovs = Array.isArray(overlays) ? overlays : [];
    if (!ovs.length) return 0;

    const firstAt =
      typeof ovs[0]?.atMsRel === "number" && Number.isFinite(ovs[0].atMsRel)
        ? ovs[0].atMsRel
        : 0;

    let maxRel = 0;
    for (const ov of ovs) {
      const candidates = [
        ov.hideAtMsRel,
        ov.fadeStartMsRel,
        ov.fullFromMsRel,
        ov.showFromMsRel,
        ov.atMsRel,
      ].filter((v): v is number => typeof v === "number" && Number.isFinite(v));

      for (const v of candidates) {
        const norm = v - firstAt;
        if (norm > maxRel) maxRel = norm;
      }
    }

    return maxRel > 0 ? maxRel / 1000 : 0;
  }, [segmentStartMs, segmentEndMs, overlays]);

  const loopLoPct =
    typeof loopASec === "number" && typeof loopBSec === "number"
      ? loopEnabled && durationSec > 0
        ? Math.max(0, Math.min(loopASec, loopBSec) / durationSec)
        : 0
      : undefined;

  const loopHiPct =
    typeof loopASec === "number" && typeof loopBSec === "number"
      ? loopEnabled && durationSec > 0
        ? Math.max(0, Math.max(loopASec, loopBSec) / durationSec)
        : 0
      : undefined;

  return (
    <Box
      sx={{
        position: "absolute",
        left: trackOffsets.left,
        right: trackOffsets.right,

        bottom: 17, // "10px",
        height: heightPx,
        borderRadius: 0,
        overflow: "visible",
        pointerEvents: "none",
        zIndex: 1200,
      }}
    >
      {/* base */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          bgcolor: "transparent", // "rgba(255,255,255,0.10)",
        }}
      />

      {loopEnabled &&
        durationSec > 0 &&
        typeof loopLoPct === "number" &&
        typeof loopHiPct === "number" && (
          <Box
            sx={{
              position: "absolute",
              left: `${loopLoPct * 100}%`,
              width: `${Math.max(0, (loopHiPct - loopLoPct) * 100)}%`,
              top: 0,
              bottom: 0,
              // bgcolor: "rgba(255,255,255,0.14)",
              // outline: "1px solid rgba(255,255,255,0.18)",
              // bgcolor: "rgba(255,0,0,0.9)",
              // outline: "1px solid rgba(120,180,255,0.28)",
              bgcolor: "rgba(255, 191, 71, 0.34)",
              outline: "1px solid rgba(255, 191, 71, 0.42)",
              pointerEvents: "none",
              zIndex: 0,
            }}
          />
        )}

      {/* progress */}
      {/* <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          //width: `${playPct * 100}%`,
          bgcolor: "rgba(255,255,255,0.22)",
        }}
        style={{width: `${playPct * 100}%`,}}
      /> */}

      <Box
        ref={progressRef}
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "0%",
          bgcolor: "rgba(255,255,255,0.22)",
        }}
      />

      {/* ticks */}
      {ticks.map((t, i) => {
        const h = tickHeightFromMph(t.mph, heightPx);
        const hot = t.mph != null && t.mph >= mphCutoff;

        return (
          <Box
            key={i}
            sx={{
              position: "absolute",
              bottom: 0,
              left: `calc(${t.pct * 100}% - 1px)`,
              width: 2,
              height: h,
              bgcolor: hot ? "rgba(255,80,80,0.95)" : "rgba(255,255,255,0.72)",
            }}
          />
        );
      })}
    </Box>
  );
}

export function tickHeightFromMph(mph: number | null, barH: number) {
  if (mph == null) return Math.max(2, Math.floor(barH * 0.35));
  const p = clamp(mph / MPH_MAX_FOR_TICK, 0, 1);
  // keep even slow shots visible; tall at high mph
  const minH = Math.max(2, Math.floor(barH * 0.25));
  const maxH = barH + 4;
  return Math.round(minH + (maxH - minH) * p);
}

// function similarModeLabel(mode: SimilarityMode): string {
//   return mode.type === "global" ? "Global" : `Player ${mode.playerId}`;
// }

// function similarityReasonLabel(reason: SimilarityReason): string {
//   switch (reason) {
//     case "same_winner_team":
//       return "same winner team";
//     case "same_player_won":
//       return "you won";
//     case "same_player_lost":
//       return "you lost";
//     case "same_player_hit_final_shot":
//       return "you hit final shot";
//     case "same_player_committed_final_error":
//       return "you made final error";
//     case "same_ended_by":
//       return "same ending type";
//     case "same_ending_shot_type":
//       return "same ending shot";
//     case "same_ending_pattern":
//       return "same ending pattern";
//     case "same_pressure_bucket":
//       return "same pressure";
//     case "both_firefights":
//       return "firefight";
//     case "both_fast_reaction":
//       return "fast reaction";
//     case "same_first_kitchen_team":
//       return "same first kitchen team";
//     case "same_player_team_reached_kitchen_first":
//       return "your team got kitchen first";
//     case "both_teams_reached_kitchen":
//       return "both teams reached kitchen";
//     case "similar_rally_length":
//       return "similar length";
//     case "similar_max_speed":
//       return "similar speed";
//     case "similar_fastest_reaction":
//       return "similar reaction";
//     case "similar_drive_count":
//       return "similar drives";
//     case "similar_drop_count":
//       return "similar drops";
//     case "similar_dink_count":
//       return "similar dinks";
//     case "similar_reset_count":
//       return "similar resets";
//     case "similar_volley_count":
//       return "similar volleys";
//     case "same_player_serve_role":
//       return "same serve role";
//     case "same_player_role_in_rally":
//       return "same role";
//     case "both_player_attacked":
//       return "you attacked";
//     case "both_player_was_attacked":
//       return "you were attacked";
//     case "experimental_erne_match":
//       return "Erne-ish";
//     case "experimental_atp_match":
//       return "ATP-ish";
//     case "experimental_lob_match":
//       return "lob-ish";
//     default:
//       return reason;
//   }
// }

// focused player helpers

type FocusedPlayerMarker = {
  u: number;
  v: number;
  team: 0 | 1;
} | null;

function getShotStartMs(rawShot: PbvShot): number | null {
  if (!rawShot) return null;
  if (typeof rawShot.start_ms === "number") return rawShot.start_ms;
  // if (typeof rawShot.startMs === "number") return rawShot.startMs;
  return null;
}

function getShotEndMs(rawShot: PbvShot): number | null {
  if (!rawShot) return null;
  if (typeof rawShot.end_ms === "number") return rawShot.end_ms;
  // if (typeof rawShot.endMs === "number") return rawShot.endMs;
  return getShotStartMs(rawShot);
}

// function getActiveOverlayForTime(args: {
//   overlays: ShotNumberOverlay[] | undefined;
//   playheadSec: number;
//   segmentStartMs: number;
// }): ShotNumberOverlay | null {
//   const { overlays, playheadSec, segmentStartMs } = args;
//   if (!overlays?.length) return null;

//   const absMs = segmentStartMs + Math.round(playheadSec * 1000);

//   for (const ov of overlays) {
//     const rawShot = (ov as any)?.rawShot;
//     const startMs = getShotStartMs(rawShot);
//     const endMs = getShotEndMs(rawShot);

//     if (
//       startMs != null &&
//       endMs != null &&
//       absMs >= startMs &&
//       absMs <= endMs
//     ) {
//       return ov;
//     }
//   }

//   let best: ShotNumberOverlay | null = null;
//   let bestDist = Number.POSITIVE_INFINITY;

//   for (const ov of overlays) {
//     const rawShot = (ov as any)?.rawShot;
//     const startMs = getShotStartMs(rawShot);
//     if (startMs == null) continue;

//     const dist = Math.abs(startMs - absMs);
//     if (dist < bestDist) {
//       bestDist = dist;
//       best = ov;
//     }
//   }

//   return best;
// }

function getActiveOverlayForPlayhead(args: {
  overlays: ShotNumberOverlay[] | undefined;
  playheadSec: number;
}): ShotNumberOverlay | null {
  const { overlays, playheadSec } = args;
  if (!overlays?.length) return null;

  const playheadMsRel = Math.round(playheadSec * 1000);

  for (const ov of overlays) {
    const showFrom =
      typeof ov.showFromMsRel === "number" ? ov.showFromMsRel : ov.atMsRel;
    const hideAt =
      typeof ov.hideAtMsRel === "number" ? ov.hideAtMsRel : ov.atMsRel + 1;

    if (playheadMsRel >= showFrom && playheadMsRel < hideAt) {
      return ov;
    }
  }

  let best: ShotNumberOverlay | null = null;
  let bestDist = Number.POSITIVE_INFINITY;

  for (const ov of overlays) {
    const t = typeof ov.atMsRel === "number" ? ov.atMsRel : 0;
    const dist = Math.abs(t - playheadMsRel);
    if (dist < bestDist) {
      bestDist = dist;
      best = ov;
    }
  }

  return best;
}

function getFocusedPlayerMarker(args: {
  focusedPlayerId: number | null | undefined;
  activeOverlay: ShotNumberOverlay | null;
  worldToScreen: ((x: number, y: number) => { u: number; v: number }) | null;
}) {
  const { focusedPlayerId, activeOverlay, worldToScreen } = args;

  if (focusedPlayerId == null) return null;
  if (!activeOverlay || !worldToScreen) return null;

  const positions =
    activeOverlay.player_positions ??
    activeOverlay.rawShot?.player_positions ??
    null;

  if (!Array.isArray(positions)) return null;

  const pos = positions[focusedPlayerId];
  if (!pos || typeof pos.x !== "number" || typeof pos.y !== "number") {
    return null;
  }

  const { u, v } = worldToScreen(pos.x, pos.y);
  if (!Number.isFinite(u) || !Number.isFinite(v)) return null;

  return {
    u,
    v,
    team: focusedPlayerId <= 1 ? 0 : 1,
  };
}
// end focused player helpers

function getAdvantageScaleValues(
  activeOverlay: ShotNumberOverlay | null,
): Array<number | null> | null {
  const candidates = [
    (activeOverlay as any)?.advantage_scale,
    (activeOverlay as any)?.rawShot?.advantage_scale,
  ];

  for (const candidate of candidates) {
    if (!Array.isArray(candidate) || candidate.length < 4) continue;
    return candidate.slice(0, 4).map((v: unknown) => {
      return typeof v === "number" && Number.isFinite(v) ? v : null;
    });
  }

  return null;
}

function formatAdvantageValue(v: number | null): string {
  if (typeof v !== "number") return "--";
  return v.toFixed(2);
}

function advantageBadgeScale(v: number | null): number {
  if (typeof v !== "number" || !Number.isFinite(v)) return 1;
  return 1 + Math.max(0, Math.min(1, v)) * 2;
}

const ADVANTAGE_ESTABLISHED_THRESHOLD = 0.75;
const ADVANTAGE_LAG_GAP_THRESHOLD = 0.35;

function getAdvantagePartnerId(playerId: number): number {
  if (playerId === 0) return 1;
  if (playerId === 1) return 0;
  if (playerId === 2) return 3;
  return 2;
}

function isAdvantagePlayerLagging(
  values: Array<number | null>,
  playerId: number,
): boolean {
  const playerValue = values[playerId];
  const partnerValue = values[getAdvantagePartnerId(playerId)];
  if (playerValue == null || partnerValue == null) return false;
  return (
    partnerValue >= ADVANTAGE_ESTABLISHED_THRESHOLD &&
    partnerValue - playerValue >= ADVANTAGE_LAG_GAP_THRESHOLD
  );
}

function getAdvantageSeriesForPlayer(
  row: KitchenArrivalRow | null,
  playerId: number,
): number[] {
  if (!row) return [];
  return (row.shotNumberOverlays ?? [])
    .map((ov) => getAdvantageScaleValues(ov)?.[playerId] ?? null)
    .filter((v): v is number => typeof v === "number");
}

function getAdvantageLagShotNumbersForPlayer(
  row: KitchenArrivalRow | null,
  playerId: number,
): number[] {
  if (!row) return [];
  const out: number[] = [];
  for (const ov of row.shotNumberOverlays ?? []) {
    const values = getAdvantageScaleValues(ov);
    if (!values) continue;
    if (isAdvantagePlayerLagging(values, playerId)) {
      out.push(ov.shotNumber);
    }
  }
  return out;
}

function AdvantageScaleMiniSparkline(props: {
  series: number[];
  currentShotNumber: number | null;
  team: 0 | 1;
  lagShotNumbers?: number[];
  terminalShotNumber?: number | null;
  terminalPlayer?: boolean;
}) {
  const {
    series,
    currentShotNumber,
    team,
    lagShotNumbers = [],
    terminalShotNumber,
    terminalPlayer,
  } = props;
  if (!series.length) return null;

  const w = 42;
  const h = 16;
  const color = team === 0 ? "rgba(120,190,255,0.98)" : "rgba(120,235,165,0.98)";
  const xOf = (idx: number) =>
    series.length <= 1 ? w / 2 : (idx / (series.length - 1)) * w;
  const yOf = (v: number) => h - Math.max(0, Math.min(1, v)) * h;
  const points = series.map((v, idx) => `${xOf(idx)},${yOf(v)}`).join(" ");
  const currentIdx =
    currentShotNumber == null
      ? null
      : Math.max(0, Math.min(series.length - 1, currentShotNumber - 1));
  const terminalIdx =
    terminalPlayer && terminalShotNumber != null
      ? Math.max(0, Math.min(series.length - 1, terminalShotNumber - 1))
      : null;
  const lagIdxs = lagShotNumbers
    .map((shotNumber) => Math.max(0, Math.min(series.length - 1, shotNumber - 1)))
    .filter((idx, idxIndex, all) => all.indexOf(idx) === idxIndex);
  const lagBandOf = (idx: number) => {
    if (series.length <= 1) return { x: 0, width: w };
    const step = w / (series.length - 1);
    const x = Math.max(0, xOf(idx) - step / 2);
    const right = Math.min(w, xOf(idx) + step / 2);
    return { x, width: Math.max(2, right - x) };
  };

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      style={{
        width: w,
        height: h,
        display: "block",
        marginTop: 3,
        overflow: "visible",
      }}
    >
      <polyline
        points={points}
        fill="none"
        stroke="rgba(255,255,255,0.38)"
        strokeWidth={3}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {lagIdxs.map((idx) => {
        const band = lagBandOf(idx);
        return (
          <rect
            key={`lag-${idx}`}
            x={band.x}
            y={0}
            width={band.width}
            height={h}
            fill="rgba(255,190,85,0.18)"
            stroke="rgba(255,190,85,0.50)"
            strokeWidth={0.55}
          />
        );
      })}
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {currentIdx != null ? (
        <circle
          cx={xOf(currentIdx)}
          cy={yOf(series[currentIdx])}
          r={2.3}
          fill="rgba(255,235,120,0.98)"
          stroke="rgba(0,0,0,0.72)"
          strokeWidth={0.9}
        />
      ) : null}
      {terminalIdx != null ? (
        <circle
          cx={xOf(terminalIdx)}
          cy={yOf(series[terminalIdx])}
          r={2.6}
          fill="rgba(255,55,55,0.98)"
          stroke="rgba(255,255,255,0.85)"
          strokeWidth={0.9}
        />
      ) : null}
    </svg>
  );
}

function getAdvantageScaleMarkers(args: {
  activeOverlay: ShotNumberOverlay | null;
  worldToScreen: ((x: number, y: number) => { u: number; v: number }) | null;
}): Array<{
  playerId: number;
  team: 0 | 1;
  u: number;
  v: number;
  value: number | null;
  lagging: boolean;
}> {
  const { activeOverlay, worldToScreen } = args;
  if (!activeOverlay || !worldToScreen) return [];

  const values = getAdvantageScaleValues(activeOverlay);
  if (!values) return [];

  const positions =
    activeOverlay.player_positions ??
    activeOverlay.rawShot?.player_positions ??
    null;
  if (!Array.isArray(positions)) return [];

  return [0, 1, 2, 3]
    .map((playerId) => {
      const pos = positions[playerId];
      if (!pos || typeof pos.x !== "number" || typeof pos.y !== "number") {
        return null;
      }

      const { u, v } = worldToScreen(pos.x, pos.y);
      if (!Number.isFinite(u) || !Number.isFinite(v)) return null;

      return {
        playerId,
        team: playerId <= 1 ? 0 : 1,
        u,
        v,
        value: values[playerId] ?? null,
        lagging: isAdvantagePlayerLagging(values, playerId),
      };
    })
    .filter((m): m is {
      playerId: number;
      team: 0 | 1;
      u: number;
      v: number;
      value: number | null;
      lagging: boolean;
    } => !!m);
}

function fmtPounceTakenShort(
  pounceResult: "taken" | "not_taken" | null | undefined,
): string {
  if (pounceResult === "taken") return "TAKEN";
  if (pounceResult === "not_taken") return "NOT TAKEN";
  return "—";
}

function fmtPounceOverlayFeet(
  n: number | null | undefined,
  digits = 1,
): string {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  return `${n.toFixed(digits)} ft`;
}

function projectWorldPointToOverlay(
  worldToScreen: ((x: number, y: number) => { u: number; v: number }) | null,
  point:
    | {
        x: number | null;
        y: number | null;
      }
    | null
    | undefined,
) {
  if (!worldToScreen || !point) return null;
  if (typeof point.x !== "number" || typeof point.y !== "number") return null;

  const { u, v } = worldToScreen(point.x, point.y);
  if (!Number.isFinite(u) || !Number.isFinite(v)) return null;

  return {
    x: point.x,
    y: point.y,
    u,
    v,
  };
}

function buildRequiredMovementLanePolygon(args: {
  start: { u: number; v: number };
  end: { u: number; v: number };
  startWidth: number;
  endWidth: number;
}) {
  const { start, end, startWidth, endWidth } = args;

  const dx = end.u - start.u;
  const dy = end.v - start.v;
  const len = Math.hypot(dx, dy);

  if (!Number.isFinite(len) || len < 0.000001) return null;

  const nx = -dy / len;
  const ny = dx / len;

  const sHalf = startWidth / 2;
  const eHalf = endWidth / 2;

  const p1 = { u: start.u + nx * sHalf, v: start.v + ny * sHalf };
  const p2 = { u: start.u - nx * sHalf, v: start.v - ny * sHalf };
  const p3 = { u: end.u - nx * eHalf, v: end.v - ny * eHalf };
  const p4 = { u: end.u + nx * eHalf, v: end.v + ny * eHalf };

  return [p1, p2, p3, p4];
}

function lanePolygonToSvgPoints(
  poly: Array<{
    u: number;
    v: number;
  }> | null,
): string | null {
  if (!poly || poly.length < 3) return null;
  return poly.map((p) => `${p.u},${p.v}`).join(" ");
}

function midpointUv(
  a: { u: number; v: number } | null,
  b: { u: number; v: number } | null,
) {
  if (!a || !b) return null;
  return {
    u: (a.u + b.u) / 2,
    v: (a.v + b.v) / 2,
  };
}

function buildPounceOverlayPolylinePoints(args: {
  opponentContactScreen: { u: number; v: number } | null;
  firstAttackableScreen: { u: number; v: number } | null;
  bestAttackScreen: { u: number; v: number } | null;
  peakScreen: { u: number; v: number } | null;
}): string | null {
  const {
    opponentContactScreen,
    firstAttackableScreen,
    bestAttackScreen,
    peakScreen,
  } = args;

  const pts = [
    opponentContactScreen,
    peakScreen,
    firstAttackableScreen,
    bestAttackScreen,
  ].filter(Boolean) as Array<{ u: number; v: number }>;

  if (pts.length < 2) return null;

  return pts.map((p) => `${p.u},${p.v}`).join(" ");
}

type OverlayScreenPoint = { u: number; v: number };

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function sampleQuadraticPoint(
  p0: OverlayScreenPoint,
  p1: OverlayScreenPoint,
  p2: OverlayScreenPoint,
  t: number,
): OverlayScreenPoint {
  const aU = lerp(p0.u, p1.u, t);
  const aV = lerp(p0.v, p1.v, t);
  const bU = lerp(p1.u, p2.u, t);
  const bV = lerp(p1.v, p2.v, t);

  return {
    u: lerp(aU, bU, t),
    v: lerp(aV, bV, t),
  };
}

function buildSampledQuadraticPolyline(args: {
  start: OverlayScreenPoint;
  control: OverlayScreenPoint;
  end: OverlayScreenPoint;
  steps?: number;
}): string {
  const { start, control, end, steps = 18 } = args;

  const pts: OverlayScreenPoint[] = [];

  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    pts.push(sampleQuadraticPoint(start, control, end, t));
  }

  return pts.map((p) => `${p.u},${p.v}`).join(" ");
}

function buildPounceShotFlightPolyline(args: {
  pounceShotStartScreen: OverlayScreenPoint | null;
  pounceShotPeakScreen: OverlayScreenPoint | null;
  pounceShotEndScreen: OverlayScreenPoint | null;
}): string | null {
  const { pounceShotStartScreen, pounceShotPeakScreen, pounceShotEndScreen } =
    args;

  if (!pounceShotStartScreen || !pounceShotEndScreen) return null;

  if (!pounceShotPeakScreen) {
    return [pounceShotStartScreen, pounceShotEndScreen]
      .map((p) => `${p.u},${p.v}`)
      .join(" ");
  }

  return [pounceShotStartScreen, pounceShotPeakScreen, pounceShotEndScreen]
    .map((p) => `${p.u},${p.v}`)
    .join(" ");
}

function buildPounceOverlayBallFlightPolyline(args: {
  opponentContactScreen: OverlayScreenPoint | null;
  firstAttackableScreen: OverlayScreenPoint | null;
  bestAttackScreen: OverlayScreenPoint | null;
  peakScreen: OverlayScreenPoint | null;
}): string | null {
  const {
    opponentContactScreen,
    firstAttackableScreen,
    bestAttackScreen,
    peakScreen,
  } = args;

  const endPoint = firstAttackableScreen ?? bestAttackScreen;

  if (!opponentContactScreen || !endPoint) return null;

  if (!peakScreen) {
    return [opponentContactScreen, endPoint]
      .map((p) => `${p.u},${p.v}`)
      .join(" ");
  }

  return buildSampledQuadraticPolyline({
    start: opponentContactScreen,
    control: peakScreen,
    end: endPoint,
    steps: 18,
  });
}

function lightlySmoothUvPoints(
  pts: Array<{ u: number; v: number }>,
): Array<{ u: number; v: number }> {
  if (pts.length <= 2) return pts;

  const out = pts.map((p) => ({ ...p }));

  for (let i = 1; i < pts.length - 1; i += 1) {
    const a = pts[i - 1];
    const b = pts[i];
    const c = pts[i + 1];

    out[i] = {
      u: 0.25 * a.u + 0.5 * b.u + 0.25 * c.u,
      v: 0.25 * a.v + 0.5 * b.v + 0.25 * c.v,
    };
  }

  return out;
}

const PLAYBACK_FIRST_SHOT_CONTEXT_LEAD_MS = 900;
const PLAYBACK_NEXT_SHOT_CONTEXT_FRACTION = 0.5;

function finiteNumber(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function horizontalDistanceFt(
  a: { x?: unknown; y?: unknown } | null | undefined,
  b: { x?: unknown; y?: unknown } | null | undefined,
): number | null {
  const ax = finiteNumber(a?.x);
  const ay = finiteNumber(a?.y);
  const bx = finiteNumber(b?.x);
  const by = finiteNumber(b?.y);
  if (ax == null || ay == null || bx == null || by == null) return null;
  return Math.hypot(bx - ax, by - ay);
}

function normalizeShotMsRelForRow(
  row: KitchenArrivalRow | null | undefined,
  rawMs: unknown,
): number | null {
  const ms = finiteNumber(rawMs);
  if (ms == null) return null;

  const segmentStartMs = finiteNumber(row?.segmentStartMs ?? row?.startMs);
  const segmentEndMs = finiteNumber(row?.segmentEndMs ?? row?.endMs);
  const segmentDurationMs =
    segmentStartMs != null && segmentEndMs != null && segmentEndMs > segmentStartMs
      ? segmentEndMs - segmentStartMs
      : null;

  if (segmentStartMs != null) {
    const fromSegmentStart = ms - segmentStartMs;
    if (
      fromSegmentStart >= -500 &&
      (segmentDurationMs == null || fromSegmentStart <= segmentDurationMs + 3000)
    ) {
      return Math.max(0, fromSegmentStart);
    }
  }

  return Math.max(0, ms);
}

type TrajectoryShapeDiagnostics = {
  speedMph: number;
  distanceFt: number | null;
  peakHeightFt: number;
  endHeightFt: number;
  startHeightFt: number | null;
  postApexDropFt: number;
  rawDip: number;
  normalizedDip: number | null;
  speedFactor: number;
  curveShape: number | null;
  targetLabel: "bounce" | "trajectory end";
  label: "Flat / mild" | "Moderate dip" | "Heavy dip" | "Diving / nasty";
  hitterTeamId: 0 | 1 | null;
  sideViewPoints: Array<{ x: number; y: number; label?: string }>;
  sideViewCurvePoints: Array<{ x: number; y: number }>;
  sideViewFlipped: boolean;
  courtLandmarks: Array<{ x: number; label: string; heightFt?: number }>;
  playerMarkers: Array<{ x: number; label: string; teamId: 0 | 1 }>;
  reactionEstimate: ShotReactionEstimate | null;
};

type ShotReactionEstimate = {
  playerId: number;
  timeSec: number;
  lateralDistanceFt: number;
  ballHeightFt: number;
};

const MINI_PLOT_NET_HEIGHT_FT = 3;
const MINI_PLOT_DEFAULT_MAX_HEIGHT_FT = 10;
const REACTION_MIN_VOLLEY_HEIGHT_FT = 1;
const REACTION_MAX_VOLLEY_HEIGHT_FT = 7.5;
const REACTION_REACH_WIDTH_FT = 5.5;

function firstTrackedBounceForShot(args: {
  overlay: ShotNumberOverlay;
  row: KitchenArrivalRow | null | undefined;
  cvJson: CvJson | null | undefined;
}): {
  heightFt: number;
  msRel: number;
  interpolated: boolean;
  courtPosition: { x: number; y: number; z: number };
} | null {
  const { overlay, row, cvJson } = args;
  if (!row || !cvJson) return null;

  const frames = cvJson.sessions?.[0]?.rallies?.[row.rallyIndex]?.frames;
  if (!Array.isArray(frames) || frames.length === 0) return null;

  const fps = finiteNumber(cvJson.camera?.fps) ?? 29.97;
  if (fps <= 0) return null;

  const overlays = [...(row.shotNumberOverlays ?? [])].sort(
    (a, b) =>
      getOverlayMsRel(a, row.segmentStartMs, row.segmentEndMs) -
      getOverlayMsRel(b, row.segmentStartMs, row.segmentEndMs),
  );
  const currentIndex = overlays.findIndex(
    (candidate) => candidate.shotNumber === overlay.shotNumber,
  );
  const startMsRel = getOverlayMsRel(overlay, row.segmentStartMs, row.segmentEndMs);
  const nextOverlay =
    currentIndex >= 0 && currentIndex + 1 < overlays.length
      ? overlays[currentIndex + 1]
      : null;
  const nextMsRel = nextOverlay
    ? getOverlayMsRel(nextOverlay, row.segmentStartMs, row.segmentEndMs)
    : null;
  const trajectoryEndMsRel = normalizeShotMsRelForRow(
    row,
    overlay.trajectoryEnd?.ms ?? overlay.rawShot?.resulting_ball_movement?.trajectory?.end?.ms,
  );
  const endMsRel =
    nextMsRel != null && Number.isFinite(nextMsRel)
      ? Math.max(startMsRel + 80, nextMsRel - 40)
      : trajectoryEndMsRel != null
        ? Math.max(startMsRel + 80, trajectoryEndMsRel + 120)
        : startMsRel + 2200;

  const startFrame = Math.max(0, Math.floor(((startMsRel + 40) / 1000) * fps));
  const endFrame = Math.min(
    frames.length - 1,
    Math.ceil((Math.max(startMsRel + 80, endMsRel) / 1000) * fps),
  );
  if (endFrame < startFrame) return null;

  const passes = [
    (frame: any) => frame?.balls?.selected === "bounce" && frame.balls.bounce?.interpolated !== true,
    (frame: any) => frame?.balls?.bounce?.interpolated !== true,
    (frame: any) => !!frame?.balls?.bounce,
  ];

  for (const accepts of passes) {
    for (let frameIndex = startFrame; frameIndex <= endFrame; frameIndex += 1) {
      const frame = frames[frameIndex] as any;
      const bounce = frame?.balls?.bounce;
      const courtPosition = bounce?.court_position;
      if (!bounce || bounce.out_of_sequence === true || !accepts(frame)) continue;

      const heightFt = finiteNumber(courtPosition?.z);
      const x = finiteNumber(courtPosition?.x);
      const y = finiteNumber(courtPosition?.y);
      if (heightFt == null || x == null || y == null) continue;

      return {
        heightFt,
        msRel: (frameIndex / fps) * 1000,
        interpolated: bounce.interpolated === true,
        courtPosition: { x, y, z: heightFt },
      };
    }
  }

  return null;
}

function labelForCurveShape(
  curveShape: number | null,
): TrajectoryShapeDiagnostics["label"] {
  if (curveShape == null) return "Flat / mild";
  if (curveShape < 8) return "Flat / mild";
  if (curveShape < 15) return "Moderate dip";
  if (curveShape < 25) return "Heavy dip";
  return "Diving / nasty";
}

function lineCrossingDistance(args: {
  start: { x?: unknown; y?: unknown } | null | undefined;
  target: { x?: unknown; y?: unknown } | null | undefined;
  lineY: number;
  totalDistance: number;
  maxExtrapolation?: number;
}): number | null {
  const sx = finiteNumber(args.start?.x);
  const sy = finiteNumber(args.start?.y);
  const tx = finiteNumber(args.target?.x);
  const ty = finiteNumber(args.target?.y);
  if (sx == null || sy == null || tx == null || ty == null) return null;
  if (!Number.isFinite(args.totalDistance) || args.totalDistance <= 0) return null;

  const dy = ty - sy;
  if (Math.abs(dy) < 0.001) return null;

  const t = (args.lineY - sy) / dy;
  const maxExtrapolation = args.maxExtrapolation ?? 0.75;
  if (t < -maxExtrapolation || t > 1 + maxExtrapolation) return null;
  return t * args.totalDistance;
}

function courtLineDistanceAlongShot(args: {
  start: { x?: unknown; y?: unknown } | null | undefined;
  target: { x?: unknown; y?: unknown } | null | undefined;
  lineY: number;
  totalDistance: number;
}): number | null {
  const direct = lineCrossingDistance({
    ...args,
    maxExtrapolation: 4,
  });
  if (direct != null) return direct;

  const sy = finiteNumber(args.start?.y);
  const ty = finiteNumber(args.target?.y);
  if (sy == null || !Number.isFinite(args.totalDistance) || args.totalDistance <= 0) {
    return null;
  }

  const dy = ty == null ? 0 : ty - sy;
  const direction =
    Math.abs(dy) > 0.001
      ? Math.sign(dy)
      : sy > 22
        ? -1
        : 1;
  const feetPerCourtY =
    Math.abs(dy) > 0.001 ? args.totalDistance / Math.abs(dy) : 1;

  return (args.lineY - sy) * direction * feetPerCourtY;
}

function projectedDistanceAlongShot(args: {
  start: { x?: unknown; y?: unknown } | null | undefined;
  target: { x?: unknown; y?: unknown } | null | undefined;
  point: { x?: unknown; y?: unknown } | null | undefined;
  totalDistance: number;
}): number | null {
  const sx = finiteNumber(args.start?.x);
  const sy = finiteNumber(args.start?.y);
  const tx = finiteNumber(args.target?.x);
  const ty = finiteNumber(args.target?.y);
  const px = finiteNumber(args.point?.x);
  const py = finiteNumber(args.point?.y);
  if (
    sx == null ||
    sy == null ||
    tx == null ||
    ty == null ||
    px == null ||
    py == null ||
    !Number.isFinite(args.totalDistance) ||
    args.totalDistance <= 0
  ) {
    return null;
  }

  const vx = tx - sx;
  const vy = ty - sy;
  const denom = vx * vx + vy * vy;
  if (denom <= 0.001) return null;

  const t = ((px - sx) * vx + (py - sy) * vy) / denom;
  return Math.max(0, Math.min(args.totalDistance, t * args.totalDistance));
}

function pointDistance2d(
  a: { x: number; y: number },
  b: { x: number; y: number },
): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

function buildQuadraticSideViewCurvePoints(
  points: Array<{ x: number; y: number; label?: string }>,
): Array<{ x: number; y: number }> {
  const start = points[0];
  const apex = points[1];
  const end = points[2];
  if (!start || !apex || !end || end.x <= start.x) return [];

  const t = Math.max(0.08, Math.min(0.92, (apex.x - start.x) / (end.x - start.x)));
  const denom = 2 * (1 - t) * t;
  if (Math.abs(denom) < 0.001) return [];

  const control = {
    x: (apex.x - (1 - t) ** 2 * start.x - t ** 2 * end.x) / denom,
    y: (apex.y - (1 - t) ** 2 * start.y - t ** 2 * end.y) / denom,
  };

  return Array.from({ length: 31 }, (_, index) => {
    const u = index / 30;
    const inv = 1 - u;
    return {
      x: inv ** 2 * start.x + 2 * inv * u * control.x + u ** 2 * end.x,
      y: inv ** 2 * start.y + 2 * inv * u * control.y + u ** 2 * end.y,
    };
  });
}

function buildCvSideViewCurvePoints(args: {
  overlay: ShotNumberOverlay;
  row: KitchenArrivalRow | null | undefined;
  cvJson: CvJson | null | undefined;
  startMsRel: number;
  endMsRel: number;
}): Array<{ x: number; y: number }> {
  const { overlay, row, cvJson, startMsRel, endMsRel } = args;
  if (!row || !cvJson || endMsRel <= startMsRel + 80) return [];

  const frames = cvJson.sessions?.[0]?.rallies?.[row.rallyIndex]?.frames;
  if (!Array.isArray(frames) || frames.length === 0) return [];

  const fps = finiteNumber(cvJson.camera?.fps) ?? 29.97;
  if (fps <= 0) return [];

  const startFrame = Math.max(0, Math.floor((startMsRel / 1000) * fps));
  const endFrame = Math.min(
    frames.length - 1,
    Math.ceil((endMsRel / 1000) * fps),
  );
  if (endFrame <= startFrame) return [];

  const rawPoints: Array<{ x: number; y: number; z: number }> = [];

  const pushPoint = (courtPosition: any) => {
    const x = finiteNumber(courtPosition?.x);
    const y = finiteNumber(courtPosition?.y);
    const z = finiteNumber(courtPosition?.z);
    if (x == null || y == null || z == null) return;

    const previous = rawPoints[rawPoints.length - 1];
    if (previous && Math.hypot(x - previous.x, y - previous.y) < 0.12) return;
    if (previous && Math.hypot(x - previous.x, y - previous.y) > 12) return;

    rawPoints.push({ x, y, z: Math.max(0, z) });
  };

  pushPoint(overlay.trajectoryStart?.location);

  for (let frameIndex = startFrame; frameIndex <= endFrame; frameIndex += 1) {
    const frame = frames[frameIndex] as any;
    const event =
      frame?.balls?.ball ??
      frame?.balls?.shot ??
      frame?.balls?.bounce ??
      null;
    if (!event || event.out_of_sequence === true) continue;
    pushPoint(event.court_position);
  }

  pushPoint(overlay.trajectoryEnd?.location);

  if (rawPoints.length < 2) return [];

  let distance = 0;
  return rawPoints.map((point, index) => {
    if (index > 0) {
      distance += pointDistance2d(rawPoints[index - 1]!, point);
    }
    return { x: distance, y: point.z };
  });
}

function estimateClosestOpponentReaction(args: {
  overlay: ShotNumberOverlay;
  row: KitchenArrivalRow | null | undefined;
  cvJson: CvJson | null | undefined;
  startMsRel: number;
  endMsRel: number;
  hitterTeamId: 0 | 1 | null;
  start: { x?: unknown; y?: unknown; z?: unknown } | null | undefined;
  peak: { x?: unknown; y?: unknown; z?: unknown } | null | undefined;
  target: { x?: unknown; y?: unknown; z?: unknown } | null | undefined;
}): ShotReactionEstimate | null {
  const {
    overlay,
    row,
    cvJson,
    startMsRel,
    endMsRel,
    hitterTeamId,
    start,
    peak,
    target,
  } = args;
  if (hitterTeamId == null || endMsRel <= startMsRel + 40) return null;

  const opponentTeamId = hitterTeamId === 0 ? 1 : 0;
  const playerPositions =
    overlay.player_positions ?? overlay.rawShot?.player_positions ?? [];
  const opponents = Array.isArray(playerPositions)
    ? playerPositions
        .map((position, index) => {
          const x = finiteNumber(position?.x);
          const y = finiteNumber(position?.y);
          const teamId = (index <= 1 ? 0 : 1) as 0 | 1;
          if (x == null || y == null || teamId !== opponentTeamId) return null;
          return { playerId: index, x, y };
        })
        .filter((position): position is { playerId: number; x: number; y: number } => !!position)
    : [];
  if (!opponents.length) return null;

  const samples: Array<{ msRel: number; x: number; y: number; z: number }> = [];
  const pushSample = (msRel: number, courtPosition: any) => {
    const x = finiteNumber(courtPosition?.x);
    const y = finiteNumber(courtPosition?.y);
    const z = finiteNumber(courtPosition?.z);
    if (x == null || y == null || z == null || !Number.isFinite(msRel)) return;
    if (msRel < startMsRel - 80 || msRel > endMsRel + 80) return;

    const previous = samples[samples.length - 1];
    if (previous && Math.abs(previous.msRel - msRel) < 1) return;
    if (previous && Math.hypot(x - previous.x, y - previous.y) > 12) return;
    samples.push({ msRel, x, y, z: Math.max(0, z) });
  };

  pushSample(startMsRel, start);

  const frames = cvJson?.sessions?.[0]?.rallies?.[row?.rallyIndex ?? -1]?.frames;
  const fps = finiteNumber(cvJson?.camera?.fps) ?? 29.97;
  if (Array.isArray(frames) && frames.length > 0 && fps > 0) {
    const startFrame = Math.max(0, Math.floor((startMsRel / 1000) * fps));
    const endFrame = Math.min(frames.length - 1, Math.ceil((endMsRel / 1000) * fps));
    for (let frameIndex = startFrame; frameIndex <= endFrame; frameIndex += 1) {
      const frame = frames[frameIndex] as any;
      const event =
        frame?.balls?.ball ??
        frame?.balls?.shot ??
        frame?.balls?.bounce ??
        null;
      if (!event || event.out_of_sequence === true) continue;
      pushSample((frameIndex / fps) * 1000, event.court_position);
    }
  }

  if (samples.length < 2) {
    pushSample(startMsRel + (endMsRel - startMsRel) * 0.5, peak);
  }
  pushSample(endMsRel, target);

  if (samples.length < 2) return null;
  samples.sort((a, b) => a.msRel - b.msRel);

  let best: ShotReactionEstimate | null = null;
  for (let i = 1; i < samples.length; i += 1) {
    const prev = samples[i - 1]!;
    const next = samples[i]!;
    const dy = next.y - prev.y;
    if (Math.abs(dy) < 0.001) continue;

    for (const opponent of opponents) {
      const t = (opponent.y - prev.y) / dy;
      if (t < 0 || t > 1) continue;

      const x = prev.x + (next.x - prev.x) * t;
      const z = prev.z + (next.z - prev.z) * t;
      if (z < REACTION_MIN_VOLLEY_HEIGHT_FT || z > REACTION_MAX_VOLLEY_HEIGHT_FT) {
        continue;
      }

      const lateralDistanceFt = Math.abs(x - opponent.x);
      if (lateralDistanceFt > REACTION_REACH_WIDTH_FT) continue;

      const timeSec =
        (prev.msRel + (next.msRel - prev.msRel) * t - startMsRel) / 1000;
      if (timeSec < 0) continue;

      if (
        !best ||
        timeSec < best.timeSec - 0.01 ||
        (Math.abs(timeSec - best.timeSec) <= 0.01 &&
          lateralDistanceFt < best.lateralDistanceFt)
      ) {
        best = {
          playerId: opponent.playerId,
          timeSec,
          lateralDistanceFt,
          ballHeightFt: z,
        };
      }
    }
  }

  if (!best) {
    for (let i = 1; i < samples.length; i += 1) {
      const prev = samples[i - 1]!;
      const next = samples[i]!;
      const dx = next.x - prev.x;
      const dy = next.y - prev.y;
      const denom = dx * dx + dy * dy;
      if (denom <= 0.001) continue;

      for (const opponent of opponents) {
        const t = Math.max(
          0,
          Math.min(1, ((opponent.x - prev.x) * dx + (opponent.y - prev.y) * dy) / denom),
        );
        const x = prev.x + dx * t;
        const y = prev.y + dy * t;
        const z = prev.z + (next.z - prev.z) * t;
        if (z < REACTION_MIN_VOLLEY_HEIGHT_FT || z > REACTION_MAX_VOLLEY_HEIGHT_FT) {
          continue;
        }

        const lateralDistanceFt = Math.hypot(x - opponent.x, y - opponent.y);
        if (lateralDistanceFt > REACTION_REACH_WIDTH_FT) continue;

        const timeSec =
          (prev.msRel + (next.msRel - prev.msRel) * t - startMsRel) / 1000;
        if (timeSec < 0) continue;

        if (
          !best ||
          lateralDistanceFt < best.lateralDistanceFt - 0.15 ||
          (Math.abs(lateralDistanceFt - best.lateralDistanceFt) <= 0.15 &&
            timeSec < best.timeSec)
        ) {
          best = {
            playerId: opponent.playerId,
            timeSec,
            lateralDistanceFt,
            ballHeightFt: z,
          };
        }
      }
    }
  }

  return best;
}

function getTrajectoryShapeDiagnostics(
  overlay: ShotNumberOverlay,
  row?: KitchenArrivalRow | null,
  cvJson?: CvJson | null,
): TrajectoryShapeDiagnostics | null {
  const movement = overlay.rawShot?.resulting_ball_movement;
  const trajectory = movement?.trajectory;
  const peak = trajectory?.peak;
  const start = trajectory?.start?.location;
  const end = trajectory?.end?.location;
  const bounce = firstTrackedBounceForShot({ overlay, row, cvJson });
  const target = bounce?.courtPosition ?? end;

  const speedMph = finiteNumber(movement?.speed);
  const hitterPlayerId = finiteNumber(overlay.playerId ?? overlay.rawShot?.player_id);
  const hitterTeamId =
    hitterPlayerId == null ? null : ((hitterPlayerId >= 2 ? 1 : 0) as 0 | 1);
  const payloadDistanceFt = finiteNumber(movement?.distance);
  const geometricDistanceFt = horizontalDistanceFt(start, target);
  const distanceFt = payloadDistanceFt ?? geometricDistanceFt;
  const startCourtY = finiteNumber(start?.y);
  const startHeightFt = finiteNumber(start?.z);
  const peakHeight = finiteNumber(peak?.z);
  const trajectoryEndHeight = finiteNumber(end?.z);
  const endHeight = bounce?.heightFt ?? trajectoryEndHeight;

  if (speedMph == null || peakHeight == null || endHeight == null) {
    return null;
  }

  const postApexDropFt = Math.max(0, peakHeight - endHeight);
  const rawDip = postApexDropFt * speedMph;
  const normalizedDip =
    distanceFt != null && distanceFt > 0
      ? 100 * (postApexDropFt / distanceFt)
      : null;
  const speedFactor = Math.sqrt(speedMph / 25);
  const curveShape =
    normalizedDip != null ? normalizedDip * speedFactor : null;
  const totalDistance = distanceFt ?? geometricDistanceFt ?? 0;
  const overlays = [...(row?.shotNumberOverlays ?? [])].sort(
    (a, b) =>
      getOverlayMsRel(a, row?.segmentStartMs, row?.segmentEndMs) -
      getOverlayMsRel(b, row?.segmentStartMs, row?.segmentEndMs),
  );
  const currentIndex = overlays.findIndex(
    (candidate) => candidate.shotNumber === overlay.shotNumber,
  );
  const nextOverlay =
    currentIndex >= 0 && currentIndex + 1 < overlays.length
      ? overlays[currentIndex + 1]
      : null;
  const startMsRel = getOverlayMsRel(overlay, row?.segmentStartMs, row?.segmentEndMs);
  const nextMsRel = nextOverlay
    ? getOverlayMsRel(nextOverlay, row?.segmentStartMs, row?.segmentEndMs)
    : null;
  const endMsRel =
    bounce?.msRel ??
    nextMsRel ??
    normalizeShotMsRelForRow(row, overlay.trajectoryEnd?.ms) ??
    startMsRel + 2200;
  const apexDistance =
    totalDistance > 0 ? horizontalDistanceFt(start, peak) : null;
  const apexX =
    apexDistance != null && totalDistance > 0
      ? Math.max(0, Math.min(totalDistance, apexDistance))
      : totalDistance * 0.5;
  const startHeight = startHeightFt ?? endHeight;
  const sideViewPoints = [
    { x: 0, y: startHeight, label: "contact" },
    { x: apexX, y: peakHeight, label: "apex" },
    {
      x: totalDistance,
      y: endHeight,
      label: bounce ? "bounce" : "end",
    },
  ];
  const fallbackSideViewCurvePoints =
    buildQuadraticSideViewCurvePoints(sideViewPoints);
  const cvSideViewCurvePoints = buildCvSideViewCurvePoints({
    overlay,
    row,
    cvJson,
    startMsRel,
    endMsRel,
  });
  const usesCvSideView = cvSideViewCurvePoints.length >= 2;
  const cvApexIndex = usesCvSideView
    ? cvSideViewCurvePoints.reduce((bestIndex, point, index, points) => {
        return point.y > points[bestIndex]!.y ? index : bestIndex;
      }, 0)
    : -1;
  const displaySideViewPoints = usesCvSideView
    ? [
        { ...cvSideViewCurvePoints[0]!, label: "contact" },
        { ...cvSideViewCurvePoints[cvApexIndex]!, label: "apex" },
        {
          ...cvSideViewCurvePoints[cvSideViewCurvePoints.length - 1]!,
          label: bounce ? "bounce" : "end",
        },
      ]
    : sideViewPoints;
  const sideViewFlipped = startCourtY != null && startCourtY > 22;
  const courtLandmarks: Array<{ x: number; label: string; heightFt: number | undefined }> = [
    { y: 0, label: "baseline" },
    { y: 22, label: "net", heightFt: MINI_PLOT_NET_HEIGHT_FT },
    { y: 15, label: "kitchen" },
    { y: 29, label: "kitchen" },
    { y: 44, label: "baseline" },
  ]
    .map((landmark) => {
      const x = courtLineDistanceAlongShot({
        start,
        target,
        lineY: landmark.y,
        totalDistance,
      });
      return x == null
        ? null
        : { x, label: landmark.label, heightFt: landmark.heightFt };
    })
    .filter(
      (
        landmark,
      ): landmark is { x: number; label: string; heightFt: number | undefined } =>
        !!landmark,
    );
  const playerPositions =
    overlay.player_positions ?? overlay.rawShot?.player_positions ?? [];
  const playerMarkers = Array.isArray(playerPositions)
    ? playerPositions
        .map((position, index) => {
          const x = projectedDistanceAlongShot({
            start,
            target,
            point: position,
            totalDistance,
          });
          if (x == null) return null;
          return {
            x,
            label: `p${index}`,
            teamId: (index <= 1 ? 0 : 1) as 0 | 1,
          };
        })
        .filter(
          (marker): marker is { x: number; label: string; teamId: 0 | 1 } =>
            !!marker,
        )
    : [];
  const reactionEstimate = estimateClosestOpponentReaction({
    overlay,
    row,
    cvJson,
    startMsRel,
    endMsRel,
    hitterTeamId,
    start,
    peak,
    target,
  });

  return {
    speedMph,
    distanceFt,
    peakHeightFt: peakHeight,
    endHeightFt: endHeight,
    startHeightFt,
    postApexDropFt,
    rawDip,
    normalizedDip,
    speedFactor,
    curveShape,
    targetLabel: bounce ? "bounce" : "trajectory end",
    label: labelForCurveShape(curveShape),
    hitterTeamId,
    sideViewPoints: displaySideViewPoints,
    sideViewCurvePoints:
      cvSideViewCurvePoints.length >= 2
        ? cvSideViewCurvePoints
        : fallbackSideViewCurvePoints,
    sideViewFlipped,
    courtLandmarks,
    playerMarkers,
    reactionEstimate,
  };
}

function classifyShotTrajectoryShape(
  overlay: ShotNumberOverlay,
  row?: KitchenArrivalRow | null,
  cvJson?: CvJson | null,
): string | null {
  const diagnostics = getTrajectoryShapeDiagnostics(overlay, row, cvJson);
  if (!diagnostics) return null;

  return diagnostics.label;
}

function formatDiagNumber(value: number | null, digits = 1): string {
  return value == null || !Number.isFinite(value) ? "n/a" : value.toFixed(digits);
}

function smoothSvgPathFromPoints(
  points: Array<{ x: number; y: number }>,
  sx: (x: number) => number,
  sy: (y: number) => number,
): string {
  if (!points.length) return "";
  if (points.length === 1) return `M ${sx(points[0]!.x)} ${sy(points[0]!.y)}`;
  if (points.length === 2) {
    return `M ${sx(points[0]!.x)} ${sy(points[0]!.y)} L ${sx(points[1]!.x)} ${sy(
      points[1]!.y,
    )}`;
  }

  const commands = [`M ${sx(points[0]!.x)} ${sy(points[0]!.y)}`];
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[Math.max(0, i - 1)]!;
    const p1 = points[i]!;
    const p2 = points[i + 1]!;
    const p3 = points[Math.min(points.length - 1, i + 2)]!;
    const c1 = {
      x: p1.x + (p2.x - p0.x) / 6,
      y: p1.y + (p2.y - p0.y) / 6,
    };
    const c2 = {
      x: p2.x - (p3.x - p1.x) / 6,
      y: p2.y - (p3.y - p1.y) / 6,
    };
    commands.push(
      `C ${sx(c1.x)} ${sy(c1.y)} ${sx(c2.x)} ${sy(c2.y)} ${sx(p2.x)} ${sy(
        p2.y,
      )}`,
    );
  }

  return commands.join(" ");
}

function TrajectorySideViewChart(props: {
  diagnostics: TrajectoryShapeDiagnostics;
}) {
  const { diagnostics } = props;
  const arrowMarkerId = `trajectory-shape-arrow-${React.useId().replace(
    /[^a-zA-Z0-9_-]/g,
    "",
  )}`;
  const width = 260;
  const height = 118;
  const pad = 16;
  const points = diagnostics.sideViewPoints;
  const curvePoints = diagnostics.sideViewCurvePoints;
  if (curvePoints.length < 2) {
    return (
      <Box sx={{ mt: 0.8, color: "rgba(255,255,255,0.72)" }}>
        Mini plot unavailable without usable trajectory points.
      </Box>
    );
  }

  const xValues = [
    ...points.map((point) => point.x),
    ...curvePoints.map((point) => point.x),
    ...diagnostics.courtLandmarks.map((landmark) => landmark.x),
    ...diagnostics.playerMarkers.map((player) => player.x),
    0,
  ];
  const minX = Math.min(...xValues);
  const maxX = Math.max(...xValues);
  const spanX = maxX - minX;
  const maxY = Math.max(
    MINI_PLOT_DEFAULT_MAX_HEIGHT_FT,
    ...points.map((point) => point.y),
    ...diagnostics.courtLandmarks.map((landmark) => landmark.heightFt ?? 0),
  );

  if (!Number.isFinite(spanX) || spanX <= 0) {
    return (
      <Box sx={{ mt: 0.8, color: "rgba(255,255,255,0.72)" }}>
        Side view unavailable without distance.
      </Box>
    );
  }

  const sx = (x: number) => {
    const displayX = diagnostics.sideViewFlipped ? maxX - x : x - minX;
    return pad + (displayX / spanX) * (width - pad * 2);
  };
  const sy = (y: number) =>
    height - pad - (Math.max(0, y) / maxY) * (height - pad * 2);
  const path = smoothSvgPathFromPoints(curvePoints, sx, sy);
  const arrowStart =
    curvePoints[Math.max(0, Math.floor((curvePoints.length - 1) * 0.54))] ??
    curvePoints[0]!;
  const arrowEnd =
    curvePoints[Math.max(0, Math.floor((curvePoints.length - 1) * 0.66))] ??
    curvePoints[curvePoints.length - 1]!;
  const shotColor =
    diagnostics.hitterTeamId === 1
      ? "rgba(120,235,165,0.95)"
      : "rgba(120,190,255,0.95)";
  const playerFill = (teamId: 0 | 1) =>
    teamId === 0 ? "rgba(120,190,255,0.95)" : "rgba(120,235,165,0.95)";

  return (
    <Box
      component="svg"
      viewBox={`0 0 ${width} ${height}`}
      sx={{ display: "block", width: "100%", height: 118, mt: 0.8 }}
    >
      <defs>
        <marker
          id={arrowMarkerId}
          markerWidth="8"
          markerHeight="8"
          refX="7"
          refY="4"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M 0 0 L 8 4 L 0 8 z" fill={shotColor} />
        </marker>
      </defs>
      <line
        x1={pad}
        y1={sy(0)}
        x2={width - pad}
        y2={sy(0)}
        stroke="rgba(255,255,255,0.36)"
        strokeWidth="1"
      />
      {diagnostics.courtLandmarks.map((landmark, index) => (
        <g key={`${landmark.label}-${index}`}>
          <line
            x1={sx(landmark.x)}
            y1={landmark.heightFt != null ? sy(landmark.heightFt) : pad}
            x2={sx(landmark.x)}
            y2={height - pad}
            stroke={
              landmark.label === "net"
                ? "rgba(255,255,255,0.62)"
                : landmark.label === "kitchen"
                  ? "rgba(255,255,255,0.48)"
                  : "rgba(255,255,255,0.32)"
            }
            strokeDasharray={
              landmark.label === "net"
                ? undefined
                : landmark.label === "kitchen"
                  ? "4 3"
                  : "2 4"
            }
            strokeWidth={
              landmark.label === "net"
                ? "1.4"
                : landmark.label === "kitchen"
                  ? "1.25"
                  : "1"
            }
          />
          {landmark.heightFt != null ? (
            <line
              x1={sx(landmark.x) - 5}
              y1={sy(landmark.heightFt)}
              x2={sx(landmark.x) + 5}
              y2={sy(landmark.heightFt)}
              stroke="rgba(255,255,255,0.62)"
              strokeWidth="1.4"
            />
          ) : null}
          <text
            x={sx(landmark.x)}
            y={height - 3}
            textAnchor="middle"
            fill="rgba(255,255,255,0.72)"
            fontSize="9"
          >
            {landmark.label}
          </text>
        </g>
      ))}
      <path
        d={path}
        fill="none"
        stroke={shotColor}
        strokeWidth="2.2"
      />
      <circle r="3.5" fill={shotColor} stroke="rgba(0,0,0,0.65)" strokeWidth="1">
        <animateMotion
          dur="1.6s"
          repeatCount="indefinite"
          path={path}
        />
      </circle>
      <line
        x1={sx(arrowStart.x)}
        y1={sy(arrowStart.y)}
        x2={sx(arrowEnd.x)}
        y2={sy(arrowEnd.y)}
        stroke={shotColor}
        strokeWidth="2.2"
        markerEnd={`url(#${arrowMarkerId})`}
      />
      {points
        .filter((point) => point.label !== "apex")
        .map((point) => (
          <g key={point.label ?? `${point.x}:${point.y}`}>
            <circle
              cx={sx(point.x)}
              cy={sy(point.y)}
              r="3"
              fill="rgba(255,255,255,0.95)"
            />
            {point.label ? (
              <text
                x={sx(point.x)}
                y={Math.max(10, sy(point.y) - 7)}
                textAnchor="middle"
                fill="rgba(255,255,255,0.88)"
                fontSize="9"
              >
                {point.label}
              </text>
            ) : null}
          </g>
        ))}
      {diagnostics.playerMarkers.map((player) => (
        <g key={player.label}>
          <circle
            cx={sx(player.x)}
            cy={sy(0)}
            r="3.2"
            fill={playerFill(player.teamId)}
            stroke="rgba(0,0,0,0.65)"
            strokeWidth="1"
          />
          <text
            x={sx(player.x)}
            y={Math.min(height - 5, sy(0) + 12)}
            textAnchor="middle"
            fill="rgba(255,255,255,0.86)"
            fontSize="9"
          >
            {player.label}
          </text>
        </g>
      ))}
    </Box>
  );
}

function ShotTrajectoryShapePanel(props: {
  overlay: ShotNumberOverlay;
  row?: KitchenArrivalRow | null;
  cvJson?: CvJson | null;
}) {
  const showDiagnosticRows = false;
  const [collapsed, setCollapsed] = React.useState(false);
  const diagnostics = React.useMemo(
    () => getTrajectoryShapeDiagnostics(props.overlay, props.row, props.cvJson),
    [props.overlay, props.row, props.cvJson],
  );

  if (!diagnostics) return null;

  const targetText =
    diagnostics.targetLabel === "bounce" ? "bounce" : "trajectory end";
  const shotTitle =
    props.row?.rallyIndex != null
      ? `R${props.row.rallyIndex + 1} Shot ${props.overlay.shotNumber}`
      : `Shot ${props.overlay.shotNumber}`;

  const statRows = [
    ["Apex height", `${diagnostics.peakHeightFt.toFixed(1)} ft`],
    ["End height", `${diagnostics.endHeightFt.toFixed(1)} ft`],
    ["Drop after apex", `${diagnostics.postApexDropFt.toFixed(1)} ft`],
    ["Distance", `${formatDiagNumber(diagnostics.distanceFt)} ft`],
    ["Speed", `${diagnostics.speedMph.toFixed(1)} mph`],
    ["Raw dip (drop x speed)", diagnostics.rawDip.toFixed(1)],
    ["Normalized dip (drop / dist x 100)", formatDiagNumber(diagnostics.normalizedDip)],
    ["Speed factor (sqrt speed / 25)", diagnostics.speedFactor.toFixed(2)],
    [
      "Opponent reaction",
      diagnostics.reactionEstimate
        ? `p${diagnostics.reactionEstimate.playerId} · ${diagnostics.reactionEstimate.timeSec.toFixed(2)}s`
        : "n/a",
    ],
    ["Target", targetText],
  ];

  if (collapsed) {
    return (
      <Box
        sx={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 19,
          px: 1,
          py: 0.55,
          borderRadius: 1,
          bgcolor: "rgba(0,0,0,0.72)",
          border: "1px solid rgba(255,255,255,0.18)",
          color: "white",
          pointerEvents: "auto",
          cursor: "pointer",
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          setCollapsed(false);
        }}
      >
        <Box sx={{ fontWeight: 900, fontSize: 11.5 }}>
          {shotTitle} - {diagnostics.label}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "absolute",
        top: 10,
        right: 10,
        zIndex: 19,
        width: 260,
        p: 0.85,
        borderRadius: 1,
        bgcolor: "rgba(0,0,0,0.72)",
        border: "1px solid rgba(255,255,255,0.18)",
        color: "white",
        pointerEvents: "auto",
        fontSize: 11.5,
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ fontWeight: 900, flex: 1, minWidth: 0 }}>
          {shotTitle} - {diagnostics.label}
        </Box>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setCollapsed(true)}
          sx={{
            minHeight: 22,
            py: 0,
            px: 0.8,
            fontSize: 11,
            textTransform: "none",
            color: "white",
            borderColor: "rgba(255,255,255,0.28)",
          }}
        >
          Hide
        </Button>
      </Box>
      {showDiagnosticRows ? (
        <>
          <Box sx={{ mt: 0.5 }}>Measured to {targetText}.</Box>
          <Box
            sx={{
              mt: 0.8,
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: "2px 10px",
              gridAutoRows: "14px",
              height: 158,
              overflow: "hidden",
            }}
          >
            {statRows.map(([label, value]) => (
              <React.Fragment key={label}>
                <Box
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </Box>
                <Box sx={{ textAlign: "right", whiteSpace: "nowrap" }}>{value}</Box>
              </React.Fragment>
            ))}
          </Box>
        </>
      ) : null}
      <TrajectorySideViewChart diagnostics={diagnostics} />
    </Box>
  );
}

function getCurrentShotNumberFromPlayback(args: {
  currentTimeSec: number;
  overlays?: ShotNumberOverlay[] | null;
  segmentStartMs?: number | null;
  segmentEndMs?: number | null;
  firstShotLeadMs?: number;
}): number | null {
  const {
    currentTimeSec,
    overlays,
    segmentStartMs,
    segmentEndMs,
    firstShotLeadMs = PLAYBACK_FIRST_SHOT_CONTEXT_LEAD_MS,
  } = args;

  const ovs = Array.isArray(overlays)
    ? [...overlays].sort((a, b) => {
        return (
          getOverlayMsRel(a, segmentStartMs, segmentEndMs) -
          getOverlayMsRel(b, segmentStartMs, segmentEndMs)
        );
      })
    : [];
  if (!ovs.length) return null;

  const currentMsRel = currentTimeSec * 1000;

  let bestShotNumber: number | null = null;

  for (let i = 0; i < ovs.length; i += 1) {
    const ov = ovs[i];
    if (!ov) continue;
    const contactMsRel = getOverlayMsRel(ov, segmentStartMs, segmentEndMs);
    const previousContactMsRel =
      i > 0 && ovs[i - 1]
        ? getOverlayMsRel(ovs[i - 1]!, segmentStartMs, segmentEndMs)
        : null;
    const activationMsRel =
      previousContactMsRel == null
        ? Math.max(0, contactMsRel - firstShotLeadMs)
        : previousContactMsRel +
          (contactMsRel - previousContactMsRel) *
            PLAYBACK_NEXT_SHOT_CONTEXT_FRACTION;
    if (!Number.isFinite(activationMsRel)) continue;

    if (activationMsRel <= currentMsRel) {
      bestShotNumber = ov.shotNumber ?? bestShotNumber;
    } else {
      break;
    }
  }

  return bestShotNumber;
}

export type ActiveRallyVideoHandle = {
  //toggleAiPanel: () => void;
  closeAiPanel: () => void;
  toggleExportHtml: () => Promise<void>;
};

type ActiveRallyVideoProps = {
  //    onAiOpenChange?: (open: boolean) => void;

  defaultPlaybackRate?: VideoPlaybackRate;
  aiOpenViaParent?: boolean;
  //onAiOpenChange?: (open: boolean) => void;

  highlightAiEntryPoint?: boolean;
  onAiButtonAnchorReady?: (el: HTMLElement | null) => void;

  renderPlayerIdentity?: RenderPlayerIdentity;
  resolvePlayerAvatarSrc?: (playerId: number | null | undefined) => string | undefined;
  titleNode?: React.ReactNode;

  activeShotExtraText?: string;
  shotOverlayPersistentExtraText?: (
    overlay: ShotNumberOverlay,
  ) => string | undefined;

  focusedFilter: FocusedReasonStakeForTeam | null;
  insightsToCvPlayerIndex: PlayerIndexMap;
  packageId: string;
  aiContextLabel: string;
  row: KitchenArrivalRow | null;
  rows: KitchenArrivalRow[];
  activeShotNumber: number | null;
  beginningScore?: [number, number] | null;
  endingScore?: [number, number] | null;
  showCondensedTimeline: boolean;
  condensedTimelineLeftEl?: React.ReactNode;
  fontColor: string;
  controlsPlacement: "top" | "bottom" | "left";
  firefightsClips: FirefightClip[];
  firefightRallySet: Set<number>;
  showRateControls?: boolean;
  highlightActiveShotTrajectory?: boolean;
  showUltraSlowRate?: boolean;
  showTitle?: boolean;
  showScoreEtc?: boolean;
  autoPlayOnRallyChange?: boolean;
  showAi?: boolean;
  aiDrawerMode?: "overlay" | "split";
  showLoopScrubber?: boolean;
  defaultLoopUiOpen?: boolean;
  disablePopout?: boolean;
  showPopoutButton?: boolean;
  showPreviewPlayPauseButton?: boolean;
  seedPlayback?: { timeSec: number; paused: boolean; nonce: number };
  seedLoopRegion?: {
    enabled: boolean;
    aSec: number;
    bSec: number;
    nonce: number;
  };
  initialNextBallReadSeed?: NextBallReadSeed;
  onNextBallReadChange?: (next: ActiveNextBallRead | null) => void;
  onLoopRegionChange?: (loop: {
    enabled: boolean;
    aSec: number;
    bSec: number;
  }) => void;
  onVideoRef?: (ref: React.RefObject<HTMLVideoElement | null>) => void;
  isInFullPopup?: boolean;
  onClipFocusChange?: (focus: ClipFocus | undefined) => void;
  onOpenRallyFromSimilarPopup?: (rallyIndex: number) => void;
  showSimilarControls?: boolean;
  forceMuted?: boolean;
  previewMode?: boolean;
  focusedPlayerId?: number | null | undefined;
  cvJson?: CvJson | null;
  showAdvantageScaleMarkers?: boolean;
  showAdvantageScaleSparklines?: boolean;
  seekToActiveShotOnChange?: boolean;
  stableVideoStage?: boolean;
  showTrajectoryShapePanel?: boolean;
  showShotSpaceButton?: boolean;
  onOpenShotSpace?: () => void;
  showShotBookmarksButton?: boolean;
  showShotLabels?: boolean;
  showTimelineTicks?: boolean;
  shotBookmarksCount?: number;
  onOpenShotBookmarks?: () => void;
  onBookmarkShot?: (payload: {
    rallyIndex: number;
    shotNumber: number;
    timeSec: number | null;
    playerId: number | null;
  }) => void;
  pounceOverlay?: PounceOverlay | null;
  onPlaybackShotNumberChange?: (shotNumber: number | null) => void;
  onShotUniverseContextShotNumberChange?: (shotNumber: number | null) => void;
  onPlaybackPause?: (currentTimeSec: number) => void;
  onPlaybackPlay?: (currentTimeSec: number) => void;
  onStepShot?: (dir: -1 | 1) => void;
  onStepRally?: (dir: -1 | 1) => void;
  controlsPortalContainer?: HTMLElement | null;
  loopControlsPortalContainer?: HTMLElement | null;
  requireLoopControlsPortal?: boolean;
  onControlsInteractionChange?: (active: boolean) => void;
  disableShotStepControls?: boolean;
  shotOverlayManualHighlightLabel?: string;
  pauseOtherVideosOnMount?: boolean;
  rallyLensMoments?: ActiveRallyLensMoment[];
  initialRallyLens?: ActiveRallyLensKey;
  rallyLensPanelPlacement?: "default" | "embeddedBottom";
  showRallyLensControls?: boolean;
  showRallyLensPanel?: boolean;
  popoutInitialRallyLens?: ActiveRallyLensKey;
  initialRallyLensMomentKey?: string | null;
};

export const ActiveRallyVideo = React.forwardRef<
  ActiveRallyVideoHandle,
  ActiveRallyVideoProps
>(function ActiveRallyVideo(props, ref) {
  // export function ActiveRallyVideo(props: {
  const isDemoRun = React.useMemo(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("demoRun") === "1";
  }, []);

  //   highlightAiEntryPoint?: boolean;
  // onAiButtonAnchorReady?: (el: HTMLElement | null) => void;

  //   renderPlayerIdentity?: RenderPlayerIdentity;
  //   titleNode?: React.ReactNode;

  //     activeShotExtraText?: string;
  //   shotOverlayPersistentExtraText?: (
  //     overlay: ShotNumberOverlay,
  //   ) => string | undefined;

  //   focusedFilter: FocusedReasonStakeForTeam | null;
  //   insightsToCvPlayerIndex: PlayerIndexMap;
  //   packageId: string;
  //   aiContextLabel: string;
  //   row: KitchenArrivalRow | null;
  //   rows: KitchenArrivalRow[]; // for timeline
  //   activeShotNumber: number | null;
  //   beginningScore?: [number, number] | null;
  //   endingScore?: [number, number] | null;
  //   showCondensedTimeline: boolean;
  //   condensedTimelineLeftEl?: React.ReactNode;
  //   fontColor: string;
  //   controlsPlacement: "top" | "bottom" | "left";
  //   firefightsClips: FirefightClip[];
  //   firefightRallySet: Set<number>;
  //   showRateControls?: boolean;
  //   showTitle?: boolean;
  //   showScoreEtc?: boolean;
  //   autoPlayOnRallyChange?: boolean; // default true

  //   /** NEW: caller decides if AI affordance appears */
  //   showAi?: boolean;
  //   aiDrawerMode?: "overlay" | "split";

  //   /** NEW: caller decides if loop/scrubber affordance appears */
  //   showLoopScrubber?: boolean;

  //   /** NEW: caller decides if the scrubber overlay starts open */
  //   defaultLoopUiOpen?: boolean;

  //   disablePopout?: boolean;
  //   /** show/hide the ↗ icon in the top-right */
  //   showPopoutButton?: boolean;

  //   /** optional: apply a playback seed once (useful for popout instance) */
  //   seedPlayback?: { timeSec: number; paused: boolean; nonce: number };
  //   seedLoopRegion?: {
  //     enabled: boolean;
  //     aSec: number;
  //     bSec: number;
  //     nonce: number;
  //   };
  //   onLoopRegionChange?: (loop: {
  //     enabled: boolean;
  //     aSec: number;
  //     bSec: number;
  //   }) => void;

  //   /**
  //    * NEW: Optional hook for wiring AI later.
  //    * If provided, called with the computed ms range when user clicks "Ask AI".
  //    */
  //   // onAskA?: (payload: ClipFocus
  //   //   // {
  //   //   // rallyIndex: number;
  //   //   // videoUrl: string;
  //   //   // segmentStartMs: number;
  //   //   // segmentEndMs: number;
  //   //   // rangeStartMs: number;
  //   //   // rangeEndMs: number;
  //   //   // rangeMode: "loop" | "full";
  //   // ) => void;
  //   /** Optional: expose the internal videoRef to parent (popout) */
  //   onVideoRef?: (ref: React.RefObject<HTMLVideoElement | null>) => void;
  //   isInFullPopup?: boolean; // if true, we are the big video in the popup, not the small one; mainly for styling decisions (e.g. condensed timeline placement)

  //   onClipFocusChange?: (focus: ClipFocus | undefined) => void;

  //   onOpenRallyFromSimilarPopup?: (rallyIndex: number) => void;
  //   showSimilarControls?: boolean;

  //   forceMuted?: boolean;
  //   previewMode?: boolean;

  //   focusedPlayerId?: number | null | undefined;
  //   cvJson?: CvJson | null;
  //   seekToActiveShotOnChange?: boolean; // default true

  //   pounceOverlay?: PounceOverlay | null | undefined;
  // onPlaybackShotNumberChange?: (shotNumber: number | null) => void;
  // forcePause?: boolean;
  // }) {

  const {
    aiDrawerMode = "overlay",
    packageId,
    row,
    rows,
    activeShotNumber,
    beginningScore,
    endingScore,
    showCondensedTimeline,
    fontColor,
    controlsPlacement,
    firefightsClips,
    firefightRallySet,
    showRateControls = true,
    showScoreEtc = true,
    showAi = true,
    showLoopScrubber = true,
    showPopoutButton = true,
    showPreviewPlayPauseButton = false,
    defaultLoopUiOpen = false,
    //onAskAi,
    showTitle = true,
    autoPlayOnRallyChange: autoPlayOnRallyChangeProp = true,
    seekToActiveShotOnChange = true,
    disablePopout = false,
    seedPlayback,
    initialNextBallReadSeed,
    isInFullPopup = false,
    focusedFilter,
    condensedTimelineLeftEl,
    aiContextLabel,
    onClipFocusChange,
    onVideoRef,
    onNextBallReadChange,
    onLoopRegionChange,
    seedLoopRegion,
    onOpenRallyFromSimilarPopup,
    showSimilarControls = true,
    forceMuted = false,
    previewMode = false,
    focusedPlayerId,
    cvJson,
    showAdvantageScaleMarkers = false,
    showAdvantageScaleSparklines = false,
    stableVideoStage = false,
    showTrajectoryShapePanel = false,
    showShotSpaceButton = false,
    onOpenShotSpace,
    showShotBookmarksButton = false,
    showShotLabels = true,
    showTimelineTicks = true,
    shotBookmarksCount = 0,
    onOpenShotBookmarks,
    onBookmarkShot,
    insightsToCvPlayerIndex,
    renderPlayerIdentity,
    resolvePlayerAvatarSrc,
    pounceOverlay,
    titleNode,
    shotOverlayPersistentExtraText,
    activeShotExtraText,
    onPlaybackShotNumberChange,
    onShotUniverseContextShotNumberChange,
    onPlaybackPause,
    onPlaybackPlay,
    onStepShot,
    onStepRally,
    controlsPortalContainer,
    loopControlsPortalContainer,
    requireLoopControlsPortal = false,
    onControlsInteractionChange,
    disableShotStepControls = false,
    shotOverlayManualHighlightLabel,
    pauseOtherVideosOnMount = isInFullPopup,
    rallyLensMoments = [],
    initialRallyLens = "off",
    rallyLensPanelPlacement = "default",
    showRallyLensControls = false,
    showRallyLensPanel = true,
    popoutInitialRallyLens,
    initialRallyLensMomentKey,
    //forcePause = false,
    highlightAiEntryPoint = false,
    onAiButtonAnchorReady,
    defaultPlaybackRate=1,
  } = props;

  const autoPlayOnRallyChange = previewMode
    ? false
    : autoPlayOnRallyChangeProp;
  const nextBallReadSeedNonce = initialNextBallReadSeed?.nonce ?? null;
  const nextBallReadSeedStage = initialNextBallReadSeed?.stage ?? null;
  const nextBallReadSeedRep = initialNextBallReadSeed?.rep ?? null;

  // console.error("props", {props});

  const DEBUG_NO_VIDEO = false;
  //   const showLoopScrubber = false;
  // const showAi = false;
  // const showPopoutButton = false;

  const pounceCatClipId = React.useId();

  const showCourtLabels = false;

  const TRAJ_COLOR = "rgba(120, 200, 255, 0.28)"; // cooler + much softer
  const TRAJ_WIDTH = 1.2;

  // const [similarMode, setSimilarMode] = React.useState<SimilarityMode>({
  //   type: "global",
  // });
  // const [similarPopupOpen, setSimilarPopupOpen] = React.useState(false);
  // const [similarPopupRect, setSimilarPopupRect] = React.useState({
  //   x: 50,
  //   y: 50,
  //   w: 1280,
  //   h: 900,
  // });

  // const worldToScreen = null as any; // 3:35pm Mar 26, 2026 useWorldToScreenFromCv(cvJson);
  const worldToScreen = useWorldToScreenFromCv(cvJson);
  const approximateTrajectoryProjection = useApprox3DTrajectoryProjectionFromCv(cvJson ?? null);

  // const { buildTrajectoryPolylineApprox3D } =
  //   useApprox3DTrajectoryProjectionFromCv(cvJson || null);

  const videoOverlayRef = React.useRef<HTMLDivElement | null>(null);

  // const lastEmittedShotNumberRef = React.useRef<number | null>(null);

  const scrubLastXRef = React.useRef<number | null>(null);
  const scrubAccumRef = React.useRef<number>(0);
  const suppressMouseScrubUntilRef = React.useRef<number>(0);
  const scrubPointerIdRef = React.useRef<number | null>(null);
  const scrubPointerElRef = React.useRef<Element | null>(null);
  const onControlsInteractionChangeRef = React.useRef(onControlsInteractionChange);

  React.useEffect(() => {
    onControlsInteractionChangeRef.current = onControlsInteractionChange;
  }, [onControlsInteractionChange]);

  const [scrubPlayheadSecUi, setScrubPlayheadSecUi] = React.useState<number>(0);
  const scrubPlayheadRafRef = React.useRef<number | null>(null);

  const setScrubPlayheadUiRaf = (sec: number) => {
    if (scrubPlayheadRafRef.current != null) return;
    scrubPlayheadRafRef.current = requestAnimationFrame(() => {
      scrubPlayheadRafRef.current = null;
      setScrubPlayheadSecUi(sec);
    });
  };

  React.useEffect(() => {
    return () => {
      if (scrubPlayheadRafRef.current != null) {
        cancelAnimationFrame(scrubPlayheadRafRef.current);
        scrubPlayheadRafRef.current = null;
      }
    };
  }, []);

  // crude fallback if rVFC not available
  const FALLBACK_FPS = 30;
  const FRAME_SEC = 1 / FALLBACK_FPS;

  // when available, this is the best “true” frame stepping you can get in browser
  const hasRVFC =
    typeof (HTMLVideoElement.prototype as any).requestVideoFrameCallback ===
    "function";

  function stepFrame(dir: 1 | -1) {
    const v = videoRef.current;
    if (!v) return;

    v.pause();

    const getLoopBounded = (t: number) => clampSecToLoopIfEnabled(t);

    if (hasRVFC) {
      const eps = 0.0005;

      v.currentTime = getLoopBounded(v.currentTime + dir * eps);
      setPlayheadSecUi(v.currentTime);

      (v as any).requestVideoFrameCallback(() => {
        v.currentTime = getLoopBounded(v.currentTime + dir * FRAME_SEC);
        setPlayheadSecUi(v.currentTime);
        setScrubPlayheadSecUi(v.currentTime);
      });
    } else {
      v.currentTime = getLoopBounded(v.currentTime + dir * FRAME_SEC);
      setPlayheadSecUi(v.currentTime);
      setScrubPlayheadSecUi(v.currentTime);
    }
  }

  const durationSec = React.useMemo(() => {
    if (!row) return null;
    const ms = row.segmentEndMs - row.segmentStartMs;
    return ms > 0 ? ms / 1000 : null;
  }, [row]);

  const minLoopSec = 0.25;

  const clampSecToLoopIfEnabled = React.useCallback(
    (sec: number) => {
      const d = durationSec ?? 0;
      const bounded = clamp(sec, 0, Math.max(0, d));

      const L = loopRef.current;
      if (!L.enabled) return bounded;

      const a = Math.max(0, Math.min(L.aSec, L.bSec));
      // const b = Math.max(a + minLoopSec, Math.max(L.aSec, L.bSec));
      const b = clamp(
        Math.max(L.aSec, L.bSec),
        a + minLoopSec,
        Math.max(a + minLoopSec, d),
      );

      return clamp(bounded, a, b);
    },
    [durationSec, minLoopSec],
  );

  function stepFrame_old(dir: 1 | -1) {
    const v = videoRef.current;
    if (!v) return;

    v.pause(); // ensure we land on a decoded frame

    if (hasRVFC) {
      // Seek a tiny amount, then let the browser present the next decoded frame.
      // Using small epsilon avoids “stuck on same frame” on some codecs.
      const eps = 0.0005;

      v.currentTime = Math.max(0, Math.min(dSec, v.currentTime + dir * eps));
      setPlayheadSecUi(v.currentTime);

      (v as any).requestVideoFrameCallback(() => {
        // Then move by one nominal frame step.
        v.currentTime = Math.max(
          0,
          Math.min(dSec, v.currentTime + dir * FRAME_SEC),
        );

        setPlayheadSecUi(v.currentTime);
      });
    } else {
      v.currentTime = Math.max(
        0,
        Math.min(dSec, v.currentTime + dir * FRAME_SEC),
      );
      setPlayheadSecUi(v.currentTime);
    }
  }

  const [playheadSecUi, setPlayheadSecUi] = React.useState(0);
  // const rafPlayheadRef = React.useRef<number | null>(null);

  const BAR_HEIGHT_CONDENSED_TIME = 40;

  const [aiQuestionText, setAiQuestionText] = React.useState("");
  const [aiTurns, setAiTurns] = React.useState<AiTurn[]>([]);
  const [aiBusy, setAiBusy] = React.useState(false);
  const [socialExportOpen, setSocialExportOpen] = React.useState(false);
  const [socialExportCaption, setSocialExportCaption] = React.useState("");
  const [socialExportIncludeShotLabels, setSocialExportIncludeShotLabels] =
    React.useState(true);
  const [socialExportBusy, setSocialExportBusy] = React.useState(false);
  const [socialExportError, setSocialExportError] = React.useState<
    string | null
  >(null);
  const [activeRallyLens, setActiveRallyLens] =
    React.useState<ActiveRallyLensKey>(initialRallyLens);
  const [selectedRallyLensMomentKey, setSelectedRallyLensMomentKey] =
    React.useState<string | null>(null);
  const [nextBallRead, setNextBallRead] =
    React.useState<ActiveNextBallRead | null>(null);
  const nextBallReadRef = React.useRef<ActiveNextBallRead | null>(null);
  const pendingRandomKitchenReadRef = React.useRef<{
    rallyIndex: number;
    shotNumber: number;
  } | null>(null);
  const appliedNextBallReadSeedNonceRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    nextBallReadRef.current = nextBallRead;
    onNextBallReadChange?.(nextBallRead);
  }, [nextBallRead, onNextBallReadChange]);

  React.useEffect(() => {
    if (initialRallyLens === "off") return;
    setActiveRallyLens(initialRallyLens);
  }, [initialRallyLens, row?.rallyIndex, row?.videoUrl]);

  React.useEffect(() => {
    if (!initialRallyLensMomentKey) return;
    setSelectedRallyLensMomentKey(initialRallyLensMomentKey);
  }, [initialRallyLensMomentKey, row?.rallyIndex, row?.videoUrl]);

  const playheadAbsMs = React.useMemo(() => {
    if (!row) return null;
    return row.segmentStartMs + Math.round(playheadSecUi * 1000);
  }, [row, playheadSecUi]);

  const rallyLensCounts = React.useMemo(() => {
    const counts: Record<Exclude<ActiveRallyLensKey, "off">, number> = {
      candidateSpeedups: 0,
      candidateBodybags: 0,
      possiblePopups: 0,
      paddleReadiness: 0,
      leftSideCoverage: 0,
    };
    for (const moment of rallyLensMoments) {
      counts[moment.lens] += 1;
    }
    return counts;
  }, [rallyLensMoments]);

  const activeRallyLensMoments = React.useMemo(() => {
    if (activeRallyLens === "off") return [];
    return rallyLensMoments
      .filter((moment) => moment.lens === activeRallyLens)
      .slice()
      .sort((a, b) => a.timeSec - b.timeSec);
  }, [activeRallyLens, rallyLensMoments]);

  React.useEffect(() => {
    setSelectedRallyLensMomentKey(null);
  }, [activeRallyLens, row?.rallyIndex, row?.videoUrl]);

  React.useEffect(() => {
    if (initialRallyLensMomentKey) {
      setSelectedRallyLensMomentKey(initialRallyLensMomentKey);
    }
  }, [activeRallyLens, initialRallyLensMomentKey, row?.rallyIndex, row?.videoUrl]);

  React.useEffect(() => {
    if (activeRallyLens === "off") return;
    if (rallyLensCounts[activeRallyLens] > 0) return;
    setActiveRallyLens("off");
  }, [activeRallyLens, rallyLensCounts]);

  const currentRallyLensMoment = React.useMemo(() => {
    if (!activeRallyLensMoments.length) return null;
    let best: ActiveRallyLensMoment | null = null;
    let bestDistance = Infinity;
    for (const moment of activeRallyLensMoments) {
      const distance = Math.abs(moment.timeSec - playheadSecUi);
      if (distance < bestDistance) {
        best = moment;
        bestDistance = distance;
      }
    }
    return bestDistance <= 1.25 ? best : null;
  }, [activeRallyLensMoments, playheadSecUi]);

  const nextRallyLensMoment = React.useMemo(() => {
    if (!activeRallyLensMoments.length) return null;
    return (
      activeRallyLensMoments.find(
        (moment) => moment.timeSec >= playheadSecUi - 0.3,
      ) ?? activeRallyLensMoments[0]
    );
  }, [activeRallyLensMoments, playheadSecUi]);

  React.useEffect(() => {
    if (!selectedRallyLensMomentKey) return;
    if (activeRallyLens === "paddleReadiness") return;
    const selected = activeRallyLensMoments.find(
      (moment) => moment.key === selectedRallyLensMomentKey,
    );
    if (!selected) {
      setSelectedRallyLensMomentKey(null);
      return;
    }
    if (Math.abs(selected.timeSec - playheadSecUi) > 2.5) {
      setSelectedRallyLensMomentKey(null);
    }
  }, [activeRallyLens, activeRallyLensMoments, playheadSecUi, selectedRallyLensMomentKey]);

  const seekToRallyLensMoment = React.useCallback(
    (moment: ActiveRallyLensMoment) => {
      setSelectedRallyLensMomentKey(moment.key);
      const v = videoRef.current;
      if (!v) return;
      const target = Math.max(0, moment.timeSec - 0.6);
      const bounded =
        typeof durationSec === "number" ? Math.min(durationSec, target) : target;
      v.currentTime = bounded;
      setPlayheadSecUi(bounded);
      setScrubPlayheadSecUi(bounded);
    },
    [durationSec],
  );

  // const pounceOverlayVisible = React.useMemo(() => {
  //   if (!pounceOverlay?.enabled) return false;
  //   if (playheadAbsMs == null) return false;

  //   const startMs = pounceOverlay.overlayStartMs;
  //   const endMs = pounceOverlay.overlayEndMs;

  //   if (
  //     typeof startMs !== "number" ||
  //     !Number.isFinite(startMs) ||
  //     typeof endMs !== "number" ||
  //     !Number.isFinite(endMs)
  //   ) {
  //     return false;
  //   }

  //   return playheadAbsMs >= startMs && playheadAbsMs <= endMs;
  // }, [pounceOverlay, playheadAbsMs]);

  const pounceOverlayVisible = true; // !!pounceOverlay?.enabled;

  const opponentContactScreen = React.useMemo(() => {
    return projectWorldPointToOverlay(
      worldToScreen,
      pounceOverlay?.opponentContact,
    );
  }, [worldToScreen, pounceOverlay]);

  const firstAttackableScreen = React.useMemo(() => {
    return projectWorldPointToOverlay(
      worldToScreen,
      pounceOverlay?.firstAttackablePoint,
    );
  }, [worldToScreen, pounceOverlay]);

  const bestAttackScreen = React.useMemo(() => {
    return projectWorldPointToOverlay(
      worldToScreen,
      pounceOverlay?.bestAttackPoint,
    );
  }, [worldToScreen, pounceOverlay]);

  const peakScreen = React.useMemo(() => {
    return projectWorldPointToOverlay(
      worldToScreen,
      pounceOverlay?.peakPoint ?? null,
    );
  }, [worldToScreen, pounceOverlay]);

  const playerAtOpponentImpactScreen = React.useMemo(() => {
    return projectWorldPointToOverlay(
      worldToScreen,
      pounceOverlay?.playerAtOpponentImpact ?? null,
    );
  }, [worldToScreen, pounceOverlay]);

  const requiredMovementStartScreen = React.useMemo(() => {
    return projectWorldPointToOverlay(
      worldToScreen,
      pounceOverlay?.requiredMovement?.startPoint ?? null,
    );
  }, [worldToScreen, pounceOverlay]);

  const requiredMovementEndScreen = React.useMemo(() => {
    return projectWorldPointToOverlay(
      worldToScreen,
      pounceOverlay?.requiredMovement?.endPoint ?? null,
    );
  }, [worldToScreen, pounceOverlay]);

  const pounceShotStartScreen = React.useMemo(() => {
    return projectWorldPointToOverlay(
      worldToScreen,
      pounceOverlay?.pounceShotTrajectory?.start ?? null,
    );
  }, [worldToScreen, pounceOverlay?.pounceShotTrajectory?.start]);

  const pounceShotPeakScreen = React.useMemo(() => {
    return projectWorldPointToOverlay(
      worldToScreen,
      pounceOverlay?.pounceShotTrajectory?.peak ?? null,
    );
  }, [worldToScreen, pounceOverlay?.pounceShotTrajectory?.peak]);

  const pounceShotEndScreen = React.useMemo(() => {
    return projectWorldPointToOverlay(
      worldToScreen,
      pounceOverlay?.pounceShotTrajectory?.end ?? null,
    );
  }, [worldToScreen, pounceOverlay?.pounceShotTrajectory?.end]);

  const requiredMovementFirstAttackableScreen = React.useMemo(() => {
    return projectWorldPointToOverlay(
      worldToScreen,
      pounceOverlay?.requiredMovement?.firstAttackablePoint ?? null,
    );
  }, [worldToScreen, pounceOverlay]);

  const requiredMovementBestAttackScreen = React.useMemo(() => {
    return projectWorldPointToOverlay(
      worldToScreen,
      pounceOverlay?.requiredMovement?.bestAttackPoint ?? null,
    );
  }, [worldToScreen, pounceOverlay]);

  const requiredMovementLanePolygonPoints = React.useMemo(() => {
    if (
      !requiredMovementStartScreen ||
      !requiredMovementEndScreen ||
      !pounceOverlay?.requiredMovement?.enabled
    ) {
      return null;
    }

    const poly = buildRequiredMovementLanePolygon({
      start: requiredMovementStartScreen,
      end: requiredMovementEndScreen,
      startWidth: isInFullPopup ? 0.008 : 0.007,
      endWidth: isInFullPopup ? 0.022 : 0.019,
    });

    return lanePolygonToSvgPoints(poly);
  }, [
    requiredMovementStartScreen,
    requiredMovementEndScreen,
    pounceOverlay,
    isInFullPopup,
  ]);

  const requiredMovementLabelUv = React.useMemo(() => {
    return midpointUv(requiredMovementStartScreen, requiredMovementEndScreen);
  }, [requiredMovementStartScreen, requiredMovementEndScreen]);

  const requiredMovementDistanceLabel = React.useMemo(() => {
    return fmtPounceOverlayFeet(
      pounceOverlay?.requiredMovement?.distanceFeet ??
        pounceOverlay?.movementDistanceFeet ??
        null,
    );
  }, [pounceOverlay]);

  // const pounceTrajectoryPolyline = React.useMemo(() => {
  //   return buildPounceOverlayPolylinePoints({
  //     opponentContactScreen,
  //     firstAttackableScreen,
  //     bestAttackScreen,
  //     peakScreen,
  //   });
  // }, [
  //   opponentContactScreen,
  //   firstAttackableScreen,
  //   bestAttackScreen,
  //   peakScreen,
  // ]);

  // const pounceShotTubePolyline = React.useMemo(() => {
  //   if (pounceOverlay?.pounceResult !== "taken") return null;

  //   const traj = pounceOverlay?.pounceShotTrajectory;
  //   if (!traj?.start || !traj?.end) return null;

  //   if (
  //     typeof traj.start.x !== "number" ||
  //     typeof traj.start.y !== "number" ||
  //     typeof traj.start.z !== "number" ||
  //     typeof traj.end.x !== "number" ||
  //     typeof traj.end.y !== "number" ||
  //     typeof traj.end.z !== "number"
  //   ) {
  //     return null;
  //   }

  //   const peak =
  //     traj.peak &&
  //     typeof traj.peak.x === "number" &&
  //     typeof traj.peak.y === "number" &&
  //     typeof traj.peak.z === "number"
  //       ? {
  //           x: traj.peak.x,
  //           y: traj.peak.y,
  //           z: traj.peak.z,
  //           ms: typeof traj.peak.ms === "number" ? traj.peak.ms : undefined,
  //         }
  //       : null;

  //   return buildTrajectoryPolylineApprox3D(
  //     {
  //       start: {
  //         location: {
  //           x: traj.start.x,
  //           y: traj.start.y,
  //           z: traj.start.z,
  //         },
  //         //ms: typeof traj.start.ms === "number" ? traj.start.ms : undefined,
  //       },
  //       peak,
  //       end: {
  //         location: {
  //           x: traj.end.x,
  //           y: traj.end.y,
  //           z: traj.end.z,
  //         },
  //         //ms: typeof traj.end.ms === "number" ? traj.end.ms : undefined,
  //       },
  //     },
  //     18,
  //   );
  // }, [pounceOverlay?.pounceResult, pounceOverlay?.pounceShotTrajectory, buildTrajectoryPolylineApprox3D]);

  const pounceTriggerOverlay = React.useMemo(() => {
    if (!pounceOverlay?.triggerShotNumber) return null;
    if (!row) return null;

    return findOverlayByShotNumber(
      row.shotNumberOverlays as ShotNumberOverlay[] | undefined,
      pounceOverlay.triggerShotNumber,
    );
  }, [pounceOverlay?.triggerShotNumber, row?.shotNumberOverlays]);

  const pounceTriggerRawShot = React.useMemo(() => {
    return pounceTriggerOverlay?.rawShot ?? null;
  }, [pounceTriggerOverlay]);

  const pounceTriggerShotStartMs = React.useMemo(() => {
    return typeof pounceTriggerRawShot?.start_ms === "number"
      ? pounceTriggerRawShot.start_ms
      : null;
  }, [pounceTriggerRawShot]);

  const pounceTriggerShotEndMs = React.useMemo(() => {
    return typeof pounceTriggerRawShot?.end_ms === "number"
      ? pounceTriggerRawShot.end_ms
      : null;
  }, [pounceTriggerRawShot]);

  const pounceRallyStartMs = React.useMemo(() => {
    return typeof row?.segmentStartMs === "number" ? row.segmentStartMs : 0;
  }, [row]);

  const pounceRallyEndMs = React.useMemo(() => {
    return typeof row?.segmentEndMs === "number"
      ? (row as any).segmentEndMs
      : null;
  }, [row]);

  const pounceRallyIndexZeroBased = React.useMemo(() => {
    if (typeof (row as any)?.rallyIndex === "number") {
      return (row as any).rallyIndex;
    }

    if (typeof (row as any)?.rallyNumber === "number") {
      return Math.max(0, (row as any).rallyNumber - 1);
    }

    return null;
  }, [row]);

  const pounceTriggerBallUvSamples = React.useMemo(() => {
    if (
      pounceRallyIndexZeroBased == null ||
      pounceTriggerShotStartMs == null ||
      pounceTriggerShotEndMs == null ||
      pounceRallyEndMs == null
    ) {
      return [];
    }

    const rallyPts = extractBallUvSamplesForRallyShotWindow({
      cvJson,
      rallyIndex: pounceRallyIndexZeroBased,
      shotStartMs: pounceTriggerShotStartMs,
      shotEndMs: pounceTriggerShotEndMs,
      rallyStartMs: pounceRallyStartMs,
      rallyEndMs: pounceRallyEndMs,
    });

    const totalFrames = rallyPts.length;
    if (totalFrames < 2) return [];

    const rallyDuration = pounceRallyEndMs - pounceRallyStartMs;
    if (rallyDuration <= 0) return rallyPts;

    return rallyPts.filter((pt, idx) => {
      const t = idx / (totalFrames - 1);

      const ms =
        //rallyStartMs +
        pounceRallyStartMs + t * rallyDuration;

      return ms >= pounceRallyStartMs && ms <= pounceRallyEndMs;
    });
  }, [
    row,
    cvJson,
    pounceRallyIndexZeroBased,
    pounceTriggerShotStartMs,
    pounceTriggerShotEndMs,
    pounceRallyStartMs,
    pounceRallyEndMs,
  ]);

  const pounceTriggerBallUvPolyline = React.useMemo(() => {
    if (!pounceTriggerBallUvSamples.length) return null;

    const MAX_JUMP = 0.12; //0.04; //0.12;
    const kept: typeof pounceTriggerBallUvSamples = [];

    for (const p of pounceTriggerBallUvSamples) {
      if (
        !Number.isFinite(p.u) ||
        !Number.isFinite(p.v) ||
        p.u < -0.05 ||
        p.u > 1.05 ||
        p.v < -0.05 ||
        p.v > 1.05
      ) {
        continue;
      }

      const prev = kept[kept.length - 1];
      if (prev) {
        const du = p.u - prev.u;
        const dv = p.v - prev.v;
        const dist = Math.sqrt(du * du + dv * dv);
        if (dist > MAX_JUMP) continue;
      }

      kept.push(p);
    }

    if (kept.length < 2) return null;

    const smoothed = lightlySmoothUvPoints(kept);

    return smoothed.map((p) => `${p.u},${p.v}`).join(" ");
  }, [pounceTriggerBallUvSamples]);

  const pounceShotStartMs = React.useMemo(() => {
    return typeof pounceOverlay?.pounceShotStartMs === "number"
      ? pounceOverlay.pounceShotStartMs
      : null;
  }, [pounceOverlay?.pounceShotStartMs]);

  const pounceShotEndMs = React.useMemo(() => {
    return typeof pounceOverlay?.pounceShotEndMs === "number"
      ? pounceOverlay.pounceShotEndMs
      : null;
  }, [pounceOverlay?.pounceShotEndMs]);

  const pounceShotBallUvSamples = React.useMemo(() => {
    if (
      pounceOverlay?.pounceResult !== "taken" ||
      pounceRallyIndexZeroBased == null ||
      pounceShotStartMs == null ||
      pounceShotEndMs == null ||
      pounceRallyEndMs == null
    ) {
      return [];
    }

    const rallyPts = extractBallUvSamplesForRallyShotWindow({
      cvJson,
      rallyIndex: pounceRallyIndexZeroBased,
      shotStartMs: pounceShotStartMs,
      shotEndMs: pounceShotEndMs,
      rallyStartMs: pounceRallyStartMs,
      rallyEndMs: pounceRallyEndMs,
    });

    if (rallyPts.length < 2) return [];

    return rallyPts;
  }, [
    pounceOverlay?.pounceResult,
    cvJson,
    pounceRallyIndexZeroBased,
    pounceShotStartMs,
    pounceShotEndMs,
    pounceRallyStartMs,
    pounceRallyEndMs,
  ]);

  const pounceShotBallUvSamples_old = React.useMemo(() => {
    if (
      pounceOverlay?.pounceResult !== "taken" ||
      pounceRallyIndexZeroBased == null ||
      pounceShotStartMs == null ||
      pounceShotEndMs == null ||
      pounceRallyEndMs == null
    ) {
      return [];
    }

    const rallyPts = extractBallUvSamplesForRallyShotWindow({
      cvJson,
      rallyIndex: pounceRallyIndexZeroBased,
      shotStartMs: pounceShotStartMs,
      shotEndMs: pounceShotEndMs,
      rallyStartMs: pounceRallyStartMs,
      rallyEndMs: pounceRallyEndMs,
    });

    const totalFrames = rallyPts.length;
    if (totalFrames < 2) return [];

    const rallyDuration = pounceRallyEndMs - pounceRallyStartMs;
    if (rallyDuration <= 0) return rallyPts;

    return rallyPts.filter((pt, idx) => {
      const t = idx / (totalFrames - 1);
      const ms = pounceRallyStartMs + t * rallyDuration;
      return ms >= pounceShotStartMs && ms <= pounceShotEndMs;
    });
  }, [
    pounceOverlay?.pounceResult,
    cvJson,
    pounceRallyIndexZeroBased,
    pounceShotStartMs,
    pounceShotEndMs,
    pounceRallyStartMs,
    pounceRallyEndMs,
  ]);

  const pounceShotTubePolyline = React.useMemo(() => {
    if (!pounceShotBallUvSamples.length) return null;

    const MAX_JUMP = 0.12;
    const kept: typeof pounceShotBallUvSamples = [];

    for (const p of pounceShotBallUvSamples) {
      if (
        !Number.isFinite(p.u) ||
        !Number.isFinite(p.v) ||
        p.u < -0.05 ||
        p.u > 1.05 ||
        p.v < -0.05 ||
        p.v > 1.05
      ) {
        continue;
      }

      const prev = kept[kept.length - 1];
      if (prev) {
        const du = p.u - prev.u;
        const dv = p.v - prev.v;
        const dist = Math.sqrt(du * du + dv * dv);
        if (dist > MAX_JUMP) continue;
      }

      kept.push(p);
    }

    if (kept.length < 2) return null;
    return kept.map((p) => `${p.u},${p.v}`).join(" ");
  }, [pounceShotBallUvSamples]);

  const aiPanelWidth = React.useMemo(() => {
    // Make AI roomy enough that the question area is usable even with prior turns.
    // This intentionally makes the video smaller when AI is open in split mode.
    if (aiTurns.length > 0) {
      return isInFullPopup ? "min(820px, 62vw)" : "min(820px, 62vw)";
    }
    return isInFullPopup ? "min(520px, 46vw)" : "min(420px, 40vw)";
  }, [aiTurns.length, isInFullPopup]);

  const turnsKey = React.useMemo(() => {
    if (!packageId) return null;
    if (!row) return null;
    return turnsStorageKey(packageId, row.rallyIndex);
  }, [packageId, row?.rallyIndex]);

  React.useEffect(() => {
    const v = videoRef.current;
    if (!v || !row) return;

    const sync = () => {
      if (!Number.isFinite(v.currentTime)) return;
      setPlayheadSecUi(v.currentTime);
    };

    const onTimeUpdate = () => sync();
    const onSeeking = () => sync();
    const onSeeked = () => sync();
    const onLoadedMetadata = () => sync();
    const onLoadedData = () => sync();
    const onPause = () => {
      sync();
      if (Number.isFinite(v.currentTime)) {
        onPlaybackPause?.(v.currentTime);
      }
    };
    const onPlay = () => {
      sync();
      const playbackCoordinatorId = playbackCoordinatorIdRef.current;
      if (playbackCoordinatorId != null) {
        pauseOtherActiveVideos(playbackCoordinatorId);
      }
      if (Number.isFinite(v.currentTime)) {
        onPlaybackPlay?.(v.currentTime);
      }
    };
    const onEnded = () => sync();

    v.addEventListener("timeupdate", onTimeUpdate);
    v.addEventListener("seeking", onSeeking);
    v.addEventListener("seeked", onSeeked);
    v.addEventListener("loadedmetadata", onLoadedMetadata);
    v.addEventListener("loadeddata", onLoadedData);
    v.addEventListener("pause", onPause);
    v.addEventListener("play", onPlay);
    v.addEventListener("ended", onEnded);

    sync();

    return () => {
      v.removeEventListener("timeupdate", onTimeUpdate);
      v.removeEventListener("seeking", onSeeking);
      v.removeEventListener("seeked", onSeeked);
      v.removeEventListener("loadedmetadata", onLoadedMetadata);
      v.removeEventListener("loadeddata", onLoadedData);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("ended", onEnded);
    };
  }, [row?.rallyIndex, row?.videoUrl, onPlaybackPause, onPlaybackPlay]);

  React.useEffect(() => {
    if (!turnsKey) {
      setAiTurns([]);
      return;
    }
    try {
      const raw = localStorage.getItem(turnsKey);
      if (!raw) {
        setAiTurns([]);
        return;
      }
      const parsed = JSON.parse(raw) as AiTurn[];
      setAiTurns(Array.isArray(parsed) ? parsed : []);
    } catch {
      setAiTurns([]);
    }
  }, [turnsKey]);

  React.useEffect(() => {
  const v = videoRef.current;
  if (!v) return;

  setRate(defaultPlaybackRate);
  //v.playbackRate = defaultPlaybackRate;
}, [row?.rallyIndex, props.seedPlayback?.nonce, defaultPlaybackRate]);

  // const persistTurns = React.useCallback(
  //   (next: AiTurn[]) => {
  //     if (!turnsKey) return;
  //     try {
  //       localStorage.setItem(turnsKey, JSON.stringify(next));
  //     } catch {
  //       // ignore
  //     }
  //   },
  //   [turnsKey],
  // );

  const persistTurns = React.useCallback(
  (next: AiTurn[]) => {
    if (!turnsKey || !row) return;

    try {
      localStorage.setItem(turnsKey, JSON.stringify(next));

      window.dispatchEvent(
        new CustomEvent("poc2-ai-turns-updated", {
          detail: {
            packageId,
            rallyIndex: row.rallyIndex,
            scope: "per-rally",
          },
        }),
      );
    } catch {
      // ignore
    }
  },
  [turnsKey, packageId, row],
);

  const setTurns = React.useCallback(
    (updater: (cur: AiTurn[]) => AiTurn[]) => {
      setAiTurns((cur) => {
        const next = updater(cur);
        persistTurns(next);
        return next;
      });
    },
    [persistTurns],
  );

  const playSeqRef = React.useRef(0);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const playbackCoordinatorIdRef = React.useRef<number | null>(null);
  if (playbackCoordinatorIdRef.current == null) {
    playbackCoordinatorIdRef.current = createVideoPlaybackId();
  }

  React.useEffect(() => {
    onVideoRef?.(videoRef);
    return () => {
      onVideoRef?.({ current: null });
    };
  }, [onVideoRef]);

  const lastPlaybackCtxRef = React.useRef<{
    currentTimeSec: number;
    currentShotNumber: number | null;
  } | null>(null);
  const [overlayLayerShotNumber, setOverlayLayerShotNumber] =
    React.useState<number | null>(null);

  React.useEffect(() => {
    lastPlaybackCtxRef.current = null;
    setOverlayLayerShotNumber(null);
  }, [row?.rallyIndex, row?.videoUrl]);

  const handleActiveShotNumberChange = React.useCallback(
    (shotNumber: number | null) => {
      setOverlayLayerShotNumber(shotNumber);
      onPlaybackShotNumberChange?.(shotNumber);
    },
    [onPlaybackShotNumberChange],
  );

  React.useEffect(() => {
    const v = videoRef.current;
    if (!v || !onShotUniverseContextShotNumberChange) return;

    let raf = 0;

    const emit = () => {
      const currentTimeSec = Number.isFinite(v.currentTime) ? v.currentTime : 0;
      const previousCtx = lastPlaybackCtxRef.current;

      if (previousCtx && currentTimeSec < previousCtx.currentTimeSec - 0.25) {
        lastPlaybackCtxRef.current = {
          currentTimeSec,
          currentShotNumber: null,
        };
        onShotUniverseContextShotNumberChange(null);
        return;
      }

      const currentShotNumber = getCurrentShotNumberFromPlayback({
        currentTimeSec,
        overlays: row?.shotNumberOverlays ?? [],
        segmentStartMs: row?.segmentStartMs ?? null,
        segmentEndMs: row?.segmentEndMs ?? null,
        firstShotLeadMs: PLAYBACK_FIRST_SHOT_CONTEXT_LEAD_MS,
      });

      if (currentShotNumber == null) {
        lastPlaybackCtxRef.current = { currentTimeSec, currentShotNumber };
        return;
      }

      if (lastPlaybackCtxRef.current?.currentShotNumber === currentShotNumber) {
        lastPlaybackCtxRef.current = { currentTimeSec, currentShotNumber };
        return;
      }

      lastPlaybackCtxRef.current = { currentTimeSec, currentShotNumber };
      onShotUniverseContextShotNumberChange(currentShotNumber);
    };

    const onTimeUpdate = () => emit();
    const onSeeking = () => emit();
    const onSeeked = () => emit();
    const onLoadedMetadata = () => emit();
    const onLoadedData = () => emit();
    const tick = () => {
      emit();
      raf = requestAnimationFrame(tick);
    };

    v.addEventListener("timeupdate", onTimeUpdate);
    v.addEventListener("seeking", onSeeking);
    v.addEventListener("seeked", onSeeked);
    v.addEventListener("loadedmetadata", onLoadedMetadata);
    v.addEventListener("loadeddata", onLoadedData);

    emit();
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      v.removeEventListener("timeupdate", onTimeUpdate);
      v.removeEventListener("seeking", onSeeking);
      v.removeEventListener("seeked", onSeeked);
      v.removeEventListener("loadedmetadata", onLoadedMetadata);
      v.removeEventListener("loadeddata", onLoadedData);
    };
  }, [row, onShotUniverseContextShotNumberChange]);

  React.useEffect(() => {
    props.onVideoRef?.(videoRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const seedAppliedRef = React.useRef<number | null>(null);
  const seedLoopAppliedRef = React.useRef<number | null>(null);

  const pressureByRallyIndex = React.useMemo(() => {
    return buildPressureByRallyIndexFromTimelineRows(rows ?? []);
  }, [rows]);

  // const insightsToCvPlayerIndex = React.useMemo(() => {
  //   return deriveInsightsToCvPlayerIndexMap({
  //     rows,
  //     cvJson,
  //   });
  // }, [rows, cvJson]);

  const pressureForActiveRally = row
    ? pressureByRallyIndex[row.rallyIndex]
    : null;

  const [rate, setRate] = React.useState<VideoPlaybackRate>(defaultPlaybackRate); // 1);

  // When normal rally navigation changes the video, reset the UI rate state to
  // the requested default. A random Next Ball Read may jump rallies after the
  // user has deliberately chosen a read speed, so preserve that rate across the
  // pending read jump.
React.useEffect(() => {
  const pendingRandomRead = pendingRandomKitchenReadRef.current;
  if (pendingRandomRead && pendingRandomRead.rallyIndex === row?.rallyIndex) {
    return;
  }
  setRate(defaultPlaybackRate);
}, [row?.rallyIndex, row?.videoUrl, defaultPlaybackRate]);

// Keep the actual <video> element in sync with the UI rate state.
React.useEffect(() => {
  const v = videoRef.current;
  if (!v) return;

  v.playbackRate = rate;
  v.defaultPlaybackRate = rate;
}, [rate, row?.rallyIndex, row?.videoUrl]);

React.useEffect(() => {
  const v = videoRef.current;
  if (!v) return;

  const applyRate = () => {
    v.playbackRate = rate;
    v.defaultPlaybackRate = rate;
  };

  v.addEventListener("loadedmetadata", applyRate);
  v.addEventListener("loadeddata", applyRate);
  applyRate();

  return () => {
    v.removeEventListener("loadedmetadata", applyRate);
    v.removeEventListener("loadeddata", applyRate);
  };
}, [rate, row?.rallyIndex, row?.videoUrl]);

React.useEffect(() => {
  const v = videoRef.current;
  if (!v) return;

  const supportedRates: VideoPlaybackRate[] = [0.1, 0.25, 0.5, 1];
  const syncRateFromVideo = () => {
    const actual = v.playbackRate;
    const next = supportedRates.find((candidate) => {
      return Math.abs(candidate - actual) < 0.001;
    });
    if (next == null) return;
    setRate((current) => {
      if (current === next) return current;
      if (current === defaultPlaybackRate) {
        window.setTimeout(() => {
          v.playbackRate = current;
          v.defaultPlaybackRate = current;
        }, 0);
        return current;
      }
      return next;
    });
  };

  v.addEventListener("ratechange", syncRateFromVideo);
  syncRateFromVideo();
  return () => {
    v.removeEventListener("ratechange", syncRateFromVideo);
  };
}, [defaultPlaybackRate, row?.rallyIndex, row?.videoUrl]);

  // IMPORTANT:
  // paused state is our source of truth (drives UI + video),
  // but we also listen to video events to stay in sync.

  // Replace: const [paused, setPaused] = React.useState(false);
  const [paused, _setPaused] = React.useState(false);

  const setPausedInternal = React.useCallback((p: boolean) => {
    _setPaused(p);
  }, []);

  const setPausedUser = React.useCallback((p: boolean) => {
    userPausedRef.current = p; // user explicitly paused/resumed
    _setPaused(p);
  }, []);

  React.useEffect(() => {
    const playbackCoordinatorId = playbackCoordinatorIdRef.current;
    if (playbackCoordinatorId == null) return;
    return registerActiveVideoPlayback(playbackCoordinatorId, () => {
      const v = videoRef.current;
      if (!v || v.paused) return;
      userPausedRef.current = true;
      _setPaused(true);
      try {
        v.pause();
      } catch {}
    });
  }, []);

  React.useEffect(() => {
    const playbackCoordinatorId = playbackCoordinatorIdRef.current;
    if (playbackCoordinatorId == null) return;
    if (!pauseOtherVideosOnMount) return;
    pauseOtherActiveVideos(playbackCoordinatorId);
  }, [pauseOtherVideosOnMount, row?.rallyIndex, row?.videoUrl]);

  const stepShotByOverlay = React.useCallback(
    (dir: -1 | 1) => {
      const v = videoRef.current;
      const overlays = [...(row?.shotNumberOverlays ?? [])]
        .filter((overlay) =>
          Number.isFinite(
            getOverlayMsRel(overlay, row?.segmentStartMs, row?.segmentEndMs),
          ),
        )
        .sort(
          (a, b) =>
            getOverlayMsRel(a, row?.segmentStartMs, row?.segmentEndMs) -
            getOverlayMsRel(b, row?.segmentStartMs, row?.segmentEndMs),
        );
      if (!v || !overlays.length) return;

      const currentMs = Number.isFinite(v.currentTime) ? v.currentTime * 1000 : 0;
      const nextOverlay =
        dir < 0
          ? [...overlays]
              .reverse()
              .find(
                (overlay) =>
                  getOverlayMsRel(overlay, row?.segmentStartMs, row?.segmentEndMs) <
                  currentMs - 40,
              ) ?? overlays[overlays.length - 1]
          : overlays.find(
              (overlay) =>
                getOverlayMsRel(overlay, row?.segmentStartMs, row?.segmentEndMs) >
                currentMs + 40,
            ) ?? overlays[0];
      if (!nextOverlay) return;

      const nextMs = getOverlayMsRel(
        nextOverlay,
        row?.segmentStartMs,
        row?.segmentEndMs,
      );
      if (!Number.isFinite(nextMs)) return;

      const nextSec = Math.max(0, nextMs / 1000);
      try {
        suppressPauseForMs(400);
        v.pause();
        v.currentTime = nextSec;
      } catch {
        return;
      }
      setPlayheadSecUi(nextSec);
      setScrubPlayheadSecUi(nextSec);
      setPausedUser(true);
    },
    [row?.segmentEndMs, row?.segmentStartMs, row?.shotNumberOverlays, setPausedUser],
  );

  const stepShotHandler = disableShotStepControls
    ? undefined
    : onStepShot ??
      ((row?.shotNumberOverlays?.length ?? 0) > 0 ? stepShotByOverlay : undefined);

  const pausedRef = React.useRef(paused);
  React.useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  // Helps prevent play/pause “flapping” when autoplay/play promises race.
  const userPausedRef = React.useRef(false);
  // Ignore transient pause events caused by our own seeks (loop jump / scrubbing)
  const suppressPauseUntilRef = React.useRef(0);
  const suppressPauseForMs = (ms: number) => {
    suppressPauseUntilRef.current = performance.now() + ms;
  };

  // const [popoutOpen, setPopoutOpen] = React.useState(false);
  // const popoutSeedRef = React.useRef<{ t: number; paused: boolean } | null>(
  //   null,
  // );

  const [popoutOpen, setPopoutOpen] = React.useState(false);
  const videoOverlayControls = showRateControls ? (
    <VideoOverlayControls
      videoRef={videoRef}
      rate={rate}
      setRate={setRate}
      paused={paused}
      setPaused={setPausedUser}
      onStepFrame={stepFrame}
      onStepShot={stepShotHandler}
      onStepRally={onStepRally}
      keyboardEnabled={isInFullPopup || !popoutOpen}
      showUltraSlowRate={props.showUltraSlowRate}
      placement={controlsPlacement}
    />
  ) : null;

  const popoutSeedRef = React.useRef<{
    t: number;
    paused: boolean;
    loop: LoopRegion;
    nonce: number;
    rallyLensMomentKey: string | null;
    nextBallRead: ActiveNextBallRead | null;
  } | null>(null);

  // ✅ When rally changes (e.g. user clicks in condensed timeline), start playing.
  React.useEffect(() => {
    if (!row) return;
    if (!autoPlayOnRallyChange) return;

    const v = videoRef.current;
    if (!v) return;

    // Treat row change as explicit user intent to play
    userPausedRef.current = false;
    setPausedInternal(false);

    // Ensure we don't "learn paused" from our own internal work
    suppressPauseForMs(900);

    // Force a clean start for this src swap
    try {
      v.muted = true;
      v.defaultMuted = true;
      v.volume = 0;
      v.currentTime = 0;
      // When src changes, load() can help on some browsers to commit the swap promptly
      v.load();
    } catch {}

    let cancelled = false;
    let tries = 0;

    const kick = () => {
      if (cancelled) return;
      if (userPausedRef.current) return; // only respect explicit user pause

      tries++;
      suppressPauseForMs(600);

      try {
        const p = v.play();
        if (p && typeof (p as any).catch === "function") {
          (p as any).catch(() => {
            // retry a couple times; browsers sometimes pause during decode/swap
            if (!cancelled && !userPausedRef.current && tries < 3) {
              setTimeout(kick, 120);
            }
          });
        }
      } catch {
        if (!cancelled && !userPausedRef.current && tries < 3) {
          setTimeout(kick, 120);
        }
      }
    };

    // Try immediately + on readiness signals
    kick();
    const onLoadedData = () => kick();
    const onCanPlay = () => kick();

    v.addEventListener("loadeddata", onLoadedData);
    v.addEventListener("canplay", onCanPlay);

    return () => {
      cancelled = true;
      v.removeEventListener("loadeddata", onLoadedData);
      v.removeEventListener("canplay", onCanPlay);
    };
  }, [row?.rallyIndex, row?.videoUrl, autoPlayOnRallyChange]);

  // const similarRallyFingerprints = React.useMemo(() => {
  //   return buildRallyFingerprintsFromRows(rows ?? []);
  // }, [rows]);

  // const similarResults = React.useMemo(() => {
  //   if (!row) return [];
  //   if (!similarRallyFingerprints?.length) return [];

  //   // console.error({similarRallyFingerprints});

  //   return findSimilarRallies({
  //     sourceRallyIndex: row.rallyIndex,
  //     fingerprints: similarRallyFingerprints,
  //     mode: similarMode,
  //     maxResults: 2,
  //     minScore: similarMode.type === "global" ? 24 : 30,
  //   });
  // }, [row, similarRallyFingerprints, similarMode]);

  // -----------------------------
  // Loop region + scrub UI state
  // -----------------------------

  const [loop, setLoop] = React.useState<LoopRegion>(() => ({
    enabled: false,
    aSec: 0,
    bSec: 2,
  }));

  const openPopout = React.useCallback(() => {
    const v = videoRef.current;
    popoutSeedRef.current = {
      t: v?.currentTime ?? 0,
      paused,
      loop,
      nonce: Date.now(),
      rallyLensMomentKey: selectedRallyLensMomentKey,
      nextBallRead: nextBallReadRef.current,
    };
    setPopoutOpen(true);
  }, [paused, loop, selectedRallyLensMomentKey]);

  const openPopoutAtNextBallRead = React.useCallback(
    (rep: NextBallReadRep) => {
      popoutSeedRef.current = {
        t: rep.readSec,
        paused: true,
        loop: {
          enabled: false,
          aSec: 0,
          bSec: Math.max(rep.revealEndSec, rep.readSec + 0.5),
        },
        nonce: Date.now(),
        rallyLensMomentKey: selectedRallyLensMomentKey,
        nextBallRead: { stage: "read", rep },
      };
      setPopoutOpen(true);
    },
    [selectedRallyLensMomentKey],
  );

  const closePopout = React.useCallback(
    (
      tFromPopout?: number,
      pausedFromPopout?: boolean,
      loopFromPopout?: LoopRegion,
      nextBallReadFromPopout?: ActiveNextBallRead | null,
    ) => {
      void nextBallReadFromPopout;
      const v = videoRef.current;

      if (typeof tFromPopout === "number" && v) {
        try {
          v.currentTime = tFromPopout;
        } catch {}
      }

      if (typeof pausedFromPopout === "boolean") {
        setPausedInternal(pausedFromPopout);
      }

      if (loopFromPopout) {
        setLoop(loopFromPopout);
        if (loopFromPopout.enabled) {
          setLoopUiOpen(true);
        }
      }

      // Next Ball Reads lives in the larger popout surface. The inline entry
      // point launches that surface, and closing it returns to normal review.
      nextBallReadRef.current = null;
      setNextBallRead(null);
      setPopoutOpen(false);
    },
    [setPausedInternal],
  );

  const loopRef = React.useRef(loop);
  React.useEffect(() => {
    loopRef.current = loop;
  }, [loop]);

  React.useEffect(() => {
    props.onLoopRegionChange?.(loop);
  }, [loop, props.onLoopRegionChange]);

  const [loopUiOpen, setLoopUiOpen] = React.useState(defaultLoopUiOpen);
  React.useEffect(() => {
    // reset when switching rallies so it doesn’t “mysteriously” open/close across contexts
    setLoopUiOpen(defaultLoopUiOpen);
  }, [row?.rallyIndex, row?.videoUrl, defaultLoopUiOpen]);

  React.useEffect(() => {
    if (!selectedRallyLensMomentKey) return;
    const selectedMoment = activeRallyLensMoments.find(
      (moment) => moment.key === selectedRallyLensMomentKey,
    );
    if (!selectedMoment) return;

    const d =
      durationSec ??
      Math.max(selectedMoment.loopEndSec ?? selectedMoment.timeSec + 2.5, 3);
    const a = clamp(
      selectedMoment.loopStartSec ?? selectedMoment.timeSec - 1.25,
      0,
      Math.max(0, d - minLoopSec),
    );
    const b = clamp(
      selectedMoment.loopEndSec ?? selectedMoment.timeSec + 2.25,
      a + minLoopSec,
      Math.max(a + minLoopSec, d),
    );
    setLoop({ enabled: true, aSec: a, bSec: b });
    setLoopUiOpen(true);
  }, [
    activeRallyLensMoments,
    durationSec,
    minLoopSec,
    selectedRallyLensMomentKey,
  ]);

  const [dragging, setDragging] = React.useState<null | "a" | "b" | "p">(null);
  const draggingRef = React.useRef<null | "a" | "b" | "p">(null);
  React.useEffect(() => {
    draggingRef.current = dragging;
  }, [dragging]);
  const wasPlayingBeforeDragRef = React.useRef<boolean>(false);

  // localStorage key (per package and rally)

  const loopKey = React.useMemo(() => {
    if (!row) return null;
    return `poc2.loopRegion.${packageId ?? "nopkg"}.rally.${row.rallyIndex}`;
  }, [packageId, row]);

  // Load saved loop when rally changes
  React.useEffect(() => {
    if (!row) return;
    if (!loopKey) return;

    // If this instance was explicitly seeded (popup), do not overwrite it
    // with the per-rally saved loop.
    if (seedLoopRegion) return;

    try {
      const raw = localStorage.getItem(loopKey);
      const d = durationSec ?? 0;

      if (!raw) {
        const nextLoop = {
          enabled: false,
          aSec: 0,
          bSec: Math.max(0.5, d || 2),
        };
        loopRef.current = nextLoop;
        setLoop(nextLoop);
        return;
      }

      const parsed = JSON.parse(raw) as Partial<LoopRegion>;
      const a = clamp(Number(parsed.aSec ?? 0), 0, Math.max(0, d - minLoopSec));
      const b = clamp(
        Number(parsed.bSec ?? Math.max(0.5, d || 2)),
        a + minLoopSec,
        Math.max(a + minLoopSec, d || a + 2),
      );

      const nextLoop = {
        enabled: Boolean(parsed.enabled ?? false),
        aSec: a,
        bSec: b,
      };
      loopRef.current = nextLoop;
      setLoop(nextLoop);
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.rallyIndex, row?.videoUrl, loopKey, durationSec, seedLoopRegion]);

  React.useEffect(() => {
    seedLoopAppliedRef.current = null;
  }, [row?.rallyIndex, row?.videoUrl]);

  // Persist loop
  React.useEffect(() => {
    if (!loopKey) return;
    try {
      localStorage.setItem(loopKey, JSON.stringify(loop));
    } catch {
      // ignore
    }
  }, [loopKey, loop]);

  // Mute iff rate < 1 (also helps Safari-ish cases)
  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const m = rate < 1;
    v.muted = m;
    v.defaultMuted = m;
    if (m) v.volume = 0;
  }, [rate]);

  // Apply playbackRate
  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.playbackRate = rate;
  }, [rate]);

  // Keep paused state in sync with actual video element (but don’t treat internal seeks as “user paused”)
  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const onPlay = () => {
      if (userPausedRef.current) {
        try {
          v.pause();
        } catch {}
        return;
      }
      setPausedInternal(false);
    };

    const onPause = () => {
      if (performance.now() < suppressPauseUntilRef.current) {
        if (!pausedRef.current && !userPausedRef.current)
          v.play().catch(() => {});
        return;
      }
      setPausedInternal(true);

      // NEW: if we didn't intend to pause, kick once
      if (!userPausedRef.current && !pausedRef.current) {
        v.play().catch(() => {});
      }
    };

    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);

    setPausedInternal(v.paused);

    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
    };
  }, [row?.videoUrl, row?.rallyIndex]);

  // Drive the video element from paused state (without flapping)
  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    if (paused) {
      if (!v.paused) {
        try {
          v.pause();
        } catch {}
      }
    } else {
      if (v.paused) v.play().catch(() => {});
    }
  }, [paused]);

  React.useEffect(() => {
    if (!seekToActiveShotOnChange) return;
    if (!row) return;
    if (activeShotNumber == null) return;

    const v = videoRef.current;
    if (!v) return;

    const ov = findOverlayByShotNumber(
      row.shotNumberOverlays,
      activeShotNumber,
    );
    if (!ov) return;

    let t =
      getOverlayMsRel(
        ov,
        row.segmentStartMs ?? row.startMs ?? null,
        row.segmentEndMs ?? row.endMs ?? null,
      ) / 1000;

    if (durationSec != null && Number.isFinite(durationSec)) {
      t = clamp(t, 0, Math.max(0, durationSec - 0.05));
    }

    try {
      v.currentTime = t;

      if (!pausedRef.current) {
        v.play().catch((err) => {
          const msg = String(err?.message ?? "");
          if (
            err?.name === "AbortError" ||
            msg.includes("interrupted by a new load request") ||
            msg.includes("interrupted by a call to pause")
          ) {
            return;
          }
        });
      }
    } catch {
      // ignore
    }
  }, [row, activeShotNumber, durationSec, seekToActiveShotOnChange]);

  // Autoplay management on new src (but do NOT fight paused state)
  React.useEffect(() => {
    if (!autoPlayOnRallyChange) return;

    const v = videoRef.current;
    if (!v) return;

    const mySeq = ++playSeqRef.current;

    // We're about to swap / re-init playback. This pause is *internal*.
    suppressPauseForMs(350);

    try {
      v.pause();
    } catch {}

    let cancelled = false;

    const tryPlay = async () => {
      if (cancelled || playSeqRef.current !== mySeq) return;
      if (userPausedRef.current) return; // ✅ only respect explicit user pause

      try {
        const p = v.play();
        if (p && typeof (p as any).catch === "function") await p;
      } catch (err: any) {
        const msg = String(err?.message ?? "");
        if (
          err?.name === "AbortError" ||
          msg.includes("interrupted by a new load request") ||
          msg.includes("interrupted by a call to pause")
        ) {
          return;
        }
      }
    };

    const onCanPlay = () => void tryPlay();
    v.addEventListener("canplay", onCanPlay, { once: true });

    void tryPlay();

    return () => {
      cancelled = true;
      v.removeEventListener("canplay", onCanPlay);
      try {
        v.pause();
      } catch {}
    };
  }, [row?.videoUrl, row?.rallyIndex]);

  // -----------------------------
  // Loop enforcement (reliable, no stutter)
  // -----------------------------

  // -----------------------------
  // Loop enforcement (guaranteed)
  // -----------------------------
  // -----------------------------
  // Loop enforcement (always-on when loop.enabled + playing)
  // -----------------------------
  const loopJumpingRef = React.useRef(false);

  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    let timer: number | null = null;

    const enforceOnce = () => {
      const L = loopRef.current;
      if (!L.enabled) return;
      if (!v) return;
      if (pausedRef.current) return; // ✅ use OUR state, not v.paused
      if (!Number.isFinite(v.currentTime)) return;
      if (v.seeking) return;
      if (loopJumpingRef.current) return;

      const a = L.aSec;
      const b = Math.max(L.bSec, a + minLoopSec);

      // tolerate tiny overshoot
      const eps = 0.04;
      const t = v.currentTime;

      if (t < a - eps || t >= b - eps) {
        loopJumpingRef.current = true;
        suppressPauseForMs(220);
        try {
          v.currentTime = a;
        } catch {}

        // re-play if we should be playing
        if (!pausedRef.current && !userPausedRef.current) {
          v.play().catch(() => {});
        }

        setTimeout(() => {
          loopJumpingRef.current = false;
        }, 0);
      }
    };

    const start = () => {
      if (timer != null) return;
      timer = window.setInterval(enforceOnce, 20);
    };

    const stop = () => {
      if (timer == null) return;
      window.clearInterval(timer);
      timer = null;
    };

    // ✅ timeupdate fallback (very reliable)
    const onTimeUpdate = () => enforceOnce();

    v.addEventListener("timeupdate", onTimeUpdate);

    // ✅ drive start/stop off of state + loop.enabled, not DOM "play"
    if (loopRef.current.enabled && !pausedRef.current) start();
    else stop();

    return () => {
      v.removeEventListener("timeupdate", onTimeUpdate);
      stop();
    };
  }, [minLoopSec, row?.videoUrl, row?.rallyIndex, loop.enabled, paused]);

  // If loop becomes enabled, snap into region immediately (so it feels “on”)
  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (!loop.enabled) return;

    const a = loop.aSec;
    const b = loop.bSec;
    // if (v.currentTime < a || v.currentTime > b) {
    //   suppressPauseForMs(220);
    //   v.currentTime = a;
    // }
    if (v.currentTime < a || v.currentTime >= b - 0.02) {
      suppressPauseForMs(220);
      v.currentTime = a;
      setPlayheadSecUi(a);
      setScrubPlayheadSecUi(a);
    }
  }, [loop.enabled, loop.aSec, loop.bSec]);

  // -----------------------------
  // Scrubber helpers
  // -----------------------------
  const trackRef = React.useRef<HTMLDivElement | null>(null);

  const clearScrubPointerCapture = React.useCallback(() => {
    const pointerId = scrubPointerIdRef.current;
    const pointerEl = scrubPointerElRef.current;
    scrubPointerIdRef.current = null;
    scrubPointerElRef.current = null;
    if (pointerId == null) return;
    try {
      if (pointerEl?.hasPointerCapture?.(pointerId)) {
        pointerEl.releasePointerCapture(pointerId);
      }
    } catch {
      // Some browsers throw if capture was already released.
    }
  }, []);

  const getTrackSecFromClientX = React.useCallback(
    (clientX: number) => {
      const d = durationSec ?? 0;
      const L = loopRef.current;
      const trackStartSec = L.enabled ? clamp(L.aSec, 0, Math.max(0, d)) : 0;
      const trackEndSec = L.enabled
        ? clamp(
            Math.max(L.bSec, trackStartSec + minLoopSec),
            trackStartSec + minLoopSec,
            Math.max(trackStartSec + minLoopSec, d),
          )
        : d;
      const trackDurationSec = Math.max(minLoopSec, trackEndSec - trackStartSec);
      const el = trackRef.current;
      if (!el || !d) return 0;
      const r = el.getBoundingClientRect();
      const t = clamp((clientX - r.left) / Math.max(1, r.width), 0, 1);
      return trackStartSec + t * trackDurationSec;
    },
    [durationSec, minLoopSec],
  );

  const getLoopClampedSecFromClientX = React.useCallback(
    (clientX: number) => {
      return clampSecToLoopIfEnabled(getTrackSecFromClientX(clientX));
    },
    [getTrackSecFromClientX, clampSecToLoopIfEnabled],
  );

  const seekNow = React.useCallback(
    (sec: number) => {
      const v = videoRef.current;
      if (!v) return;
      try {
        const next = clampSecToLoopIfEnabled(sec);
        suppressPauseForMs(220);
        v.currentTime = next;
        setPlayheadSecUi(next);
        setScrubPlayheadSecUi(next);
      } catch {}
    },
    [clampSecToLoopIfEnabled],
  );

  const seekNow_old = React.useCallback((sec: number) => {
    const v = videoRef.current;
    if (!v) return;
    try {
      suppressPauseForMs(220);
      v.currentTime = sec;
    } catch {}
  }, []);

  // const seekRaf = useRafThrottle((sec: number) => {
  //   seekNow(sec);
  //   setPlayheadSecUi(sec);
  // });
  const seekRaf = useRafThrottle((sec: number) => {
    seekNow(sec);
  });

  const beginScrubDrag = React.useCallback(
    (
      event: React.PointerEvent<HTMLElement>,
      nextDragging: "a" | "b" | "p",
      seekOnStart: boolean,
    ) => {
      suppressMouseScrubUntilRef.current = Date.now() + 800;
      onControlsInteractionChange?.(true);
      if (!durationSec) return;

      clearScrubPointerCapture();
      scrubPointerIdRef.current = event.pointerId;
      scrubPointerElRef.current = event.currentTarget;
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // Pointer capture is an affordance, not a requirement.
      }

      const v = videoRef.current;
      wasPlayingBeforeDragRef.current = !!v && !v.paused;
      draggingRef.current = nextDragging;
      setDragging(nextDragging);
      scrubLastXRef.current = event.clientX;
      scrubAccumRef.current = 0;
      setPausedUser(true);

      if (seekOnStart) {
        const secRaw = getTrackSecFromClientX(event.clientX);
        const secClamped = clampSecToLoopIfEnabled(secRaw);
        setScrubPlayheadUiRaf(secClamped);
        seekNow(secClamped);
      }

      event.preventDefault();
      event.stopPropagation();
    },
    [
      clearScrubPointerCapture,
      clampSecToLoopIfEnabled,
      durationSec,
      getTrackSecFromClientX,
      onControlsInteractionChange,
      seekNow,
      setPausedUser,
    ],
  );

  const beginMouseScrubDrag = React.useCallback(
    (
      event: React.MouseEvent<HTMLElement>,
      nextDragging: "a" | "b" | "p",
      seekOnStart: boolean,
    ) => {
      if (Date.now() < suppressMouseScrubUntilRef.current) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      onControlsInteractionChange?.(true);
      if (!durationSec) return;

      clearScrubPointerCapture();
      const v = videoRef.current;
      wasPlayingBeforeDragRef.current = !!v && !v.paused;
      draggingRef.current = nextDragging;
      setDragging(nextDragging);
      scrubLastXRef.current = event.clientX;
      scrubAccumRef.current = 0;
      setPausedUser(true);

      if (seekOnStart) {
        const secRaw = getTrackSecFromClientX(event.clientX);
        const secClamped = clampSecToLoopIfEnabled(secRaw);
        setScrubPlayheadUiRaf(secClamped);
        seekNow(secClamped);
      }

      event.preventDefault();
      event.stopPropagation();
    },
    [
      clearScrubPointerCapture,
      clampSecToLoopIfEnabled,
      durationSec,
      getTrackSecFromClientX,
      onControlsInteractionChange,
      seekNow,
      setPausedUser,
    ],
  );

  const finishScrubDrag = React.useCallback(() => {
    const hadDrag = draggingRef.current != null;
    onControlsInteractionChange?.(false);
    clearScrubPointerCapture();
    setDragging(null);
    scrubLastXRef.current = null;
    scrubAccumRef.current = 0;

    if (!hadDrag) return;

    // Restore to whatever state it was before drag.
    setPausedUser(!wasPlayingBeforeDragRef.current);

    // If loop enabled, clamp playhead into region.
    const v = videoRef.current;
    if (v && loopRef.current.enabled) {
      const a = loopRef.current.aSec;
      const b = loopRef.current.bSec;
      if (v.currentTime < a || v.currentTime >= b - 0.02) {
        v.currentTime = a;
        setPlayheadSecUi(a);
        setScrubPlayheadSecUi(a);
      }
    }
  }, [clearScrubPointerCapture, onControlsInteractionChange, setPausedUser]);

  const setLoopA = React.useCallback(
    (aSecNext: number) => {
      const d = durationSec ?? 0;
      setLoop((cur) => {
        const a = clamp(aSecNext, 0, Math.max(0, d - minLoopSec));
        const b = clamp(
          cur.bSec,
          a + minLoopSec,
          Math.max(a + minLoopSec, d || a + 2),
        );

        const v = videoRef.current;
        if (v) {
          const nextT = clamp(v.currentTime, a, b);
          if (nextT !== v.currentTime) {
            suppressPauseForMs(220);
            v.currentTime = nextT;
            setPlayheadSecUi(nextT);
            setScrubPlayheadSecUi(nextT);
          }
        }

        return { ...cur, enabled: true, aSec: a, bSec: b };
      });
    },
    [durationSec, minLoopSec],
  );

  const setLoopB = React.useCallback(
    (bSecNext: number) => {
      const d = durationSec ?? 0;
      setLoop((cur) => {
        const b = clamp(
          bSecNext,
          cur.aSec + minLoopSec,
          Math.max(cur.aSec + minLoopSec, d || cur.aSec + 2),
        );

        const v = videoRef.current;
        if (v) {
          const nextT = clamp(v.currentTime, cur.aSec, b);
          if (nextT !== v.currentTime) {
            suppressPauseForMs(220);
            v.currentTime = nextT;
            setPlayheadSecUi(nextT);
            setScrubPlayheadSecUi(nextT);
          }
        }

        return { ...cur, enabled: true, aSec: cur.aSec, bSec: b };
      });
    },
    [durationSec, minLoopSec],
  );

  const applyScrubDragClientX = React.useCallback(
    (clientX: number) => {
      if (!durationSec) return;
      const activeDrag = draggingRef.current;
      if (!activeDrag) return;

      const secRaw = getTrackSecFromClientX(clientX);
      const secClamped = clampSecToLoopIfEnabled(secRaw);

      if (activeDrag === "a") {
        setLoopA(secRaw);
        seekRaf(secRaw);
      } else if (activeDrag === "b") {
        setLoopB(secRaw);
      } else {
        scrubLastXRef.current = clientX;
        scrubAccumRef.current = 0;
        setScrubPlayheadUiRaf(secClamped);
        seekRaf(secClamped);
      }
    },
    [
      clampSecToLoopIfEnabled,
      durationSec,
      getTrackSecFromClientX,
      seekRaf,
      setLoopA,
      setLoopB,
    ],
  );

  const setLoopA_old = React.useCallback(
    (aSecNext: number) => {
      const d = durationSec ?? 0;
      setLoop((cur) => {
        const a = clamp(aSecNext, 0, Math.max(0, d - minLoopSec));
        const b = clamp(
          cur.bSec,
          a + minLoopSec,
          Math.max(a + minLoopSec, d || a + 2),
        );
        // ✅ auto-enable loop when user adjusts region
        return { ...cur, enabled: true, aSec: a, bSec: b };
      });
    },
    [durationSec],
  );

  const setLoopB_old = React.useCallback(
    (bSecNext: number) => {
      const d = durationSec ?? 0;
      setLoop((cur) => {
        const b = clamp(
          bSecNext,
          cur.aSec + minLoopSec,
          Math.max(cur.aSec + minLoopSec, d || cur.aSec + 2),
        );
        // ✅ auto-enable loop when user adjusts region
        return { ...cur, enabled: true, bSec: b };
      });
    },
    [durationSec],
  );

  const clearLoop = React.useCallback(() => {
    const d = durationSec ?? 0;
    const resetLoop = {
      enabled: false,
      aSec: 0,
      bSec: Math.max(0.5, d || 2),
    };
    loopRef.current = resetLoop;
    setDragging(null);
    setLoop({
      ...resetLoop,
    });
    const v = videoRef.current;
    if (v && Number.isFinite(v.currentTime)) {
      const next = clamp(v.currentTime, 0, resetLoop.bSec);
      setPlayheadSecUi(next);
      setScrubPlayheadSecUi(next);
    }
  }, [durationSec]);

  const nextBallReadReps = React.useMemo<NextBallReadRep[]>(() => {
    return buildNextBallReadRepsForRow(row, durationSec, "rally");
  }, [durationSec, row]);

  const randomKitchenReadCandidates = React.useMemo<NextBallReadRep[]>(() => {
    return rows.flatMap((candidateRow) =>
      buildNextBallReadRepsForRow(
        candidateRow,
        candidateRow === row ? durationSec : null,
        "random-kitchen",
      ).filter(isKitchenNextBallReadCandidate),
    );
  }, [durationSec, row, rows]);

  const seekToNextBallReadRep = React.useCallback(
    (rep: NextBallReadRep, stage: NextBallReadStage = "read") => {
      const v = videoRef.current;
      const next = { rep, stage };

      clearLoop();
      nextBallReadRef.current = next;
      setNextBallRead(next);
      if (!v) return;

      suppressPauseForMs(500);
      try {
        v.pause();
        v.currentTime = rep.readSec;
      } catch {}
      setPlayheadSecUi(rep.readSec);
      setScrubPlayheadSecUi(rep.readSec);
      setPausedUser(true);
    },
    [clearLoop, setPausedUser],
  );

  const selectNextBallReadRep = React.useCallback(() => {
    if (!nextBallReadReps.length) return null;

    const currentSec = Number.isFinite(videoRef.current?.currentTime)
      ? videoRef.current!.currentTime
      : playheadSecUi;
    const currentRep = nextBallRead?.rep;
    const containingIndex = nextBallReadReps.findIndex(
      (rep) =>
        currentSec >= rep.readSec - 0.05 &&
        currentSec <= rep.revealEndSec + 0.05,
    );
    const startIndex =
      currentRep != null
        ? currentRep.overlayIndex
        : containingIndex >= 0
          ? containingIndex
          : Math.max(
              0,
              nextBallReadReps.findIndex(
                (rep) => rep.readSec > currentSec + 0.05,
              ),
            );
    return nextBallReadReps[startIndex] ?? nextBallReadReps[0] ?? null;
  }, [nextBallRead?.rep, nextBallReadReps, playheadSecUi]);

  React.useEffect(() => {
    if (!nextBallRead || nextBallRead.stage !== "read") return;
    const v = videoRef.current;
    if (!v) return;

    let cancelled = false;
    const timeouts: number[] = [];
    let rafId: number | null = null;

    const enforceReadFrame = () => {
      if (cancelled) return;
      const currentRead = nextBallReadRef.current;
      if (
        currentRead?.stage !== "read" ||
        currentRead.rep !== nextBallRead.rep
      ) {
        return;
      }
      suppressPauseForMs(500);
      try {
        v.pause();
        if (
          !Number.isFinite(v.currentTime) ||
          Math.abs(v.currentTime - nextBallRead.rep.readSec) > 0.025
        ) {
          v.currentTime = nextBallRead.rep.readSec;
        }
      } catch {}
      setPlayheadSecUi(nextBallRead.rep.readSec);
      setScrubPlayheadSecUi(nextBallRead.rep.readSec);
      setPausedUser(true);
    };

    enforceReadFrame();
    rafId = window.requestAnimationFrame(enforceReadFrame);
    timeouts.push(
      window.setTimeout(enforceReadFrame, 80),
      window.setTimeout(enforceReadFrame, 240),
      window.setTimeout(enforceReadFrame, 520),
    );
    v.addEventListener("loadedmetadata", enforceReadFrame);
    v.addEventListener("canplay", enforceReadFrame);
    return () => {
      cancelled = true;
      if (rafId != null) window.cancelAnimationFrame(rafId);
      timeouts.forEach((id) => window.clearTimeout(id));
      v.removeEventListener("loadedmetadata", enforceReadFrame);
      v.removeEventListener("canplay", enforceReadFrame);
    };
  }, [nextBallRead, setPausedUser]);

  const startNextBallRead = React.useCallback(() => {
    const rep = selectNextBallReadRep();
    if (rep) seekToNextBallReadRep(rep);
  }, [seekToNextBallReadRep, selectNextBallReadRep]);

  const openPopoutAndStartNextBallRead = React.useCallback(() => {
    const rep = selectNextBallReadRep();
    if (rep) openPopoutAtNextBallRead(rep);
  }, [openPopoutAtNextBallRead, selectNextBallReadRep]);

  const startRandomKitchenRead = React.useCallback(() => {
    if (!randomKitchenReadCandidates.length) return;

    const currentKey =
      nextBallRead?.rep.rallyIndex != null
        ? `${nextBallRead.rep.rallyIndex}:${nextBallRead.rep.shotNumber}`
        : null;
    const navigableCandidates = onOpenRallyFromSimilarPopup
      ? randomKitchenReadCandidates
      : randomKitchenReadCandidates.filter(
          (rep) => rep.rallyIndex === row?.rallyIndex,
        );
    const candidatesWithoutCurrent =
      navigableCandidates.length > 1 && currentKey
        ? navigableCandidates.filter(
            (rep) => `${rep.rallyIndex}:${rep.shotNumber}` !== currentKey,
          )
        : navigableCandidates;
    const crossRallyCandidates = candidatesWithoutCurrent.filter(
      (rep) => rep.rallyIndex !== row?.rallyIndex,
    );
    const candidates =
      onOpenRallyFromSimilarPopup && crossRallyCandidates.length > 0
        ? crossRallyCandidates
        : candidatesWithoutCurrent;
    const rep = candidates[Math.floor(Math.random() * candidates.length)];
    if (!rep || rep.rallyIndex == null) return;

    pendingRandomKitchenReadRef.current = {
      rallyIndex: rep.rallyIndex,
      shotNumber: rep.shotNumber,
    };

    if (rep.rallyIndex === row?.rallyIndex) {
      seekToNextBallReadRep(rep);
      pendingRandomKitchenReadRef.current = null;
      return;
    }

    if (onOpenRallyFromSimilarPopup) {
      onOpenRallyFromSimilarPopup(rep.rallyIndex);
    }
  }, [
    nextBallRead?.rep.rallyIndex,
    nextBallRead?.rep.shotNumber,
    onOpenRallyFromSimilarPopup,
    randomKitchenReadCandidates,
    row?.rallyIndex,
    seekToNextBallReadRep,
  ]);

  const revealNextBallRead = React.useCallback(() => {
    const active = nextBallRead;
    const v = videoRef.current;
    if (!active || !v) return;

    suppressPauseForMs(500);
    try {
      v.currentTime = active.rep.readSec;
      setPlayheadSecUi(active.rep.readSec);
      setScrubPlayheadSecUi(active.rep.readSec);
      v.play().catch(() => {});
    } catch {}
    setPausedUser(false);
    const next = { ...active, stage: "revealing" as const };
    nextBallReadRef.current = next;
    setNextBallRead(next);
  }, [nextBallRead, setPausedUser]);

  const watchNextBallReadAgain = React.useCallback(() => {
    const rep = nextBallRead?.rep;
    if (!rep) return;
    seekToNextBallReadRep(rep);
  }, [nextBallRead?.rep, seekToNextBallReadRep]);

  const goToNextBallReadRep = React.useCallback(() => {
    if (!nextBallReadReps.length) return;
    const currentIndex = nextBallRead?.rep.overlayIndex ?? -1;
    const nextRep =
      nextBallReadReps[(currentIndex + 1) % nextBallReadReps.length] ??
      nextBallReadReps[0];
    if (nextRep) seekToNextBallReadRep(nextRep);
  }, [nextBallRead?.rep.overlayIndex, nextBallReadReps, seekToNextBallReadRep]);

  const goToPrevBallReadRep = React.useCallback(() => {
    if (!nextBallReadReps.length) return;
    const currentIndex = nextBallRead?.rep.overlayIndex ?? 0;
    const prevIndex =
      (currentIndex - 1 + nextBallReadReps.length) % nextBallReadReps.length;
    const prevRep = nextBallReadReps[prevIndex] ?? nextBallReadReps[0];
    if (prevRep) seekToNextBallReadRep(prevRep);
  }, [nextBallRead?.rep.overlayIndex, nextBallReadReps, seekToNextBallReadRep]);

  const exitNextBallRead = React.useCallback(() => {
    nextBallReadRef.current = null;
    setNextBallRead(null);
  }, []);

  React.useEffect(() => {
    nextBallReadRef.current = null;
    setNextBallRead(null);
  }, [row?.rallyIndex, row?.videoUrl]);

  React.useEffect(() => {
    if (
      nextBallReadSeedNonce == null ||
      !nextBallReadSeedStage ||
      !nextBallReadSeedRep
    ) {
      appliedNextBallReadSeedNonceRef.current = null;
      return;
    }
    if (appliedNextBallReadSeedNonceRef.current === nextBallReadSeedNonce) {
      return;
    }
    if (
      nextBallReadSeedRep.rallyIndex != null &&
      nextBallReadSeedRep.rallyIndex !== row?.rallyIndex
    ) {
      return;
    }

    const matchingRep = nextBallReadReps.find(
      (candidate) => candidate.shotNumber === nextBallReadSeedRep.shotNumber,
    );
    const rep = {
      ...(matchingRep ?? nextBallReadSeedRep),
      source: nextBallReadSeedRep.source ?? matchingRep?.source ?? "rally",
    };
    seekToNextBallReadRep(rep, nextBallReadSeedStage);
    appliedNextBallReadSeedNonceRef.current = nextBallReadSeedNonce;
  }, [
    nextBallReadSeedNonce,
    nextBallReadSeedRep,
    nextBallReadSeedStage,
    nextBallReadReps,
    row?.rallyIndex,
    seekToNextBallReadRep,
  ]);

  React.useEffect(() => {
    const pending = pendingRandomKitchenReadRef.current;
    if (!pending || pending.rallyIndex !== row?.rallyIndex) return;

    const rep = nextBallReadReps.find(
      (candidate) => candidate.shotNumber === pending.shotNumber,
    );
    if (!rep) return;

    pendingRandomKitchenReadRef.current = null;
    seekToNextBallReadRep({ ...rep, source: "random-kitchen" });
  }, [nextBallReadReps, row?.rallyIndex, seekToNextBallReadRep]);

  React.useEffect(() => {
    const v = videoRef.current;
    if (!v || nextBallRead?.stage !== "revealing") return;
    let cancelled = false;
    const playTimeouts: number[] = [];

    const ensureRevealPlayback = () => {
      if (
        cancelled ||
        nextBallReadRef.current?.stage !== "revealing" ||
        !v.paused ||
        v.currentTime >= nextBallRead.rep.revealEndSec - 0.025
      ) {
        return;
      }

      suppressPauseForMs(500);
      setPausedUser(false);
      v.play().catch(() => {});
    };

    ensureRevealPlayback();
    playTimeouts.push(
      window.setTimeout(ensureRevealPlayback, 80),
      window.setTimeout(ensureRevealPlayback, 240),
      window.setTimeout(ensureRevealPlayback, 520),
    );

    return () => {
      cancelled = true;
      playTimeouts.forEach((id) => window.clearTimeout(id));
    };
  }, [nextBallRead, setPausedUser]);

  React.useEffect(() => {
    const v = videoRef.current;
    if (!v || nextBallRead?.stage !== "revealing") return;
    let cancelled = false;
    let rafId: number | null = null;
    let rvfcId: number | null = null;
    let timeoutId: number | null = null;
    let stopped = false;

    const stopAtRevealEnd = () => {
      if (
        cancelled ||
        stopped ||
        v.currentTime < nextBallRead.rep.revealEndSec - 0.006
      ) {
        return;
      }
      stopped = true;
      suppressPauseForMs(250);
      try {
        v.pause();
      } catch {}
      const stoppedSec = Math.max(v.currentTime, nextBallRead.rep.revealEndSec);
      setPlayheadSecUi(stoppedSec);
      setScrubPlayheadSecUi(stoppedSec);
      setPausedUser(true);
      setNextBallRead((current) => {
        const next =
          current?.rep === nextBallRead.rep
            ? { ...current, stage: "revealed" as const }
            : current;
        nextBallReadRef.current = next;
        return next;
      });
    };

    const scheduleFrameCheck = () => {
      if (cancelled) return;
      stopAtRevealEnd();
      if (v.paused) return;
      const requestVideoFrameCallback = (
        v as HTMLVideoElement & {
          requestVideoFrameCallback?: (cb: () => void) => number;
        }
      ).requestVideoFrameCallback;
      if (requestVideoFrameCallback) {
        rvfcId = requestVideoFrameCallback.call(v, scheduleFrameCheck);
      } else {
        rafId = window.requestAnimationFrame(scheduleFrameCheck);
      }
    };

    const msUntilRevealEnd = Math.max(
      0,
      ((nextBallRead.rep.revealEndSec - v.currentTime) /
        Math.max(0.1, v.playbackRate || 1)) *
        1000 -
        10,
    );
    timeoutId = window.setTimeout(stopAtRevealEnd, msUntilRevealEnd);
    scheduleFrameCheck();
    v.addEventListener("timeupdate", stopAtRevealEnd);
    return () => {
      cancelled = true;
      if (timeoutId != null) window.clearTimeout(timeoutId);
      if (rafId != null) window.cancelAnimationFrame(rafId);
      if (rvfcId != null) {
        (
          v as HTMLVideoElement & {
            cancelVideoFrameCallback?: (id: number) => void;
          }
        ).cancelVideoFrameCallback?.(rvfcId);
      }
      v.removeEventListener("timeupdate", stopAtRevealEnd);
    };
  }, [nextBallRead, setPausedUser]);

  const canStartNextBallRead = nextBallReadReps.length > 0;
  const canStartRandomKitchenRead = randomKitchenReadCandidates.some(
    (rep) => onOpenRallyFromSimilarPopup || rep.rallyIndex === row?.rallyIndex,
  );
  const showNextBallReadButton =
    isInFullPopup || (!disablePopout && !popoutOpen);
  const nextBallReadButtonActive = isInFullPopup && Boolean(nextBallRead);
  const handleNextBallReadButtonClick = isInFullPopup
    ? nextBallRead
      ? exitNextBallRead
      : startNextBallRead
    : openPopoutAndStartNextBallRead;
  const nextBallReadUseTopPlacement =
    isInFullPopup || stableVideoStage || controlsPlacement === "left";

  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const handleDemoFullRallyPlayback = (event: Event) => {
      const detail = (event as CustomEvent<{
        timeSec?: number;
        play?: boolean;
        muted?: boolean;
      }>).detail ?? {};
      const d = Number.isFinite(v.duration) ? v.duration : (durationSec ?? 0);
      const timeSec = Math.max(
        0,
        Math.min(
          Number.isFinite(Number(detail.timeSec)) ? Number(detail.timeSec) : 0,
          Math.max(0, d || 0),
        ),
      );

      clearLoop();
      userPausedRef.current = detail.play === false;
      setPausedInternal(detail.play === false);
      suppressPauseForMs(900);
      try {
        v.muted = detail.muted !== false;
        v.currentTime = timeSec;
      } catch {}
      setPlayheadSecUi(timeSec);
      setScrubPlayheadSecUi(timeSec);

      if (detail.play !== false) {
        try {
          v.play().catch(() => {});
        } catch {}
      } else {
        try {
          v.pause();
        } catch {}
      }
    };

    v.addEventListener("pbv-demo-full-rally-playback", handleDemoFullRallyPlayback);
    return () => {
      v.removeEventListener("pbv-demo-full-rally-playback", handleDemoFullRallyPlayback);
    };
  }, [clearLoop, durationSec]);

  React.useEffect(() => {
    return () => {
      clearScrubPointerCapture();
      setDragging(null);
      scrubLastXRef.current = null;
      scrubAccumRef.current = 0;
      onControlsInteractionChangeRef.current?.(false);
    };
  }, [
    clearScrubPointerCapture,
    row?.rallyIndex,
    row?.videoUrl,
  ]);

  // -----------------------------
  // AI Slideout (stubby UI)
  // -----------------------------
  const [aiOpen, setAiOpen] = React.useState(false);
  const [aiRangeMode, setAiRangeMode] = React.useState<AiRangeMode>("loop");

  React.useEffect(() => {
    if (props.aiOpenViaParent == null) return;
    setAiOpen(props.aiOpenViaParent);
  }, [props.aiOpenViaParent]);

  React.useEffect(() => {
    setAiRangeMode(loop.enabled ? "loop" : "full");
  }, [loop.enabled, row?.rallyIndex]);

  // -----------------------------
  // ClipFocus emission (for Ask AI + parent UI)
  // -----------------------------
  const emitFocusRef = React.useRef<ClipFocus | undefined>(undefined);

  const computeClipFocus = React.useCallback((): ClipFocus | undefined => {
    if (!row) return undefined;

    const d = durationSec ?? 0;
    const useLoop = aiRangeMode === "loop" && loop.enabled;

    const aSec = useLoop ? loop.aSec : 0;
    const bSec = useLoop ? loop.bSec : Math.max(0, d);

    const rangeStartMs = row.segmentStartMs + Math.round(aSec * 1000);
    const rangeEndMs = row.segmentStartMs + Math.round(bSec * 1000);

    return {
      rallyIndex: row.rallyIndex,
      videoUrl: row.videoUrl,
      segmentStartMs: row.segmentStartMs,
      segmentEndMs: row.segmentEndMs,
      rangeStartMs,
      rangeEndMs,
      rangeMode: useLoop ? "loop" : "full",
      kind: useLoop ? "scrubber" : "full_rally",
    };
  }, [row, durationSec, aiRangeMode, loop.enabled, loop.aSec, loop.bSec]);

  const emitClipFocusChange = React.useCallback(() => {
    const next = computeClipFocus();

    // Dedup so we don't spam the parent while dragging.
    const prev = emitFocusRef.current;
    // const same =
    //   (!prev && !next) ||
    //   (prev &&
    //     next &&
    //     prev.rallyIndex === next.rallyIndex &&
    //     prev.videoUrl === next.videoUrl &&
    //     prev.segmentStartMs === next.segmentStartMs &&
    //     prev.segmentEndMs === next.segmentEndMs &&
    //     prev.rangeStartMs === next.rangeStartMs &&
    //     prev.rangeEndMs === next.rangeEndMs &&
    //     prev.rangeMode === next.rangeMode);

    // if (same) return;

    emitFocusRef.current = next;
    props.onClipFocusChange?.(next);
  }, [computeClipFocus, props]);

  React.useEffect(() => {
    emitClipFocusChange();
  }, [
    emitClipFocusChange,
    row?.rallyIndex,
    row?.videoUrl,
    row?.segmentStartMs,
    row?.segmentEndMs,
    durationSec,
    aiRangeMode,
    loop.enabled,
    loop.aSec,
    loop.bSec,
  ]);

  const effectiveAiRange = React.useMemo(() => {
    if (!row) return null;
    const d = durationSec ?? 0;
    const useLoop = aiRangeMode === "loop" && loop.enabled;

    const aSec = useLoop ? loop.aSec : 0;
    const bSec = useLoop ? loop.bSec : Math.max(0, d);

    const startMs = row.segmentStartMs + Math.round(aSec * 1000);
    const endMs = row.segmentStartMs + Math.round(bSec * 1000);

    return {
      startMs,
      endMs,
      mode: useLoop ? ("loop" as const) : ("full" as const),
    };
  }, [row, durationSec, aiRangeMode, loop.enabled, loop.aSec, loop.bSec]);

  // React.useEffect(() => {
  //   if (!seedPlayback) return;
  //   if (seedAppliedRef.current === seedPlayback.nonce) return;

  //   const v = videoRef.current;
  //   if (!v) return;

  //   seedAppliedRef.current = seedPlayback.nonce;

  //   try {
  //     suppressPauseForMs(400);
  //     v.muted = true;
  //     v.defaultMuted = true;
  //     v.volume = 0;
  //     v.currentTime = seedPlayback.timeSec ?? 0;
  //   } catch {}

  //   // Important: set paused state to match seed (don’t mark it as “user paused”)
  //   setPausedInternal(!!seedPlayback.paused);

  //   if (!seedPlayback.paused) {
  //     userPausedRef.current = false;
  //     v.play().catch(() => {});
  //   } else {
  //     try {
  //       v.pause();
  //     } catch {}
  //   }
  // }, [seedPlayback, setPausedInternal]);

  // React.useEffect(() => {
  //   if (!seedLoopRegion) return;
  //   if (seedLoopAppliedRef.current === seedLoopRegion.nonce) return;

  //   const d = durationSec ?? 0;
  //   const a = clamp(
  //     Number(seedLoopRegion.aSec ?? 0),
  //     0,
  //     Math.max(0, d - minLoopSec),
  //   );
  //   const b = clamp(
  //     Number(seedLoopRegion.bSec ?? Math.max(0.5, d || 2)),
  //     a + minLoopSec,
  //     Math.max(a + minLoopSec, d || a + 2),
  //   );

  //   seedLoopAppliedRef.current = seedLoopRegion.nonce;
  //   setLoop({
  //     enabled: !!seedLoopRegion.enabled,
  //     aSec: a,
  //     bSec: b,
  //   });
  // }, [seedLoopRegion, durationSec, minLoopSec]);

  // React.useEffect(() => {
  //   if (!seedPlayback) return;
  //   if (seedAppliedRef.current === seedPlayback.nonce) return;

  //   const v = videoRef.current;
  //   if (!v) return;

  //   seedAppliedRef.current = seedPlayback.nonce;

  //   const seededTimeSec = seedPlayback.timeSec ?? 0;

  //   try {
  //     suppressPauseForMs(400);
  //     v.muted = true;
  //     v.defaultMuted = true;
  //     v.volume = 0;
  //     v.currentTime = seededTimeSec;
  //   } catch {}

  //   setPlayheadSecUi(seededTimeSec);
  //   setScrubPlayheadSecUi(seededTimeSec);

  //   // Important: set paused state to match seed (don’t mark it as “user paused”)
  //   setPausedInternal(!!seedPlayback.paused);

  //   if (!seedPlayback.paused) {
  //     userPausedRef.current = false;
  //     v.play().catch(() => {});
  //   } else {
  //     try {
  //       v.pause();
  //     } catch {}
  //   }
  // }, [seedPlayback, setPausedInternal]);

  React.useEffect(() => {
    if (!seedPlayback) return;
    if (seedAppliedRef.current === seedPlayback.nonce) return;

    const v = videoRef.current;
    if (!v) return;

    let cancelled = false;

    const applySeed = () => {
      if (cancelled) return;
      if (seedAppliedRef.current === seedPlayback.nonce) return;

      const seededTimeSec = seedPlayback.timeSec ?? 0;

      try {
        suppressPauseForMs(400);
        v.currentTime = seededTimeSec;
      } catch {
        return;
      }

      seedAppliedRef.current = seedPlayback.nonce;
      setPlayheadSecUi(seededTimeSec);
      setScrubPlayheadSecUi(seededTimeSec);

      userPausedRef.current = !!seedPlayback.paused;
      setPausedInternal(!!seedPlayback.paused);

      if (seedPlayback.paused) {
        try {
          v.pause();
        } catch {}
      } else {
        try {
          v.muted = true;
          v.defaultMuted = true;
          v.volume = 0;
        } catch {}
        suppressPauseForMs(900);
        v.play().catch(() => {});
      }
    };

    if (v.readyState >= 1) {
      applySeed();
    } else {
      v.addEventListener("loadedmetadata", applySeed, { once: true });
      v.addEventListener("loadeddata", applySeed, { once: true });
      v.addEventListener("canplay", applySeed, { once: true });
    }

    return () => {
      cancelled = true;
      v.removeEventListener("loadedmetadata", applySeed);
      v.removeEventListener("loadeddata", applySeed);
      v.removeEventListener("canplay", applySeed);
    };
  }, [seedPlayback, row?.videoUrl, setPausedInternal]);
  React.useEffect(() => {
    if (!seedLoopRegion) return;
    if (seedLoopAppliedRef.current === seedLoopRegion.nonce) return;

    const fallbackDurationSec = Math.max(
      Number(seedLoopRegion.bSec ?? 0),
      Number(seedLoopRegion.aSec ?? 0) + minLoopSec,
      2,
    );
    const d = durationSec && durationSec > 0 ? durationSec : fallbackDurationSec;
    const a = clamp(
      Number(seedLoopRegion.aSec ?? 0),
      0,
      Math.max(0, d - minLoopSec),
    );
    const b = clamp(
      Number(seedLoopRegion.bSec ?? Math.max(0.5, d || 2)),
      a + minLoopSec,
      Math.max(a + minLoopSec, d || a + 2),
    );

    const nextLoop = {
      enabled: !!seedLoopRegion.enabled,
      aSec: a,
      bSec: b,
    };

    seedLoopAppliedRef.current = seedLoopRegion.nonce;
    loopRef.current = nextLoop;
    setLoop(nextLoop);

    if (seedLoopRegion.enabled) {
      setLoopUiOpen(true);
    }
  }, [seedLoopRegion, durationSec, minLoopSec]);

  // const deleteTurn = React.useCallback(
  //   (turnId: string) => {
  //     setTurns((cur) => cur.filter((t) => t.id !== turnId));
  //   },
  //   [setTurns],
  // );

  const deleteTurn = React.useCallback(
    (turnId: string) => {
      setTurns((cur) => cur.filter((t) => t.id !== turnId));
      if (packageId && row) {
        deleteGlobalTurn(packageId, row.rallyIndex, turnId);
      }
    },
    [setTurns, packageId, row],
  );

  // const activeOverlayForFocusedPlayer = React.useMemo(() => {
  //   if (!row) return null;

  //   const overlays = row.shotNumberOverlays as ShotNumberOverlay[] | undefined;
  //   if (!overlays?.length) return null;

  //   const playheadMsRel = Math.round(playheadSecUi * 1000);
  //   const baseMs = overlays[0]?.atMsRel ?? 0;

  //   for (const ov of overlays) {
  //     const showFrom = (ov.showFromMsRel ?? ov.atMsRel) - baseMs;
  //     const hideAt = (ov.hideAtMsRel ?? ov.atMsRel + 1) - baseMs;

  //     if (playheadMsRel >= showFrom && playheadMsRel < hideAt) {
  //       console.log("focused dot debug", {
  //         playheadMsRel,
  //         activeShotNumber: ov.shotNumber,
  //         baseMs,
  //         atMsRelNorm: ov.atMsRel - baseMs,
  //         showFromMsRelNorm: showFrom,
  //         hideAtMsRelNorm: hideAt,
  //       });
  //       return ov;
  //     }
  //   }

  //   let best: ShotNumberOverlay | null = null;
  //   let bestDist = Number.POSITIVE_INFINITY;

  //   for (const ov of overlays) {
  //     const tNorm = ov.atMsRel - baseMs;
  //     const dist = Math.abs(tNorm - playheadMsRel);
  //     if (dist < bestDist) {
  //       bestDist = dist;
  //       best = ov;
  //     }
  //   }

  //   console.log("focused dot debug", {
  //     playheadMsRel,
  //     activeShotNumber: best?.shotNumber,
  //     baseMs,
  //     atMsRelNorm: best ? best.atMsRel - baseMs : null,
  //     fallback: true,
  //   });

  //   return best;
  // }, [row, playheadSecUi]);

  // const livePlayheadSec = videoCurrentTimeSec ?? playheadSecUi;

  // const activeOverlayForFocusedPlayer = null;

  const activeOverlayForFocusedPlayer = React.useMemo(() => {
    if (!row) return null;

    const overlays = row.shotNumberOverlays as ShotNumberOverlay[] | undefined;
    if (!overlays?.length) return null;

    const segmentStartMs = row.segmentStartMs ?? row.startMs ?? null;
    const segmentEndMs = row.segmentEndMs ?? row.endMs ?? null;

    const normalizeOverlayFieldMsRel = (
      value: unknown,
      fallbackMsRel: number,
    ): number => {
      const ms = typeof value === "number" && Number.isFinite(value) ? value : null;
      if (ms == null) return fallbackMsRel;

      const segStart = finiteNumber(segmentStartMs);
      const segEnd = finiteNumber(segmentEndMs);
      const segmentDurationMs =
        segStart != null && segEnd != null && segEnd > segStart
          ? segEnd - segStart
          : null;

      if (
        segStart != null &&
        segmentDurationMs != null &&
        ms >= segStart - 1500 &&
        ms <= segStart + segmentDurationMs + 3000
      ) {
        return Math.max(0, ms - segStart);
      }

      if (
        segmentDurationMs == null ||
        (ms >= -1500 && ms <= segmentDurationMs + 3000)
      ) {
        return Math.max(0, ms);
      }

      return fallbackMsRel;
    };

    const playheadMsRel = Math.round(playheadSecUi * 1000);

    for (const ov of overlays) {
      const atMsRel = getOverlayMsRel(ov, segmentStartMs, segmentEndMs);
      const showFrom = normalizeOverlayFieldMsRel(
        ov.showFromMsRel,
        Math.max(0, atMsRel - 200),
      );
      const hideAt = normalizeOverlayFieldMsRel(
        ov.hideAtMsRel,
        atMsRel + 1,
      );

      if (playheadMsRel >= showFrom && playheadMsRel < hideAt) {
        // console.log("focused dot debug", {
        //   playheadMsRel,
        //   activeShotNumber: ov.shotNumber,
        //   baseMs,
        //   atMsRelNorm: ov.atMsRel - baseMs,
        //   showFromMsRelNorm: showFrom,
        //   hideAtMsRelNorm: hideAt,
        // });
        return ov;
      }
    }

    let best: ShotNumberOverlay | null = null;
    let bestDist = Number.POSITIVE_INFINITY;

    for (const ov of overlays) {
      const tNorm = getOverlayMsRel(ov, segmentStartMs, segmentEndMs);
      const dist = Math.abs(tNorm - playheadMsRel);
      if (dist < bestDist) {
        bestDist = dist;
        best = ov;
      }
    }

    // console.log("focused dot debug", {
    //   playheadMsRel,
    //   activeShotNumber: best?.shotNumber,
    //   baseMs,
    //   atMsRelNorm: best ? best.atMsRel - baseMs : null,
    //   fallback: true,
    // });

    return best;
  }, [row, playheadSecUi]);

  const activeOverlayForTrajectoryPanel = React.useMemo(() => {
    if (!row) return null;

    const overlays = row.shotNumberOverlays as ShotNumberOverlay[] | undefined;
    if (!overlays?.length) return null;

    if (activeShotNumber != null) {
      const fromSelectedShot =
        overlays.find((overlay) => overlay.shotNumber === activeShotNumber) ?? null;
      if (fromSelectedShot) return fromSelectedShot;
    }

    if (overlayLayerShotNumber != null) {
      const fromOverlayLayer =
        overlays.find((overlay) => overlay.shotNumber === overlayLayerShotNumber) ??
        null;
      if (fromOverlayLayer) return fromOverlayLayer;
    }

    return activeOverlayForFocusedPlayer;
  }, [activeOverlayForFocusedPlayer, activeShotNumber, overlayLayerShotNumber, row]);

  const activeShotTrajectoryHighlight = React.useMemo(() => {
    if (!props.highlightActiveShotTrajectory || !row || !cvJson || activeShotNumber == null) {
      return null;
    }
    const overlays = [...(row.shotNumberOverlays ?? [])].sort(
      (a, b) =>
        getOverlayMsRel(a, row.segmentStartMs, row.segmentEndMs) -
        getOverlayMsRel(b, row.segmentStartMs, row.segmentEndMs),
    );
    const index = overlays.findIndex((overlay) => overlay.shotNumber === activeShotNumber);
    if (index < 0) return null;
    const overlay = overlays[index];
    const startMs = getOverlayMsRel(overlay, row.segmentStartMs, row.segmentEndMs);
    const nextOverlay = overlays[index + 1] ?? null;
    const trajectoryEndMs = normalizeShotMsRelForRow(
      row,
      overlay.trajectoryEnd?.ms ??
        overlay.rawShot?.resulting_ball_movement?.trajectory?.end?.ms,
    );
    const endMs = nextOverlay
      ? getOverlayMsRel(nextOverlay, row.segmentStartMs, row.segmentEndMs)
      : trajectoryEndMs ?? startMs + 2200;
    const rallyStartMsValue = row.segmentStartMs ?? row.startMs;
    if (typeof rallyStartMsValue !== "number" || !Number.isFinite(rallyStartMsValue)) return null;
    const rallyStartMs = rallyStartMsValue;
    const trajectoryWindow = extractBallUvSamplesForAbsoluteShotWindow({
      cvJson,
      rallyStartMs,
      shotStartMs: rallyStartMs + startMs,
      shotEndMs: rallyStartMs + endMs,
    });
    if (!trajectoryWindow) return null;
    const trajectory =
      overlay.rawShot?.resulting_ball_movement?.trajectory ?? null;
    const trajectoryStart = trajectory?.start?.location;
    const trajectoryPeak = trajectory?.peak;
    const trajectoryEnd = trajectory?.end?.location;
    if (!trajectoryStart || !trajectoryPeak || !trajectoryEnd) return null;
    const points = sampleQuadraticWorldTrajectory({
      start: trajectoryStart,
      peak: trajectoryPeak,
      end: trajectoryEnd,
      steps: Math.max(24, trajectoryWindow.endFrameOffset - trajectoryWindow.startFrameOffset),
    })
      .map((point, index, allPoints) => {
        const projected = approximateTrajectoryProjection.projectWorldPointApprox3D(point);
        if (!projected) return null;
        return {
          ...projected,
          frameOffset:
            trajectoryWindow.startFrameOffset +
            (index / Math.max(1, allPoints.length - 1)) *
              (trajectoryWindow.endFrameOffset - trajectoryWindow.startFrameOffset),
        };
      })
      .filter((point): point is { u: number; v: number; frameOffset: number } => point != null);
    if (points.length < 2) return null;
    const playheadMs = playheadSecUi * 1000;
    const visible = playheadMs >= startMs && playheadMs < endMs;
    if (!visible) {
      return {
        visible: false,
        points: "",
        start: points[0],
        end: points[0],
        cvRallyIndex: trajectoryWindow.cvRallyIndex,
        startFrameOffset: trajectoryWindow.startFrameOffset,
        endFrameOffset: trajectoryWindow.endFrameOffset,
        pathSource: "projected-3d",
      };
    }
    const progress = Math.max(0, Math.min(1, (playheadMs - startMs) / Math.max(1, endMs - startMs)));
    const currentFrameOffset =
      trajectoryWindow.startFrameOffset +
      progress * (trajectoryWindow.endFrameOffset - trajectoryWindow.startFrameOffset);
    const revealedPoints = points.filter(
      (point) => point.frameOffset <= currentFrameOffset,
    );
    const visiblePoints = revealedPoints.length >= 2 ? revealedPoints : points.slice(0, 2);
    return {
      visible,
      points: visiblePoints.map((point) => `${point.u},${point.v}`).join(" "),
      start: visiblePoints[0],
      end: visiblePoints[visiblePoints.length - 1],
      cvRallyIndex: trajectoryWindow.cvRallyIndex,
      startFrameOffset: trajectoryWindow.startFrameOffset,
      endFrameOffset: trajectoryWindow.endFrameOffset,
      pathSource: "projected-3d",
    };
  }, [
    activeShotNumber,
    approximateTrajectoryProjection,
    cvJson,
    playheadSecUi,
    props.highlightActiveShotTrajectory,
    row,
  ]);

  const handleBookmarkCurrentShot = React.useCallback(() => {
    if (!row || !onBookmarkShot) return;
    const video = videoRef.current;
    const videoTime =
      video && Number.isFinite(video.currentTime)
        ? Math.max(0, video.currentTime)
        : null;
    if (video && !video.paused) {
      setPausedUser(true);
      video.pause();
    }
    const playbackShotNumber =
      videoTime == null
        ? null
        : getCurrentShotNumberFromPlayback({
            currentTimeSec: videoTime,
            overlays: row.shotNumberOverlays,
            segmentStartMs: row.segmentStartMs ?? row.startMs ?? null,
            segmentEndMs: row.segmentEndMs ?? row.endMs ?? null,
          });
    const playbackOverlay =
      playbackShotNumber == null
        ? null
        : row.shotNumberOverlays?.find(
            (candidate) => candidate.shotNumber === playbackShotNumber,
          ) ?? null;
    const overlay = playbackOverlay ?? activeOverlayForTrajectoryPanel;
    const shotNumber =
      overlay?.shotNumber ??
      playbackShotNumber ??
      row.shotNumberOverlays?.[0]?.shotNumber ??
      row.breakShotNumber ??
      1;
    onBookmarkShot({
      rallyIndex: row.rallyIndex,
      shotNumber,
      timeSec: videoTime,
      playerId:
        overlay && Number.isFinite(Number((overlay as any).playerId))
          ? Number((overlay as any).playerId)
          : null,
    });
  }, [
    activeOverlayForTrajectoryPanel,
    onBookmarkShot,
    row,
    setPausedUser,
  ]);

  //     React.useEffect(() => {
  //   props.onAiOpenChange?.(aiOpen);
  // }, [aiOpen, props.onAiOpenChange]);

  // const activeOverlayForFocusedPlayer = React.useMemo(() => {
  //   console.log("overlay memo fired");

  //   if (!row) {
  //     console.log("no row");
  //     return null;
  //   }

  //   const overlays = row.shotNumberOverlays as ShotNumberOverlay[] | undefined;

  //   console.log("overlay info", {
  //     count: overlays?.length,
  //     playheadSecUi,
  //   });

  //   if (!overlays?.length) return null;

  //   const playheadMsRel = Math.round(playheadSecUi * 1000);

  //   let best: ShotNumberOverlay | null = null;
  //   let bestDist = Infinity;

  //   for (const ov of overlays) {
  //     const dist = Math.abs(ov.atMsRel - playheadMsRel);
  //     if (dist < bestDist) {
  //       bestDist = dist;
  //       best = ov;
  //     }
  //   }

  //   console.log("overlay chosen", {
  //     shot: best?.shotNumber,
  //     atMsRel: best?.atMsRel,
  //     playheadMsRel
  //   });

  //   return best;
  // }, [row, playheadSecUi]);

  const FOCUSED_PLAYER_GHOST_TRAIL = {
    enabled: true,

    // How many prior trail points to show, not counting the live/current dot
    pointCount: 5,

    // Time spacing between ghost points
    stepMs: 90,

    // Keep trail visible even when paused
    showWhenPaused: true,
    showWhilePlaying: false,

    // Circle sizing
    currentRadiusFullPopup: 0.0125,
    currentRadiusInline: 0.011,

    // ghostRadiusStartFullPopup: 0.0105,
    // ghostRadiusEndFullPopup: 0.006,
    // ghostRadiusStartInline: 0.009,
    // ghostRadiusEndInline: 0.0055,

    // // Base opacity
    // ghostOpacityStart: 0.42,
    // ghostOpacityEnd: 0.1,

    // // Default stroke
    // ghostStrokeOpacityStart: 0.55,
    // ghostStrokeOpacityEnd: 0.08,
    // ghostStrokeWidth: 0.0024,

    ghostRadiusStartFullPopup: 0.0095,
    ghostRadiusEndFullPopup: 0.0055,
    ghostRadiusStartInline: 0.0082,
    ghostRadiusEndInline: 0.005,

    ghostOpacityStart: 0.42, //24,
    ghostOpacityEnd: 0.06,

    ghostStrokeOpacityStart: 0.24,
    ghostStrokeOpacityEnd: 0.04,
    ghostStrokeWidth: 0.0016,

    pathOpacity: 0.08,
    pathWidth: 0.0015,

    // Optional connector line
    showPath: false,
    // pathOpacity: 0.16,
    // pathWidth: 0.0022,

    // Ignore tiny movement so the trail does not become a dense blob
    minScreenDistance: 0.008,

    // Fast-movement highlighting
    highlightFastMovement: true,

    // Assuming court-space is feet
    fastMovementMphThreshold: 6.5,
    veryFastMovementMphThreshold: 9.5,

    // fastMovementStroke: "rgba(255,90,90,0.95)",
    // veryFastMovementStroke: "rgba(255,30,30,0.98)",

    // fastMovementStrokeWidthMultiplier: 1.35,
    // veryFastMovementStrokeWidthMultiplier: 1.8,

    fastMovementStroke: "rgba(255,120,120,0.95)",
    veryFastMovementStroke: "rgba(255,90,90,0.98)",

    fastMovementStrokeWidthMultiplier: 1.35, // 1.15,
    veryFastMovementStrokeWidthMultiplier: 1.3,

    // Small label/debug helper if you later want to inspect values
    includeSpeedMphOnTrailPoints: true,
  } as const;

  function getCvPlayerPositionAtPlayhead(args: {
    cvJson: CvJson | null | undefined;
    rallyIndex: number | null | undefined;
    playheadSec: number;
    focusedPlayerId: number | null | undefined;
    insightsToCvPlayerIndex: PlayerIndexMap;
    worldToScreen: ((x: number, y: number) => { u: number; v: number }) | null;
  }) {
    const {
      cvJson,
      rallyIndex,
      playheadSec,
      focusedPlayerId,
      insightsToCvPlayerIndex,
      worldToScreen,
    } = args;

    if (focusedPlayerId == null) return null;
    if (!worldToScreen) return null;
    if (!cvJson) return null;
    if (rallyIndex == null || rallyIndex < 0) return null;

    const cvPlayerIndex = insightsToCvPlayerIndex[focusedPlayerId];

    const session = cvJson.sessions?.[0];
    const rally = session?.rallies?.[rallyIndex];
    const frames = rally?.frames;
    if (!Array.isArray(frames) || frames.length === 0) return null;

    const fpsRaw = cvJson.camera?.fps;
    const fps =
      typeof fpsRaw === "number" && Number.isFinite(fpsRaw) && fpsRaw > 0
        ? fpsRaw
        : 29.97;

    const exactFrame = Math.max(0, playheadSec * fps);
    const lo = Math.max(0, Math.floor(exactFrame));
    const hi = Math.min(frames.length - 1, Math.ceil(exactFrame));
    const t = Math.max(0, Math.min(1, exactFrame - lo));

    const loPos = frames[lo]?.player_court_positions?.[cvPlayerIndex];
    const hiPos = frames[hi]?.player_court_positions?.[cvPlayerIndex];

    if (!loPos || typeof loPos.x !== "number" || typeof loPos.y !== "number") {
      return null;
    }

    const x =
      hiPos && typeof hiPos.x === "number"
        ? lerp(loPos.x, hiPos.x, t)
        : loPos.x;
    const y =
      hiPos && typeof hiPos.y === "number"
        ? lerp(loPos.y, hiPos.y, t)
        : loPos.y;

    const { u, v } = worldToScreen(x, y);
    if (!Number.isFinite(u) || !Number.isFinite(v)) return null;

    return {
      x,
      y,
      u,
      v,
      team: (focusedPlayerId <= 1 ? 0 : 1) as 0 | 1,
    };
  }

  const opponentContactMs = pounceOverlay?.opponentContact?.ms ?? null;
  const entryMs = pounceOverlay?.entryMs ?? null;
  const exitMs = pounceOverlay?.exitMs ?? null;

  const pounceWasTaken = pounceOverlay?.pounceResult === "taken";

  const bestAttackBaseRadius = isInFullPopup ? 0.0135 : 0.012;

  const bestAttackStroke = pounceWasTaken
    ? "rgba(196,92,92,0.82)"
    : "rgba(196,92,92,0.40)";

  const bestAttackFill = pounceWasTaken
    ? "rgba(196,92,92,0.16)"
    : "rgba(196,92,92,0.00)";

  const bestAttackStrokeWidth = pounceWasTaken
    ? isInFullPopup
      ? 0.0051
      : 0.0045
    : isInFullPopup
      ? 0.0045
      : 0.004;

  const distanceMovedLabel =
    typeof pounceOverlay?.movementDistanceFeet === "number"
      ? `${pounceOverlay.movementDistanceFeet.toFixed(1)} ft`
      : null;

  // const triggerOverlayVisible = React.useMemo(() => {
  //   if (!pounceOverlay?.enabled) return false;
  //   if (playheadAbsMs == null) return false;
  //   if (
  //     typeof opponentContactMs !== "number" ||
  //     !Number.isFinite(opponentContactMs)
  //   ) {
  //     return false;
  //   }

  //   return (
  //     playheadAbsMs >= opponentContactMs - 220 &&
  //     playheadAbsMs <= opponentContactMs + 140
  //   );
  // }, [pounceOverlay, playheadAbsMs, opponentContactMs]);

  // const pounceWindowVisible = React.useMemo(() => {
  //   if (!pounceOverlay?.enabled) return false;
  //   if (playheadAbsMs == null) return false;
  //   if (
  //     typeof entryMs !== "number" ||
  //     !Number.isFinite(entryMs) ||
  //     typeof exitMs !== "number" ||
  //     !Number.isFinite(exitMs)
  //   ) {
  //     return false;
  //   }

  //   return playheadAbsMs >= entryMs - 120 && playheadAbsMs <= exitMs + 180;
  // }, [pounceOverlay, playheadAbsMs, entryMs, exitMs]);

  const triggerOverlayVisible = true;
  //   React.useMemo(() => {
  //   if (!pounceOverlay?.enabled) return false;
  //   if (playheadAbsMs == null) return false;
  //   if (
  //     typeof opponentContactMs !== "number" ||
  //     !Number.isFinite(opponentContactMs)
  //   ) {
  //     return false;
  //   }

  //   return (
  //     playheadAbsMs >= opponentContactMs - 500 &&
  //     playheadAbsMs <= opponentContactMs + 350
  //   );
  // }, [pounceOverlay, playheadAbsMs, opponentContactMs]);

  const pounceWindowVisible = true;
  // React.useMemo(() => {
  //   if (!pounceOverlay?.enabled) return false;
  //   if (playheadAbsMs == null) return false;
  //   if (
  //     typeof entryMs !== "number" ||
  //     !Number.isFinite(entryMs) ||
  //     typeof exitMs !== "number" ||
  //     !Number.isFinite(exitMs)
  //   ) {
  //     return false;
  //   }

  //   return playheadAbsMs >= entryMs - 700 && playheadAbsMs <= exitMs + 500;
  // }, [pounceOverlay, playheadAbsMs, entryMs, exitMs]);

  const requiredMovementVisible =
    pounceWindowVisible &&
    !!pounceOverlay?.requiredMovement?.enabled &&
    !!requiredMovementStartScreen &&
    !!requiredMovementEndScreen;

  const anyPounceOverlayVisible =
    triggerOverlayVisible || pounceWindowVisible || requiredMovementVisible;

  const showPounceCatAtStart =
    requiredMovementVisible &&
    typeof pounceOverlay?.requiredMovement?.distanceFeet === "number" &&
    pounceOverlay.requiredMovement.distanceFeet >= 4 &&
    !!requiredMovementStartScreen &&
    !!requiredMovementEndScreen;

  const pounceCatPlacement = React.useMemo(() => {
    if (!showPounceCatAtStart) return null;
    if (!requiredMovementStartScreen || !requiredMovementEndScreen) return null;

    const startU = requiredMovementStartScreen.u;
    const startV = requiredMovementStartScreen.v;
    const endU = requiredMovementEndScreen.u;
    const endV = requiredMovementEndScreen.v;

    const dx = endU - startU;
    const dy = endV - startV;
    const len = Math.hypot(dx, dy) || 1;

    const ux = dx / len;
    const uy = dy / len;

    const angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;

    const catSize = isInFullPopup ? 0.06 : 0.05;
    const backwardNudge = isInFullPopup ? 0.016 : 0.013;

    const cx = startU;
    const cy = startV;

    const x = cx - catSize / 2 - ux * backwardNudge;
    const y = cy - catSize / 2 - uy * backwardNudge;

    return {
      x,
      y,
      width: catSize,
      height: catSize,
      cx,
      cy,
      angleDeg,
    };
  }, [
    showPounceCatAtStart,
    requiredMovementStartScreen,
    requiredMovementEndScreen,
    isInFullPopup,
  ]);

  function getCvPlayerPositionAtPlayhead_old(args: {
    cvJson: CvJson | null | undefined;
    rallyIndex: number | null | undefined;
    playheadSec: number;
    focusedPlayerId: number | null | undefined;
    worldToScreen: ((x: number, y: number) => { u: number; v: number }) | null;
  }) {
    const { cvJson, rallyIndex, playheadSec, focusedPlayerId, worldToScreen } =
      args;

    if (focusedPlayerId == null) return null;
    if (!worldToScreen) return null;
    if (!cvJson) return null;
    if (rallyIndex == null || rallyIndex < 0) return null;

    const session = cvJson.sessions?.[0];
    const rally = session?.rallies?.[rallyIndex];
    const frames = rally?.frames;
    if (!Array.isArray(frames) || frames.length === 0) return null;

    const fpsRaw = cvJson.camera?.fps;
    const fps =
      typeof fpsRaw === "number" && Number.isFinite(fpsRaw) && fpsRaw > 0
        ? fpsRaw
        : 29.97;

    const exactFrame = Math.max(0, playheadSec * fps);
    const lo = Math.max(0, Math.floor(exactFrame));
    const hi = Math.min(frames.length - 1, Math.ceil(exactFrame));
    const t = Math.max(0, Math.min(1, exactFrame - lo));

    const loPos = frames[lo]?.player_court_positions?.[focusedPlayerId];
    const hiPos = frames[hi]?.player_court_positions?.[focusedPlayerId];

    if (!loPos || typeof loPos.x !== "number" || typeof loPos.y !== "number") {
      return null;
    }

    const x =
      hiPos && typeof hiPos.x === "number"
        ? lerp(loPos.x, hiPos.x, t)
        : loPos.x;
    const y =
      hiPos && typeof hiPos.y === "number"
        ? lerp(loPos.y, hiPos.y, t)
        : loPos.y;

    const { u, v } = worldToScreen(x, y);
    if (!Number.isFinite(u) || !Number.isFinite(v)) return null;

    return {
      x,
      y,
      u,
      v,
      team: (focusedPlayerId <= 1 ? 0 : 1) as 0 | 1,
    };
  }

  const focusedPlayerGhostTrail = React.useMemo(() => {
    if (!FOCUSED_PLAYER_GHOST_TRAIL.enabled) return [];
    if (focusedPlayerId == null) return [];
    if (!paused && !FOCUSED_PLAYER_GHOST_TRAIL.showWhilePlaying) return [];

    if (paused && !FOCUSED_PLAYER_GHOST_TRAIL.showWhenPaused) {
      return [];
    }

    return getFocusedPlayerGhostTrail({
      cvJson,
      rallyIndex: row?.rallyIndex ?? null,
      playheadSec: playheadSecUi,
      focusedPlayerId,
      insightsToCvPlayerIndex,
      worldToScreen,
      pointCount: FOCUSED_PLAYER_GHOST_TRAIL.pointCount,
      stepMs: FOCUSED_PLAYER_GHOST_TRAIL.stepMs,
      minScreenDistance: FOCUSED_PLAYER_GHOST_TRAIL.minScreenDistance,
    });
  }, [
    cvJson,
    row?.rallyIndex,
    playheadSecUi,
    paused,
    focusedPlayerId,
    insightsToCvPlayerIndex,
    worldToScreen,
  ]);

  function getFocusedPlayerGhostTrail(args: {
    cvJson: CvJson | null | undefined;
    rallyIndex: number | null | undefined;
    playheadSec: number;
    focusedPlayerId: number | null | undefined;
    insightsToCvPlayerIndex: PlayerIndexMap;
    worldToScreen: ((x: number, y: number) => { u: number; v: number }) | null;
    pointCount: number;
    stepMs: number;
    minScreenDistance: number;
  }) {
    const {
      cvJson,
      rallyIndex,
      playheadSec,
      focusedPlayerId,
      worldToScreen,
      pointCount,
      stepMs,
      minScreenDistance,
      insightsToCvPlayerIndex,
    } = args;

    if (focusedPlayerId == null) return [];

    const out: Array<{
      x: number;
      y: number;
      u: number;
      v: number;
      team: 0 | 1;
      ageIndex: number;
      speedMph: number | null;
    }> = [];

    let prevScreen: { u: number; v: number } | null = null;
    let prevWorld: { x: number; y: number } | null = null;

    for (let i = 1; i <= pointCount; i++) {
      const tSec = Math.max(0, playheadSec - (i * stepMs) / 1000);

      const pos = getCvPlayerPositionAtPlayhead({
        cvJson,
        rallyIndex,
        playheadSec: tSec,
        focusedPlayerId,
        insightsToCvPlayerIndex,
        worldToScreen,
      });

      if (!pos) continue;

      if (prevScreen) {
        const du = pos.u - prevScreen.u;
        const dv = pos.v - prevScreen.v;
        const dist = Math.sqrt(du * du + dv * dv);
        if (dist < minScreenDistance) {
          continue;
        }
      }

      let speedMph: number | null = null;

      if (prevWorld) {
        const dx = prevWorld.x - pos.x;
        const dy = prevWorld.y - pos.y;
        const distFeet = Math.sqrt(dx * dx + dy * dy);
        const dtSec = stepMs / 1000;

        if (dtSec > 0) {
          const feetPerSec = distFeet / dtSec;
          speedMph = feetPerSec * 0.681818;
        }
      }

      out.push({
        ...pos,
        ageIndex: i,
        speedMph,
      });

      prevScreen = { u: pos.u, v: pos.v };
      prevWorld = { x: pos.x, y: pos.y };
    }

    return out;
  }

  function lerpRange(start: number, end: number, t: number) {
    return start + (end - start) * t;
  }

  function lerp(a: number, b: number, t: number) {
    return a + (b - a) * t;
  }

  function getFocusedPlayerMarkerFromCv(args: {
    cvJson: CvJson | null | undefined;
    rallyIndex: number | null | undefined;
    playheadSec: number;
    focusedPlayerId: number | null | undefined;
    insightsToCvPlayerIndex: PlayerIndexMap;
    worldToScreen: ((x: number, y: number) => { u: number; v: number }) | null;
  }) {
    const {
      cvJson,
      rallyIndex,
      playheadSec,
      focusedPlayerId,
      insightsToCvPlayerIndex,
      worldToScreen,
    } = args;

    if (focusedPlayerId == null) return null;
    if (!worldToScreen) return null;
    if (!cvJson) return null;
    if (rallyIndex == null || rallyIndex < 0) return null;

    const cvPlayerIndex = insightsToCvPlayerIndex[focusedPlayerId];

    const session = cvJson.sessions?.[0];
    const rally = session?.rallies?.[rallyIndex];
    const frames = rally?.frames;

    if (!Array.isArray(frames) || frames.length === 0) return null;

    const fpsRaw = cvJson.camera?.fps;
    const fps =
      typeof fpsRaw === "number" && Number.isFinite(fpsRaw) && fpsRaw > 0
        ? fpsRaw
        : 29.97;

    const exactFrame = playheadSec * fps;
    const lo = Math.max(0, Math.floor(exactFrame));
    const hi = Math.min(frames.length - 1, Math.ceil(exactFrame));
    const t = Math.max(0, Math.min(1, exactFrame - lo));

    const loPos = frames[lo]?.player_court_positions?.[cvPlayerIndex];
    const hiPos = frames[hi]?.player_court_positions?.[cvPlayerIndex];

    if (!loPos || typeof loPos.x !== "number" || typeof loPos.y !== "number") {
      return null;
    }

    const x =
      hiPos && typeof hiPos.x === "number"
        ? lerp(loPos.x, hiPos.x, t)
        : loPos.x;

    const y =
      hiPos && typeof hiPos.y === "number"
        ? lerp(loPos.y, hiPos.y, t)
        : loPos.y;

    const { u, v } = worldToScreen(x, y);
    if (!Number.isFinite(u) || !Number.isFinite(v)) return null;

    return {
      u,
      v,
      team: focusedPlayerId <= 1 ? 0 : 1,
    };
  }

  function getFocusedPlayerMarkerFromCv_old(args: {
    cvJson: CvJson | null | undefined;
    rallyIndex: number | null | undefined;
    playheadSec: number;
    focusedPlayerId: number | null | undefined;
    worldToScreen: ((x: number, y: number) => { u: number; v: number }) | null;
  }) {
    const { cvJson, rallyIndex, playheadSec, focusedPlayerId, worldToScreen } =
      args;

    if (focusedPlayerId == null) return null;
    if (!worldToScreen) return null;
    if (!cvJson) return null;
    if (rallyIndex == null || rallyIndex < 0) return null;

    const session = cvJson.sessions?.[0];
    const rally = session?.rallies?.[rallyIndex];
    const frames = rally?.frames;

    if (!Array.isArray(frames) || frames.length === 0) return null;

    const fpsRaw = cvJson.camera?.fps;
    const fps =
      typeof fpsRaw === "number" && Number.isFinite(fpsRaw) && fpsRaw > 0
        ? fpsRaw
        : 29.97;

    const exactFrame = playheadSec * fps;

    const lo = Math.max(0, Math.floor(exactFrame));
    const hi = Math.min(frames.length - 1, Math.ceil(exactFrame));
    const t = Math.max(0, Math.min(1, exactFrame - lo));

    const loPos = frames[lo]?.player_court_positions?.[focusedPlayerId];
    const hiPos = frames[hi]?.player_court_positions?.[focusedPlayerId];

    if (!loPos || typeof loPos.x !== "number" || typeof loPos.y !== "number") {
      return null;
    }

    const x =
      hiPos && typeof hiPos.x === "number"
        ? lerp(loPos.x, hiPos.x, t)
        : loPos.x;

    const y =
      hiPos && typeof hiPos.y === "number"
        ? lerp(loPos.y, hiPos.y, t)
        : loPos.y;

    const { u, v } = worldToScreen(x, y);
    if (!Number.isFinite(u) || !Number.isFinite(v)) return null;

    return {
      u,
      v,
      team: focusedPlayerId <= 1 ? 0 : 1,
    };
  }

  // const focusedPlayerMarker = null;

  const focusedPlayerMarker = React.useMemo(() => {
    const fromCv = getFocusedPlayerMarkerFromCv({
      cvJson,
      rallyIndex: row?.rallyIndex ?? null,
      playheadSec: playheadSecUi,
      focusedPlayerId,
      insightsToCvPlayerIndex,
      worldToScreen,
    });

    if (fromCv) return fromCv;

    return getFocusedPlayerMarker({
      focusedPlayerId,
      activeOverlay: activeOverlayForFocusedPlayer,
      worldToScreen,
    });
  }, [
    cvJson,
    row?.rallyIndex,
    playheadSecUi,
    focusedPlayerId,
    insightsToCvPlayerIndex,
    activeOverlayForFocusedPlayer,
    worldToScreen,
  ]);

  const advantageScaleMarkers = React.useMemo(() => {
    if (!showAdvantageScaleMarkers) return [];
    return getAdvantageScaleMarkers({
      activeOverlay: activeOverlayForFocusedPlayer,
      worldToScreen,
    });
  }, [activeOverlayForFocusedPlayer, showAdvantageScaleMarkers, worldToScreen]);

  const terminalAdvantageOverlay = React.useMemo(() => {
    const overlays = row?.shotNumberOverlays ?? [];
    return overlays.length ? overlays[overlays.length - 1] : null;
  }, [row?.shotNumberOverlays]);

  const terminalAdvantagePlayerId =
    typeof terminalAdvantageOverlay?.playerId === "number"
      ? terminalAdvantageOverlay.playerId
      : null;
  const terminalAdvantageShotNumber = terminalAdvantageOverlay?.shotNumber ?? null;

  const askAi = React.useCallback(async () => {

    if (aiBusy) return;



    if (!row) return;
    if (!packageId) return;




    const q = (aiQuestionText || "").trim();
    if (!q) return;




    const prompt = `${q}`;

    const focus = computeClipFocus();
    if (!focus) return;

            setAiBusy(true);


    const turn: AiTurn = {
      id: mkTurnId(),
      createdAtMs: Date.now(),
      question: prompt,
      focus,
      status: "pending",
      contextLabel: props.aiContextLabel || "NA",
    };

    setTurns((cur) => [turn, ...cur]); // newest first

    // also persist into global index
    upsertGlobalTurn({
      ...turn,
      packageId,
      rallyIndex: row.rallyIndex,
    });

    try {
      const res = await fetch(ASK_AI_END_POINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          packageId,
          rallyIndex: row.rallyIndex,
          question: q,
          // IMPORTANT: backend expects segmentStartMs/segmentEndMs.
          // Here, we send the *focus range* (loop or full).
          segmentStartMs: focus.rangeStartMs,
          segmentEndMs: focus.rangeEndMs,
          // locator/framePolicy optional; omit for now
        }),
      });

      const json = await res.json().catch(() => ({}) as any);
      if (!res.ok) {
        const msg = String(json?.error ?? `HTTP ${res.status}`);
        throw new Error(msg);
      }

      // Prefer the backend's structured shape: { turn: { result: ... } }
      const extracted =
        json?.turn?.result ?? json?.turn?.analysis ?? json?.result ?? json;

      const responseText =
        typeof extracted?.answerToQuestion === "string"
          ? extracted.answerToQuestion
          : typeof json?.answer === "string"
            ? json.answer
            : undefined;

      setTurns((cur) =>
        cur.map((t) => {
          if (t.id !== turn.id) return t;
          const updated = {
            ...t,
            status: "done" as const,
            responseJson: extracted,
            responseText,
          };
          upsertGlobalTurn({
            ...(updated as AiTurn),
            packageId,
            rallyIndex: row.rallyIndex,
          });
          return updated;
        }),
      );

    } catch (err: any) {
      const msg = String(err?.message ?? err ?? "Unknown error");
      setTurns((cur) =>
        cur.map((t) => {
          if (t.id !== turn.id) return t;
          const updated = { ...t, status: "error" as const, error: msg, responseText: msg };
          upsertGlobalTurn({
            ...(updated as AiTurn),
            packageId,
            rallyIndex: row.rallyIndex,
          });
          return updated;
        }),
      );
      // setTurns((cur) =>
      //   cur.map((t) =>
      //     t.id === turn.id ? { ...t, status: "error", error: msg } : t,
      //   ),
      // );
    } finally {
      setAiBusy(false);
    }
  }, [
    aiBusy,
    row,
    packageId,
    aiQuestionText,
    computeClipFocus,
    ASK_AI_END_POINT,
    setTurns,
  ]);

  // -----------------------------
  // Render
  // -----------------------------
  // early return if no rally selected
  // if (!row) {
  //   return (
  //     <Box
  //       sx={{
  //         border: "1px solid rgba(255,255,255,0.12)",
  //         borderRadius: 2,
  //         p: 2,
  //       }}
  //     >
  //       <Typography sx={{ opacity: 0.75 }}>
  //         Select a rally to preview it.
  //       </Typography>
  //     </Box>
  //   );
  // }

  const begin = beginningScore
    ? `${beginningScore[0]}–${beginningScore[1]}`
    : null;
  const end = endingScore ? `${endingScore[0]}–${endingScore[1]}` : null;
  const changed =
    !!beginningScore &&
    !!endingScore &&
    (beginningScore[0] !== endingScore[0] ||
      beginningScore[1] !== endingScore[1]);

  const pointAwardedTeamFromDelta = (() => {
    if (!beginningScore || !endingScore) return null;
    const d0 = endingScore[0] - beginningScore[0];
    const d1 = endingScore[1] - beginningScore[1];
    if (d0 === 1 && d1 === 0) return 0;
    if (d1 === 1 && d0 === 0) return 1;
    return null;
  })();

  const pointAwardedTeam: 0 | 1 | null =
    row?.pointAwardedTeam === 0 || row?.pointAwardedTeam === 1
      ? row.pointAwardedTeam
      : pointAwardedTeamFromDelta;

  const dSec = durationSec ?? 0;
  const scrubTrackStartSec = loop.enabled ? loop.aSec : 0;
  const scrubTrackEndSec = loop.enabled ? loop.bSec : dSec;
  const scrubTrackDurationSec = Math.max(
    minLoopSec,
    scrubTrackEndSec - scrubTrackStartSec,
  );
  const aPct = loop.enabled || dSec <= 0 ? 0 : clamp(loop.aSec / dSec, 0, 1);
  const bPct = loop.enabled || dSec <= 0 ? 1 : clamp(loop.bSec / dSec, 0, 1);
  const scrubPlayPct =
    scrubTrackDurationSec > 0
      ? clamp((scrubPlayheadSecUi - scrubTrackStartSec) / scrubTrackDurationSec, 0, 1)
      : 0;

  const THUMB_W = 24;
  const THUMB_H = 28;
  const THUMB_GAP = 3;

  const aThumbLeftCss = `max(calc(${aPct * 100}% - ${THUMB_W + THUMB_GAP}px), 2px)`;
  const bThumbLeftCss = `min(calc(${bPct * 100}% + ${THUMB_GAP}px), calc(100% - ${THUMB_W + 2}px))`;

  const persistentExtraTextForOverlay = React.useCallback(
    (overlay: ShotNumberOverlay): string | undefined => {
      const existing = shotOverlayPersistentExtraText?.(overlay)?.trim();
      const trajectoryShape = classifyShotTrajectoryShape(overlay, row, cvJson);
      const parts = [existing, trajectoryShape].filter(Boolean) as string[];
      return parts.length ? parts.join(" • ") : undefined;
    },
    [cvJson, row, shotOverlayPersistentExtraText],
  );

  function leftSideCoverageTitleFromCaption(
    caption: string,
  ): string | null {
    const firstLine = caption
      .split(/\r?\n/)
      .map((line) => line.trim())
      .find(Boolean);
    if (!firstLine) return null;
    const normalized = firstLine
      .toLowerCase()
      .replace(/[’']/g, "'")
      .replace(/\s+/g, " ");
    if (normalized.includes("left-side didn't take it")) {
      return "Left-side didn't take it";
    }
    if (
      normalized.includes("left side didn't take it") ||
      normalized.includes("left-side did not take it") ||
      normalized.includes("left side did not take it")
    ) {
      return "Left-side didn't take it";
    }
    if (
      normalized.includes("left-side took it") ||
      normalized.includes("left side took it")
    ) {
      return "Left-side took it";
    }
    return null;
  }

  function buildClipExportPayload():
    | { clipId: string; body: Record<string, any> }
    | null {
    if (!row) return null;

    const focus = computeClipFocus();
    if (!focus) return null;

    // clipId just needs to be stable-ish for debug extraction
    const clipId = `rally_${row.rallyIndex + 1}`;

    // “ContextTimeline thing” is a stretch, so start with JSON context:
    // - include the selected row + a small window of rows around it
    const CONTEXT_WINDOW = 6;
    const center = row.rallyIndex;
    const contextRows = (rows || [])
      .filter((r) => Math.abs(r.rallyIndex - center) <= CONTEXT_WINDOW)
      .map((r) => ({
        rallyIndex: r.rallyIndex,
        videoUrl: r.videoUrl,
        // include whatever else is cheap + useful
        // (keep this minimal to avoid huge HTML)
        startSec: (r as any).startSec,
        endSec: (r as any).endSec,
        // if you have score info or tags on the row, include those too
        beginningScore: (r as any).beginningScore,
        endingScore: (r as any).endingScore,
      }));
    const focusStartSec = Math.max(
      0,
      (((focus as any).rangeStartMs ?? row.segmentStartMs) - row.segmentStartMs) /
        1000,
    );
    const focusEndSec = Math.max(
      focusStartSec,
      (((focus as any).rangeEndMs ?? row.segmentEndMs) - row.segmentStartMs) /
        1000,
    );
    const focusMidSec = (focusStartSec + focusEndSec) / 2;
    const rangeMatchedLensMoment =
      activeRallyLens === "leftSideCoverage"
        ? activeRallyLensMoments
            .filter((moment) => moment.lens === "leftSideCoverage")
            .filter(
              (moment) =>
                moment.timeSec >= focusStartSec - 0.25 &&
                moment.timeSec <= focusEndSec + 0.25,
            )
            .sort(
              (a, b) =>
                Math.abs(a.timeSec - focusMidSec) -
                Math.abs(b.timeSec - focusMidSec),
            )[0] ?? null
        : null;
    const socialLensMoment =
      activeRallyLens === "leftSideCoverage"
        ? rangeMatchedLensMoment ??
          (selectedRallyLensMomentKey
            ? activeRallyLensMoments.find(
                (moment) => moment.key === selectedRallyLensMomentKey,
              ) ?? null
            : null) ??
          currentRallyLensMoment ??
          nextRallyLensMoment
        : null;
    const socialLensTitleOverride =
      socialLensMoment?.lens === "leftSideCoverage"
        ? leftSideCoverageTitleFromCaption(socialExportCaption)
        : null;
    const socialLensShotPathTiming =
      socialLensMoment?.shotPaths?.find(
        (path) => path.shotNumber === socialLensMoment.shotNumber,
      ) ?? null;
    const socialLensShotPaths =
      socialLensMoment?.shotPaths
        ?.filter((path) => path.points.length >= 2)
        .filter((path) => {
          const startSec = path.startSec ?? 0;
          const endSec = path.endSec ?? startSec + 0.75;
          return endSec >= focusStartSec - 0.5 && startSec <= focusEndSec + 0.5;
        })
        .map((path) => {
          const startSec = path.startSec ?? focusStartSec;
          const endSec = path.endSec ?? startSec + 0.75;
          return {
            shotNumber: path.shotNumber,
            playerId: path.playerId,
            team: path.team,
            pathStartSec: startSec - focusStartSec,
            pathEndSec: Math.max(startSec + 0.25, endSec) - focusStartSec,
            points: path.points,
          };
        }) ?? [];
    const socialLensPathStartSec =
      socialLensShotPathTiming?.startSec != null
        ? socialLensShotPathTiming.startSec - focusStartSec
        : 0;
    const socialLensPathEndSec =
      socialLensShotPathTiming?.endSec != null
        ? Math.max(
            socialLensPathStartSec + 0.25,
            socialLensShotPathTiming.endSec - focusStartSec,
          )
        : socialLensPathStartSec + 0.75;
    const socialLensSideLabelForTeam = (
      team: 0 | 1 | null | undefined,
    ): string | null => {
      if (team !== 0 && team !== 1) return null;
      const player = socialLensMoment?.courtPlayers?.find((p) => p.team === team);
      if (!player || typeof player.y !== "number") return null;
      return player.y >= 22 ? "near" : "far";
    };
    const socialLensSideLabelForPlayer = (
      playerId: number | null | undefined,
    ): string | null => {
      if (typeof playerId !== "number") return null;
      const player = socialLensMoment?.courtPlayers?.find(
        (p) => p.playerId === playerId,
      );
      if (!player || typeof player.y !== "number") return null;
      return player.y >= 22 ? "near" : "far";
    };
    const socialLensOverlay =
      socialLensMoment && socialLensMoment.lens === "leftSideCoverage"
        ? {
            lens: socialLensMoment.lens,
            title: socialLensTitleOverride ?? socialLensMoment.title,
            rallyNumber: socialLensMoment.rallyNumber,
            shotNumber: socialLensMoment.shotNumber,
            reviewedPlayerId: socialLensMoment.reviewedPlayerId,
            hitterPlayerId: socialLensMoment.hitterPlayerId,
            partnerPlayerId: socialLensMoment.partnerPlayerId,
            reviewedTeam: socialLensMoment.reviewedTeam,
            rallyWinnerTeamId: socialLensMoment.rallyWinnerTeamId,
            partnerSideDistanceFt: socialLensMoment.partnerSideDistanceFt,
            reviewedTeamSide: socialLensSideLabelForPlayer(
              socialLensMoment.reviewedPlayerId,
            ),
            rallyWinnerTeamSide: socialLensSideLabelForTeam(
              socialLensMoment.rallyWinnerTeamId,
            ),
            shotPath: socialLensMoment.shotPath,
            shotPaths: socialLensShotPaths,
            pathStartSec: socialLensPathStartSec,
            pathEndSec: socialLensPathEndSec,
            courtPlayers: socialLensMoment.courtPlayers,
          }
        : null;

    //     type ReqBody = {
    //   packageId: string;
    //   rallyIndex: number;
    //   segmentStartMs: number;
    //   segmentEndMs: number;

    //   // OPTIONAL (future-proof): if you decide to send it from client later
    //   videoUrl?: string;

    //   // ✅ NEW: UI context for export rendering
    //   exportChips?: string[]; // pills
    //   activePick?: { context?: string; featureLabel?: string; id?: number | null } | null;
    //   activePickedLabel?: string | null;

    //   // ✅ NEW: shot overlay data
    //   exportShotOverlays?: any[] | null; // ShotNumberOverlay[] shape (you can tighten later)

    //   // ✅ NEW: allow sending current in-memory AI turns from the UI (preferred)
    //   aiTurns?: any[] | null;
    // };

    // const body = {
    //   packageId,
    //   rallyIndex: row.rallyIndex,
    //   clipId,
    //   segmentStartMs:         (focus as any).segmentStartMs != null
    //       ? focus.segmentStartMs
    //       : undefined,

    //   segmentEndMs:  (focus as any).segmentEndMs != null
    //       ? focus.segmentEndMs
    //       : undefined,
    //   videoUrl: row.videoUrl,
    //   exportShotOverlays: row.shotNumberOverlays ?? [],
    //   meta,
    //   // send the local AiTurn objects directly; server normalizes them now
    //   aiRuns: aiTurns,
    // };

    const ffSeqsForThisRally = (props.firefightsClips ?? [])
      .map((c) => c?.seq)
      .filter((s) => s && s.rallyIndex === row.rallyIndex);

    const exportShotOverlaysBase = (row.shotNumberOverlays ?? []).map(
      (ov: ShotNumberOverlay) => {
        const sn = typeof ov?.shotNumber === "number" ? ov.shotNumber : null;
        if (sn == null || ffSeqsForThisRally.length === 0) return ov;

        let inFirefight = false;
        for (const seq of ffSeqsForThisRally) {
          if (
            typeof seq?.startShotNumber === "number" &&
            typeof seq?.endShotNumber === "number" &&
            sn >= seq.startShotNumber &&
            sn <= seq.endShotNumber
          ) {
            inFirefight = true;
            break;
          }
        }

        if (!inFirefight) return ov;

        const sk = Array.isArray(ov.storyKinds) ? [...ov.storyKinds] : [];
        if (!sk.includes("firefight")) sk.push("firefight");
        return { ...ov, storyKinds: sk };
      },
    );

    const exportShotOverlays = exportShotOverlaysBase.map(
      (ov: ShotNumberOverlay) => {
        const shotNumber =
          typeof ov?.shotNumber === "number" ? ov.shotNumber : undefined;
        const playerId =
          typeof ov?.playerId === "number"
            ? ov.playerId
            : typeof (ov as any)?.player_id === "number"
              ? (ov as any).player_id
              : typeof (ov as any)?.rawShot?.player_id === "number"
                ? (ov as any).rawShot.player_id
                : undefined;

        const baseParts = [
          shotNumber != null ? `Shot ${shotNumber}` : null,
          playerId != null ? `p${playerId}` : null,
        ].filter(Boolean) as string[];

        const persistent = persistentExtraTextForOverlay(ov)?.trim();
        const extraParts: string[] = [];
        if (persistent) extraParts.push(persistent);

        // Turning-point labeling needs to be context-specific; do not add it
        // automatically just because the rally has a break shot.
        const isBreakShot = false;
        // const isBreakShot =
        //   typeof row.breakShotNumber === "number" &&
        //   shotNumber === row.breakShotNumber;
        // if (isBreakShot) extraParts.push("Turning Point");

        const currentIndex = exportShotOverlaysBase.findIndex(
          (candidate) => candidate?.shotNumber === ov?.shotNumber,
        );
        const previous =
          currentIndex > 0 ? exportShotOverlaysBase[currentIndex - 1] : null;
        const prevStartMs =
          typeof previous?.rawShot?.start_ms === "number"
            ? previous.rawShot.start_ms
            : null;
        const curStartMs =
          typeof ov?.rawShot?.start_ms === "number" ? ov.rawShot.start_ms : null;
        if (prevStartMs != null && curStartMs != null) {
          const reactionMs = curStartMs - prevStartMs;
          if (Number.isFinite(reactionMs) && reactionMs >= 0) {
            extraParts.push(
              `required pre-shot reaction ${(reactionMs / 1000).toFixed(2)}s`,
            );
          }
        }

        const mph = (ov as any)?.rawShot?.resulting_ball_movement?.speed;
        if (typeof mph === "number" && Number.isFinite(mph)) {
          extraParts.push(`${mph.toFixed(0)}mph`);
        }
        const isFastShot =
          typeof mph === "number" &&
          Number.isFinite(mph) &&
          mph >= FAST_SHOT_MPH_THRESHOLD;

        const dedupedExtraParts = Array.from(
          new Set(extraParts.filter(Boolean)),
        );
        const storyKinds = Array.isArray((ov as any).storyKinds)
          ? (ov as any).storyKinds
          : [];
        const isFirefight =
          storyKinds.some(
            (kind: unknown) =>
              typeof kind === "string" &&
              kind.toLowerCase().includes("firefight"),
          ) || ffSeqsForThisRally.some((seq) => {
            if (
              typeof seq?.startShotNumber !== "number" ||
              typeof seq?.endShotNumber !== "number" ||
              shotNumber == null
            ) {
              return false;
            }
            return (
              shotNumber >= seq.startShotNumber &&
              shotNumber <= seq.endShotNumber
            );
          });
        const focusIcon =
          focusedFilter && shotMatchesFocusedFilter(ov, focusedFilter)
            ? exportIconForFocusedReason(focusedFilter.reason)
            : undefined;
        const leftIcons = [
          ...(isFirefight ? ["🔥"] : []),
          ...(isFastShot ? ["🚀"] : []),
          ...(focusIcon ? [focusIcon] : []),
        ];
        const rightIcons = isFirefight ? ["🔥"] : [];

        const exportBadgeText = [...baseParts, ...dedupedExtraParts].join(" • ");
        return {
          ...ov,
          exportBadgeText: exportBadgeText || undefined,
          exportBadgeTone: isBreakShot ? "turningPoint" : undefined,
          exportBadgeIcons: leftIcons.length ? leftIcons : undefined,
          exportBadgeRightIcons: rightIcons.length ? rightIcons : undefined,
        };
      },
    );

    const meta = {
      packageId,
      rallyIndex: row.rallyIndex,
      mphThreshold: FAST_SHOT_MPH_THRESHOLD,
      fastMphCutoff: FAST_SHOT_MPH_THRESHOLD,
      aiContextLabel,
      activeShotNumber,
      beginningScore,
      endingScore,
      focus,
      loop: { ...loop },
      durationSec,
      // for server debug extraction (your export endpoint looks for these)
      // startSec:
      //   (focus as any).segmentStartMs != null
      //     ? focus.segmentStartMs / 1000
      //     : undefined,
      // endSec:
      //   (focus as any).segmentEndMs != null
      //     ? focus.segmentEndMs / 1000
      //     : undefined,

      startSec:
        (focus as any).segmentStartMs != null
          ? focus.segmentStartMs / 1000
          : undefined,
      endSec:
        (focus as any).segmentEndMs != null
          ? focus.segmentEndMs / 1000
          : undefined,

      contextRows,
    };

// console.error({ meta });

    const body = {
      packageId,

      // backend expects these at top-level
      rallyIndex: Number(row.rallyIndex),
      segmentStartMs: Number((focus as any).rangeStartMs), // segmentStartMs), //Number(row.segmentStartMs),
      segmentEndMs: Number((focus as any).rangeEndMs),
      // segmentEndMs: Number(row.segmentEndMs),

      // helps backend resolve disk file sometimes
      // videoUrl: row.videoUrl,
      videoUrl:
        row.videoUrl ?? (row as any).clipVideoUrl ?? (focus as any).videoUrl,

      // overlays + turns (correct keys)
      exportShotOverlays: exportShotOverlays, // row.shotNumberOverlays ?? [],
      aiTurns: aiTurns,

      // keep your meta (fine)
      meta,
      socialLensOverlay,

      // optional
      clipId,
    };
    return { clipId, body };
  }

  async function exportHtml() {
    const payload = buildClipExportPayload();
    if (!payload) return;

    const res = await fetch(EXPORT_HTML_END_POINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload.body),
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      console.warn("export html failed", res.status, txt);
      return;
    }

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${payload.clipId}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function exportSocialMp4() {
    if (socialExportBusy) return;

    const payload = buildClipExportPayload();
    if (!payload) return;
    const sourceBody = payload.body;
    const socialBody = {
      packageId: sourceBody.packageId,
      rallyIndex: sourceBody.rallyIndex,
      segmentStartMs: sourceBody.segmentStartMs,
      segmentEndMs: sourceBody.segmentEndMs,
      videoUrl: sourceBody.videoUrl,
      exportShotOverlays: slimShotOverlaysForSocialExport(
        sourceBody.exportShotOverlays,
      ),
      captionText: socialExportCaption.trim(),
      includeShotLabels: socialExportIncludeShotLabels,
      includeFullFrame: true,
      socialLensOverlay: sourceBody.socialLensOverlay,
      meta: sourceBody.meta,
    };

    setSocialExportBusy(true);
    setSocialExportError(null);

    try {
      const res = await fetch(EXPORT_SOCIAL_MP4_END_POINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(socialBody),
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `HTTP ${res.status}`);
      }

      const blob = await res.blob();
      const contentType = res.headers.get("content-type") ?? "";
      const isZip =
        contentType.includes("zip") ||
        res.headers.get("X-PGE-Export-Format") === "social-mp4-bundle";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = isZip
        ? `${payload.clipId}_social-export.zip`
        : `${payload.clipId}_vertical.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setSocialExportOpen(false);
    } catch (err: any) {
      console.warn("social mp4 export failed", err);
      setSocialExportError(String(err?.message ?? err ?? "Export failed"));
    } finally {
      setSocialExportBusy(false);
    }
  }

  const aiEntryButtonRef = React.useRef<HTMLButtonElement | null>(null);

  React.useLayoutEffect(() => {
    onAiButtonAnchorReady?.(showAi ? aiEntryButtonRef.current : null);

    return () => {
      onAiButtonAnchorReady?.(null);
    };
  }, [onAiButtonAnchorReady, showAi, aiOpen, row?.rallyIndex]);

  const toggleAiPanel = React.useCallback(() => {
    if (!props.showAi) return;
    setAiOpen((prev) => !prev);
  }, [props.showAi]);

  const closeAiPanel = React.useCallback(() => {
    setAiOpen(false);
  }, []);

  // const exportCurrentRallyToHtml = React.useCallback(async () => {
  //   if (!row || !packageId) return;

  //   try {
  // //    setExportBusy(true);

  //     const res = await fetch(EXPORT_HTML_END_POINT, {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify({
  //         packageId,
  //         rallyIndex: row.rallyIndex,
  //       }),
  //     });

  //     if (!res.ok) {
  //       throw new Error(`Export failed: ${res.status}`);
  //     }

  //     const blob = await res.blob();
  //     const url = window.URL.createObjectURL(blob);
  //     const a = document.createElement("a");
  //     a.href = url;
  //     a.download = `rally-${row.rallyIndex + 1}.html`;
  //     document.body.appendChild(a);
  //     a.click();
  //     a.remove();
  //     window.URL.revokeObjectURL(url);
  //   } finally {
  //     //setExportBusy(false);
  //   }
  // }, [row, packageId]);

  React.useImperativeHandle(
    ref,
    () => ({
      toggleAiPanel,
      closeAiPanel,
      toggleExportHtml: exportHtml, // exportCurrentRallyToHtml,
    }),
    [toggleAiPanel, closeAiPanel, exportHtml],
  );

  // -----------------------------
  // Render
  // -----------------------------
  // early return if no rally selected
  if (!row) {
    return (
      <Box
        sx={{
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 2,
          p: 2,
        }}
      >
        <Typography sx={{ opacity: 0.75 }}>
          Select a rally to preview it.
        </Typography>
      </Box>
    );
  }

  if (DEBUG_NO_VIDEO) {
    //  return ( <Box
    //     sx={{
    //       border: "1px solid rgba(255,255,255,0.12)",
    //       borderRadius: 2,
    //       p: 2,
    //     }}
    //   >
    //     <Typography sx={{ color:"white" }}>
    //       Video is disabled in this debug mode.
    //     </Typography>
    //   </Box>
    //  );

    return (
      <Box sx={{ width: "100%", height: "100%", bgcolor: "black" }}>
        <video
          ref={videoRef}
          src={row?.videoUrl}
          controls
          autoPlay={autoPlayOnRallyChange}
          preload={previewMode ? "metadata" : "auto"}
          muted
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      </Box>
    );
  }

  return (
    <Box
      className="active-rally-video-root"
      data-demo="active-rally-video-root"
      data-active-rally-number={row.rallyIndex + 1}
      data-active-rally-playhead-sec={playheadSecUi.toFixed(3)}
      data-active-rally-playback-shot-number={overlayLayerShotNumber ?? ""}
      data-active-rally-focused-shot-number={activeOverlayForFocusedPlayer?.shotNumber ?? ""}
      data-active-rally-trajectory-shot-number={activeOverlayForTrajectoryPanel?.shotNumber ?? ""}
      sx={{
        // border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 2,
        p: 1.2,
        color: fontColor,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        padding: "0 !important",
        ...DEMO_RECORDING_FOCUS_SUPPRESSION_SX,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.8 }}>
        {showTitle && (
          <Typography sx={{ fontWeight: "normal" }}>
            {showTitle && <>Rally {row.rallyIndex + 1}</>}
            {showScoreEtc && (
              <>
                {" • "}
                Score: {begin ?? "—"}
                {changed ? ` → ${end}` : ""}
              </>
            )}
          </Typography>
        )}

        {showScoreEtc && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            <Box
              data-team={row.servingTeam}
              sx={{ display: "flex", alignItems: "center", gap: 0.6 }}
            >
              <Typography>Serving</Typography>
              {row.servingTeam === 0 || row.servingTeam === 1 ? (
                <ServePaddleIcon
                  color={teamColor(row.servingTeam)}
                  width={24}
                />
              ) : (
                <ServePaddleIcon color={NEUTRAL} dim />
              )}
            </Box>

            {pointAwardedTeam != null ? (
              <Box
                data-team={pointAwardedTeam}
                sx={{ display: "flex", alignItems: "center", gap: 0.6 }}
              >
                <Typography>Point</Typography>
                <Box
                  sx={{
                    width: 11,
                    height: 11,
                    borderRadius: "3px",
                    bgcolor: teamColor(pointAwardedTeam),
                    border: "1px solid rgba(0,0,0,0.35)",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.18)",
                  }}
                />
              </Box>
            ) : (
              <Typography>No Point</Typography>
            )}

            <Typography>
              {row.numShots != null ? `${row.numShots} shots` : "shots: —"}
            </Typography>
            {showNextBallReadButton ? (
              <>
                <Button
                  size="small"
                  variant={nextBallReadButtonActive ? "contained" : "outlined"}
                  onClick={handleNextBallReadButtonClick}
                  disabled={!canStartNextBallRead}
                  data-demo={
                    isInFullPopup
                      ? "next-ball-reads-toggle"
                      : "next-ball-reads-inline-launch"
                  }
                  sx={{
                    minHeight: 26,
                    py: 0.1,
                    px: 1.1,
                    textTransform: "none",
                    fontWeight: 850,
                    borderColor: "rgba(255,255,255,0.34)",
                    color: "rgba(255,255,255,0.94)",
                    bgcolor: nextBallReadButtonActive
                      ? "rgba(25,118,210,0.86)"
                      : "rgba(255,255,255,0.06)",
                    "&:hover": {
                      bgcolor: nextBallReadButtonActive
                        ? "rgba(30,136,229,0.96)"
                        : "rgba(255,255,255,0.12)",
                      borderColor: "rgba(255,255,255,0.50)",
                    },
                    "&.Mui-disabled": {
                      color: "rgba(255,255,255,0.30)",
                      borderColor: "rgba(255,255,255,0.14)",
                    },
                  }}
                >
                  {nextBallReadButtonActive ? "Exit Reads" : "Next Ball Reads"}
                </Button>
                {isInFullPopup ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={startRandomKitchenRead}
                    disabled={!canStartRandomKitchenRead}
                    data-demo="next-ball-reads-random-start"
                    sx={{
                      minHeight: 26,
                      py: 0.1,
                      px: 1.1,
                      textTransform: "none",
                      fontWeight: 850,
                      borderColor: "rgba(129,212,250,0.56)",
                      color: "rgba(179,229,252,0.94)",
                      bgcolor: "rgba(2,136,209,0.12)",
                      "&:hover": {
                        borderColor: "rgba(179,229,252,0.92)",
                        bgcolor: "rgba(2,136,209,0.22)",
                      },
                      "&.Mui-disabled": {
                        color: "rgba(255,255,255,0.30)",
                        borderColor: "rgba(255,255,255,0.14)",
                      },
                    }}
                  >
                    Random Read
                  </Button>
                ) : null}
              </>
            ) : null}
            {showShotSpaceButton && onOpenShotSpace ? (
              <Button
                size="small"
                variant="outlined"
                onClick={onOpenShotSpace}
                data-happy-path="shot-target-lab-open"
                sx={{
                  minHeight: 26,
                  py: 0.1,
                  px: 1.1,
                  textTransform: "none",
                  fontWeight: 850,
                  borderColor: "rgba(25,118,210,0.75)",
                  color: "#64b5f6",
                  bgcolor: "rgba(25,118,210,0.10)",
                  "&:hover": {
                    borderColor: "rgba(100,181,246,0.95)",
                    bgcolor: "rgba(25,118,210,0.18)",
                  },
                }}
              >
                Shot Target Lab
              </Button>
            ) : null}
            {onBookmarkShot ? (
              <Button
                size="small"
                variant="outlined"
                onClick={handleBookmarkCurrentShot}
                data-demo="bookmark-current-shot"
                sx={{
                  minHeight: 26,
                  py: 0.1,
                  px: 1.1,
                  textTransform: "none",
                  fontWeight: 850,
                  borderColor: "rgba(255,255,255,0.34)",
                  color: "rgba(255,255,255,0.86)",
                  bgcolor: "rgba(255,255,255,0.06)",
                }}
              >
                Bookmark
              </Button>
            ) : null}
            {showShotBookmarksButton && onOpenShotBookmarks ? (
              <Button
                size="small"
                variant="outlined"
                onClick={onOpenShotBookmarks}
                data-demo="open-shot-bookmarks"
                sx={{
                  minHeight: 26,
                  py: 0.1,
                  px: 1.1,
                  textTransform: "none",
                  fontWeight: 850,
                  borderColor: "rgba(255,255,255,0.34)",
                  color: "rgba(255,255,255,0.86)",
                  bgcolor: "rgba(255,255,255,0.06)",
                }}
              >
                Bookmarks{shotBookmarksCount ? ` (${shotBookmarksCount})` : ""}
              </Button>
            ) : null}
            {showRallyLensControls && rallyLensMoments.length > 0 ? (
              <ToggleButtonGroup
                size="small"
                exclusive
                value={activeRallyLens}
                onChange={(_, nextLens: ActiveRallyLensKey | null) => {
                  if (!nextLens) return;
                  setActiveRallyLens(nextLens);
                }}
                data-demo="active-rally-lens-toggle"
                sx={{
                  height: 28,
                  bgcolor: "rgba(255,255,255,0.045)",
                  borderRadius: 1.5,
                  "& .MuiToggleButton-root": {
                    px: 0.75,
                    py: 0,
                    minWidth: 0,
                    color: "rgba(255,255,255,0.72)",
                    borderColor: "rgba(255,255,255,0.16)",
                    textTransform: "none",
                    fontSize: 11,
                    fontWeight: 850,
                    "&.Mui-selected": {
                      color: "white",
                      bgcolor: "rgba(41,182,246,0.34)",
                    },
                    "&.Mui-selected:hover": {
                      bgcolor: "rgba(41,182,246,0.44)",
                    },
                  },
                }}
              >
                <ToggleButton value="off" title="Turn off rally lenses">
                  Lens: Off
                </ToggleButton>
                {(
                  [
                    "candidateSpeedups",
                    "possiblePopups",
                    "paddleReadiness",
                    "leftSideCoverage",
                  ] as const
                ).map((lens) => {
                  const count = rallyLensCounts[lens];
                  return (
                    <ToggleButton
                      key={lens}
                      value={lens}
                      disabled={count === 0}
                      title={`${activeRallyLensLongLabel(
                        lens,
                      )}: ${activeRallyLensCountLabel(
                        count,
                      )} in this rally`}
                    >
                      {activeRallyLensLabel(lens)} (
                      {activeRallyLensCountLabel(count)})
                    </ToggleButton>
                  );
                })}
              </ToggleButtonGroup>
            ) : null}
          </Box>
        )}
      </Box>

      {showCondensedTimeline && (
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          {props.condensedTimelineLeftEl}
          <Box>
            <CondensedRallyTimeline_ContextOnly
              activeRally={row}
              rows={rows}
              barsAreaHeightPx={BAR_HEIGHT_CONDENSED_TIME}
              showLegend={true}
              showRallyIndex={true}
              showWinnerAndServe={true}
              firefightRallySet={firefightRallySet}
            />
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          gap: 1.2,
          alignItems: "stretch",
          flex: "1 1 auto",
          minHeight: 0,
          minWidth: 0,
        }}
      >
        <Box
          ref={videoOverlayRef}
          sx={{
            position: "relative",
            flex: "1 1 0",
            minWidth: 0,
            minHeight: 0,
            // overflow: "visible", // allow AI drawer to extend to the right
            overflow: aiDrawerMode === "overlay" ? "visible" : "hidden",
          }}
        >
          <Box
            sx={{
              minHeight: 0,
              position: "relative",
              width: "100%",
              height: "100%",
              pb: stableVideoStage ? 0 : `${TIMELINE_WHITE_TICKS_H}px`,
              contain: stableVideoStage ? "layout paint size" : undefined,
            }}
          >
            {controlsPortalContainer && videoOverlayControls
              ? createPortal(videoOverlayControls, controlsPortalContainer)
              : videoOverlayControls}

            <video
              ref={videoRef}
              src={row.videoUrl}
              controls={false}
              muted={forceMuted}
              autoPlay={autoPlayOnRallyChange}
              preload={previewMode ? "metadata" : "auto"}
              // loop={true}
              playsInline
              style={{
                width: "100%",
                height: "100%",
                maxHeight: "100%",
                position: stableVideoStage ? "absolute" : undefined,
                inset: stableVideoStage ? 0 : undefined,
                zIndex: stableVideoStage ? 0 : undefined,
                display: "block",
                borderRadius: 10,
                objectFit: "contain",
                backgroundColor: "black",
              }}
            />

            {isInFullPopup && nextBallRead ? (
              <Box
                data-demo="next-ball-reads-overlay"
                data-stage={nextBallRead.stage}
                data-shot-number={nextBallRead.rep.shotNumber}
                data-shot-sec={nextBallRead.rep.shotSec.toFixed(3)}
                data-next-shot-sec={
                  nextBallRead.rep.nextShotSec != null
                    ? nextBallRead.rep.nextShotSec.toFixed(3)
                    : ""
                }
                data-read-sec={nextBallRead.rep.readSec.toFixed(3)}
                data-reveal-end-sec={nextBallRead.rep.revealEndSec.toFixed(3)}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
                sx={{
                  position: "absolute",
                  left: nextBallReadUseTopPlacement ? 18 : 10,
                  top: nextBallReadUseTopPlacement ? 56 : "auto",
                  bottom: nextBallReadUseTopPlacement ? "auto" : 48,
                  zIndex: 155,
                  width: nextBallReadUseTopPlacement
                    ? "min(390px, calc(100% - 36px))"
                    : "min(420px, calc(100% - 20px))",
                  borderRadius: 1.5,
                  border: "1px solid rgba(255,255,255,0.18)",
                  bgcolor: "rgba(8,12,16,0.72)",
                  color: "white",
                  backdropFilter: "blur(8px)",
                  boxShadow: "0 12px 36px rgba(0,0,0,0.34)",
                  px: 1.2,
                  py: 1,
                  pointerEvents: "auto",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 0.8,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: isInFullPopup ? 16 : 14,
                        fontWeight: 900,
                        lineHeight: 1.1,
                      }}
                    >
                      Read the next ball
                    </Typography>
                    <Typography
                      data-demo="next-ball-reads-meta-label"
                      sx={{
                        mt: 0.25,
                        fontSize: isInFullPopup ? 12 : 11,
                        color: "rgba(255,255,255,0.68)",
                        lineHeight: 1.2,
                      }}
                    >
                      {nextBallRead.rep.source === "random-kitchen"
                        ? (
                          <Box component="span" data-demo="next-ball-reads-source-label">
                            Random kitchen read ·{" "}
                          </Box>
                        )
                        : ""}
                      {nextBallRead.rep.rallyIndex != null ? (
                        <>
                          <Box
                            component="span"
                            data-demo="next-ball-reads-rally-label"
                            data-rally-number={nextBallRead.rep.rallyIndex + 1}
                          >
                            Rally {nextBallRead.rep.rallyIndex + 1}
                          </Box>
                          {" · "}
                        </>
                      ) : null}
                      Shot {nextBallRead.rep.shotNumber}
                      {nextBallRead.rep.shotType ? ` · ${nextBallRead.rep.shotType}` : ""}
                      {" · "}
                      {nextBallRead.rep.overlayIndex + 1}/{nextBallRead.rep.total}
                    </Typography>
                  </Box>
                  <Tooltip
                    title={isDemoRun ? "" : "Exit Next Ball Reads"}
                    placement="top"
                    disableHoverListener={isDemoRun}
                    disableFocusListener={isDemoRun}
                    disableTouchListener={isDemoRun}
                  >
                    <IconButton
                      size="small"
                      onClick={exitNextBallRead}
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: 1.2,
                        color: "rgba(255,255,255,0.84)",
                        bgcolor: "rgba(255,255,255,0.06)",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: 0.7,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <Tooltip
                    title={
                      isDemoRun
                        ? ""
                        : nextBallRead.stage === "revealed"
                          ? "Replay from the read frame through the actual next ball."
                          : "Play forward from the read pause so you can see where the ball actually went."
                    }
                    placement="top"
                    disableHoverListener={isDemoRun}
                    disableFocusListener={isDemoRun}
                    disableTouchListener={isDemoRun}
                    slotProps={{
                      popper: {
                        sx: { zIndex: 30000 },
                      },
                    }}
                  >
                    <span>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={revealNextBallRead}
                        disabled={nextBallRead.stage === "revealing"}
                        data-demo="next-ball-reads-reveal"
                        sx={{
                          minHeight: 28,
                          px: 1.4,
                          textTransform: "none",
                          fontWeight: 900,
                          bgcolor: "rgba(25,118,210,0.94)",
                          color: "rgba(255,255,255,0.98)",
                          border: "1px solid rgba(187,222,251,0.72)",
                          boxShadow:
                            "0 1px 0 rgba(255,255,255,0.26) inset, 0 2px 8px rgba(0,0,0,0.24)",
                          "&:hover": { bgcolor: "rgba(30,136,229,0.98)" },
                          "&.Mui-disabled": {
                            color: "rgba(255,255,255,0.72)",
                            bgcolor: "rgba(21,101,192,0.64)",
                            borderColor: "rgba(187,222,251,0.36)",
                          },
                        }}
                      >
                        {nextBallRead.stage === "revealing"
                          ? "Revealing..."
                          : nextBallRead.stage === "revealed"
                            ? "Replay Reveal"
                            : "Reveal"}
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      isDemoRun
                        ? ""
                        : "Return to the paused read frame so you can study the cues again."
                    }
                    placement="top"
                    disableHoverListener={isDemoRun}
                    disableFocusListener={isDemoRun}
                    disableTouchListener={isDemoRun}
                    slotProps={{
                      popper: {
                        sx: { zIndex: 30000 },
                      },
                    }}
                  >
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={watchNextBallReadAgain}
                        data-demo="next-ball-reads-watch-again"
                        sx={{
                          minHeight: 28,
                          px: 1.15,
                          textTransform: "none",
                          fontWeight: 900,
                          color: "rgba(255,255,255,0.96)",
                          borderColor: "rgba(129,212,250,0.72)",
                          bgcolor: "rgba(2,136,209,0.74)",
                          boxShadow: "0 0 0 1px rgba(255,255,255,0.10)",
                          "&:hover": {
                            borderColor: "rgba(179,229,252,0.96)",
                            bgcolor: "rgba(2,136,209,0.92)",
                          },
                        }}
                      >
                        Watch Again
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={goToPrevBallReadRep}
                    data-demo="next-ball-reads-prev-rep"
                    sx={{
                      minHeight: 28,
                      px: 0.9,
                      textTransform: "none",
                      fontWeight: 850,
                      color: "rgba(255,255,255,0.86)",
                      borderColor: "rgba(255,255,255,0.30)",
                      bgcolor: "rgba(255,255,255,0.05)",
                    }}
                  >
                    Prev Shot
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={goToNextBallReadRep}
                    data-demo="next-ball-reads-next-rep"
                    sx={{
                      minHeight: 28,
                      px: 0.9,
                      textTransform: "none",
                      fontWeight: 850,
                      color: "rgba(255,255,255,0.86)",
                      borderColor: "rgba(255,255,255,0.30)",
                      bgcolor: "rgba(255,255,255,0.05)",
                    }}
                  >
                    Next Shot
                  </Button>
                  <Tooltip
                    title={
                      isDemoRun
                        ? ""
                        : "Jump to a random later-rally kitchen-style shot from the match."
                    }
                    placement="top"
                    disableHoverListener={isDemoRun}
                    disableFocusListener={isDemoRun}
                    disableTouchListener={isDemoRun}
                    slotProps={{
                      popper: {
                        sx: { zIndex: 30000 },
                      },
                    }}
                  >
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={startRandomKitchenRead}
                        disabled={!canStartRandomKitchenRead}
                        data-demo="next-ball-reads-random-next"
                        sx={{
                          minHeight: 28,
                          px: 0.9,
                          textTransform: "none",
                          fontWeight: 850,
                          color: "rgba(179,229,252,0.96)",
                          borderColor: "rgba(129,212,250,0.62)",
                          bgcolor: "rgba(2,136,209,0.16)",
                          "&:hover": {
                            borderColor: "rgba(179,229,252,0.96)",
                            bgcolor: "rgba(2,136,209,0.26)",
                          },
                          "&.Mui-disabled": {
                            color: "rgba(255,255,255,0.28)",
                            borderColor: "rgba(255,255,255,0.14)",
                          },
                        }}
                      >
                        Random Next
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            ) : null}

            {showRallyLensPanel && (
              <ActiveRallyLensPanel
                lens={activeRallyLens}
                moments={activeRallyLensMoments}
                currentMoment={currentRallyLensMoment}
                nextMoment={nextRallyLensMoment}
                selectedMomentKey={selectedRallyLensMomentKey}
                onSeek={seekToRallyLensMoment}
                isInFullPopup={isInFullPopup}
                placement={rallyLensPanelPlacement}
                playheadSec={playheadSecUi}
                insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                renderPlayerIdentity={renderPlayerIdentity}
              />
            )}

            {showTrajectoryShapePanel && activeOverlayForTrajectoryPanel ? (
              <ShotTrajectoryShapePanel
                overlay={activeOverlayForTrajectoryPanel}
                row={row}
                cvJson={cvJson}
              />
            ) : null}

            {activeShotTrajectoryHighlight?.visible ? (
              <svg
                data-demo="active-shot-trajectory-highlight"
                data-source-rally-index={row.rallyIndex}
                data-cv-rally-index={activeShotTrajectoryHighlight.cvRallyIndex}
                data-start-frame-offset={activeShotTrajectoryHighlight.startFrameOffset}
                data-end-frame-offset={activeShotTrajectoryHighlight.endFrameOffset}
                data-path-source={activeShotTrajectoryHighlight.pathSource}
                viewBox="0 0 1 1"
                preserveAspectRatio="none"
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  pointerEvents: "none",
                  zIndex: 14,
                }}
              >
                <polyline
                  points={activeShotTrajectoryHighlight.points}
                  fill="none"
                  stroke="rgba(0,0,0,0.72)"
                  strokeWidth={0.008}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <polyline
                  points={activeShotTrajectoryHighlight.points}
                  fill="none"
                  stroke="rgba(255,214,64,0.96)"
                  strokeWidth={0.0035}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle
                  cx={activeShotTrajectoryHighlight.start.u}
                  cy={activeShotTrajectoryHighlight.start.v}
                  r={0.0045}
                  fill="rgba(255,214,64,0.98)"
                  stroke="rgba(0,0,0,0.78)"
                  strokeWidth={0.002}
                />
                <circle
                  cx={activeShotTrajectoryHighlight.end.u}
                  cy={activeShotTrajectoryHighlight.end.v}
                  r={0.0055}
                  fill="rgba(255,255,255,0.98)"
                  stroke="rgba(255,214,64,0.98)"
                  strokeWidth={0.0025}
                />
              </svg>
            ) : null}

            {focusedPlayerMarker && (
              <>
                <svg
                  viewBox="0 0 1 1"
                  preserveAspectRatio="none"
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    display: "block",
                    pointerEvents: "none",
                    zIndex: 12,
                  }}
                >
                  <circle
                    cx={focusedPlayerMarker.u}
                    cy={focusedPlayerMarker.v}
                    r={isInFullPopup ? 0.0108 : 0.0096}
                    fill={
                      focusedPlayerMarker.team === 0
                        ? "rgba(90,170,255,0.62)"
                        : "rgba(90,235,150,0.62)"
                    }
                    stroke="rgba(255,255,255,0.42)"
                    strokeWidth={0.0022}
                  />
                </svg>
              </>
            )}

            {advantageScaleMarkers.length > 0 ? (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                  zIndex: 13,
                }}
              >
                {advantageScaleMarkers.map((marker) => (
                  <Box
                    key={`adv-${marker.playerId}`}
                    sx={{
                      position: "absolute",
                      left: `${marker.u * 100}%`,
                      top: `${marker.v * 100}%`,
                      transform: "translate(-50%, calc(-100% - 10px))",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: isInFullPopup ? 42 : 34,
                        height:
                          (isInFullPopup ? 18 : 15) *
                          advantageBadgeScale(marker.value),
                        borderRadius: 999,
                        bgcolor:
                          marker.lagging
                            ? "rgba(255,180,60,0.90)"
                            : marker.team === 0
                              ? "rgba(65,150,245,0.82)"
                              : "rgba(60,205,120,0.82)",
                        color: "rgba(255,255,255,0.98)",
                        border: marker.lagging
                          ? "2px solid rgba(255,245,190,0.98)"
                          : "1px solid rgba(255,255,255,0.52)",
                        boxShadow: marker.lagging
                          ? "0 0 0 3px rgba(255,190,85,0.30), 0 0 20px rgba(255,190,85,0.46), 0 4px 12px rgba(0,0,0,0.34)"
                          : "0 4px 12px rgba(0,0,0,0.34)",
                        fontSize: isInFullPopup ? 12 : 10.5,
                        fontWeight: 900,
                        lineHeight: 1,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        fontVariantNumeric: "tabular-nums",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatAdvantageValue(marker.value)}
                    </Box>
                    {marker.lagging ? (
                      <Box
                        component="span"
                        sx={{
                          mt: 0.3,
                          px: 0.45,
                          py: 0.05,
                          borderRadius: 999,
                          bgcolor: "rgba(20,20,20,0.72)",
                          border: "1px solid rgba(255,190,85,0.78)",
                          color: "rgba(255,220,135,0.98)",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.28)",
                          fontSize: isInFullPopup ? 9.5 : 8.5,
                          fontWeight: 900,
                          lineHeight: 1.15,
                          letterSpacing: 0,
                        }}
                      >
                        LAG
                      </Box>
                    ) : null}
                    {showAdvantageScaleSparklines ? (
                      <AdvantageScaleMiniSparkline
                        series={getAdvantageSeriesForPlayer(row, marker.playerId)}
                        currentShotNumber={
                          activeOverlayForFocusedPlayer?.shotNumber ?? null
                        }
                        team={marker.team}
                        lagShotNumbers={getAdvantageLagShotNumbersForPlayer(
                          row,
                          marker.playerId,
                        )}
                        terminalShotNumber={terminalAdvantageShotNumber}
                        terminalPlayer={terminalAdvantagePlayerId === marker.playerId}
                      />
                    ) : null}
                  </Box>
                ))}
              </Box>
            ) : null}

            {(focusedPlayerMarker || focusedPlayerGhostTrail.length > 0) && (
              <svg
                viewBox="0 0 1 1"
                preserveAspectRatio="none"
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  display: "block",
                  pointerEvents: "none",
                  zIndex: 12,
                }}
              >
                {FOCUSED_PLAYER_GHOST_TRAIL.showPath &&
                  focusedPlayerGhostTrail.length >= 2 && (
                    <polyline
                      points={[...focusedPlayerGhostTrail]
                        .reverse()
                        .map((p) => `${p.u},${p.v}`)
                        .join(" ")}
                      fill="none"
                      stroke={
                        focusedPlayerGhostTrail[0]?.team === 0
                          ? `rgba(90,170,255,${FOCUSED_PLAYER_GHOST_TRAIL.pathOpacity})`
                          : `rgba(90,235,150,${FOCUSED_PLAYER_GHOST_TRAIL.pathOpacity})`
                      }
                      strokeWidth={FOCUSED_PLAYER_GHOST_TRAIL.pathWidth}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  )}

                {focusedPlayerGhostTrail.map((p, idx) => {
                  const n = Math.max(1, focusedPlayerGhostTrail.length - 1);
                  const t = n === 0 ? 0 : idx / n;

                  const rStart = isInFullPopup
                    ? FOCUSED_PLAYER_GHOST_TRAIL.ghostRadiusStartFullPopup
                    : FOCUSED_PLAYER_GHOST_TRAIL.ghostRadiusStartInline;

                  const rEnd = isInFullPopup
                    ? FOCUSED_PLAYER_GHOST_TRAIL.ghostRadiusEndFullPopup
                    : FOCUSED_PLAYER_GHOST_TRAIL.ghostRadiusEndInline;

                  const radius = lerpRange(rStart, rEnd, t);
                  const fillOpacity = lerpRange(
                    FOCUSED_PLAYER_GHOST_TRAIL.ghostOpacityStart,
                    FOCUSED_PLAYER_GHOST_TRAIL.ghostOpacityEnd,
                    t,
                  );
                  const strokeOpacity = lerpRange(
                    FOCUSED_PLAYER_GHOST_TRAIL.ghostStrokeOpacityStart,
                    FOCUSED_PLAYER_GHOST_TRAIL.ghostStrokeOpacityEnd,
                    t,
                  );

                  // const fill =
                  //   p.team === 0
                  //     ? `rgba(90,170,255,${fillOpacity})`
                  //     : `rgba(90,235,150,${fillOpacity})`;

                  const fill =
                    p.team === 0
                      ? `rgba(120,185,255,${fillOpacity})`
                      : `rgba(120,225,165,${fillOpacity})`;

                  let stroke = `rgba(255,255,255,${strokeOpacity})`;
                  let strokeWidth = FOCUSED_PLAYER_GHOST_TRAIL.ghostStrokeWidth;

                  if (
                    FOCUSED_PLAYER_GHOST_TRAIL.highlightFastMovement &&
                    typeof p.speedMph === "number"
                  ) {
                    if (
                      p.speedMph >=
                      FOCUSED_PLAYER_GHOST_TRAIL.veryFastMovementMphThreshold
                    ) {
                      stroke =
                        FOCUSED_PLAYER_GHOST_TRAIL.veryFastMovementStroke;
                      strokeWidth =
                        FOCUSED_PLAYER_GHOST_TRAIL.ghostStrokeWidth *
                        FOCUSED_PLAYER_GHOST_TRAIL.veryFastMovementStrokeWidthMultiplier;
                    } else if (
                      p.speedMph >=
                      FOCUSED_PLAYER_GHOST_TRAIL.fastMovementMphThreshold
                    ) {
                      stroke = FOCUSED_PLAYER_GHOST_TRAIL.fastMovementStroke;
                      strokeWidth =
                        FOCUSED_PLAYER_GHOST_TRAIL.ghostStrokeWidth *
                        FOCUSED_PLAYER_GHOST_TRAIL.fastMovementStrokeWidthMultiplier;
                    }
                  }

                  return (
                    <circle
                      key={`${idx}-${p.ageIndex}`}
                      cx={p.u}
                      cy={p.v}
                      r={radius}
                      fill={fill}
                      stroke={stroke}
                      strokeWidth={strokeWidth}
                    />
                  );
                })}

                {focusedPlayerMarker && (
                  <circle
                    cx={focusedPlayerMarker.u}
                    cy={focusedPlayerMarker.v}
                    r={
                      isInFullPopup
                        ? FOCUSED_PLAYER_GHOST_TRAIL.currentRadiusFullPopup
                        : FOCUSED_PLAYER_GHOST_TRAIL.currentRadiusInline
                    }
                    // fill={
                    //   focusedPlayerMarker.team === 0
                    //     ? "rgba(90,170,255,0.95)"
                    //     : "rgba(90,235,150,0.95)"
                    // }
                    // stroke="rgba(255,255,255,0.96)"
                    // strokeWidth={0.0035}
                    //                   <circle
                    // cx={focusedPlayerMarker.u}
                    // cy={focusedPlayerMarker.v}
                    // r={isInFullPopup ? 0.0125 : 0.011}
                    // fill={
                    //   focusedPlayerMarker.team === 0
                    //     ? "rgba(90,170,255,0.95)"
                    //     : "rgba(90,235,150,0.95)"
                    // }
                    // stroke="rgba(255,255,255,0.96)"
                    // strokeWidth={0.0035}
                    // r={isInFullPopup ? 0.0108 : 0.0096}
                    fill={
                      focusedPlayerMarker.team === 0
                        ? "rgba(90,170,255,0.62)"
                        : "rgba(90,235,150,0.62)"
                    }
                    stroke="rgba(255,255,255,0.42)"
                    strokeWidth={0.0022}
                  />
                )}
              </svg>
            )}

            {anyPounceOverlayVisible && pounceOverlay && (
              <svg
                viewBox="0 0 1 1"
                preserveAspectRatio="none"
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  display: "block",
                  pointerEvents: "none",
                  zIndex: 13,
                }}
              >
                {/* {staticPounceTrajectorySvg} */}
                {/* {staticPounceMarkersSvg} */}

                {pounceCatPlacement &&
                  (pounceOverlay?.focusedPlayerId === 2 ||
                    pounceOverlay?.focusedPlayerId === 3) && (
                    <>
                      <defs>
                        <clipPath
                          id={pounceCatClipId}
                          clipPathUnits="objectBoundingBox"
                        >
                          <rect
                            x="0"
                            y="0"
                            width="1"
                            height="1"
                            rx="0.12"
                            ry="0.12"
                          />
                        </clipPath>
                      </defs>

                      <g
                        transform={`translate(${pounceCatPlacement.x} ${pounceCatPlacement.y}) rotate(${pounceCatPlacement.angleDeg} ${pounceCatPlacement.width / 2} ${pounceCatPlacement.height / 2})`}
                        pointerEvents="none"
                        opacity={0.6}
                      >
                        <image
                          href="/pouncecat_7_export.jpg"
                          x={0}
                          y={0}
                          width={pounceCatPlacement.width}
                          height={pounceCatPlacement.height}
                          preserveAspectRatio="xMidYMid slice"
                          clipPath={`url(#${pounceCatClipId})`}
                        />

                        <rect
                          x={0}
                          y={0}
                          width={pounceCatPlacement.width}
                          height={pounceCatPlacement.height}
                          rx={pounceCatPlacement.width * 0.12}
                          ry={pounceCatPlacement.height * 0.12}
                          fill="none"
                          stroke="#ffffff"
                          strokeOpacity={0}
                          strokeWidth={0.0025}
                        />
                      </g>
                    </>
                  )}
                {requiredMovementVisible &&
                  requiredMovementLanePolygonPoints && (
                    <g>
                      <polygon
                        points={requiredMovementLanePolygonPoints}
                        fill={teamRgba(
                          teamOfPlayerId(
                            pounceOverlay.focusedPlayerId,
                          ) as Team01,
                          0.4,
                        )} //  "rgba(255, 220, 90, 0.22)"
                        stroke={teamRgba(
                          teamOfPlayerId(
                            pounceOverlay.focusedPlayerId,
                          ) as Team01,
                          0.5,
                        )} //"rgba(255, 235, 140, 0.52)"
                        strokeWidth={isInFullPopup ? 0.0028 : 0.0024}
                        strokeLinejoin="round"
                      />

                      <circle
                        cx={requiredMovementStartScreen!.u}
                        cy={requiredMovementStartScreen!.v}
                        r={0.0001} //isInFullPopup ? 0.0105 : 0.0095}
                        fill={teamRgba(
                          teamOfPlayerId(
                            pounceOverlay.focusedPlayerId,
                          ) as Team01,
                        )} //"rgba(120,255,190,0.88)"
                        stroke="rgba(255,255,255,0.70)"
                        strokeWidth={isInFullPopup ? 0.0036 : 0.003}
                      />

                      <circle
                        cx={requiredMovementEndScreen!.u}
                        cy={requiredMovementEndScreen!.v}
                        r={isInFullPopup ? 0.013 : 0.0115}
                        fill="rgba(0,0,0,0)"
                        //stroke="rgba(255,225,110,0.98)"
                        // stroke="rgba(255,0,0,0.48)"
                        // strokeWidth={isInFullPopup ? 0.0052 : 0.0045}
                        // strokeDasharray={pounceWasTaken ? undefined : "0.006 0.006"}
                        // fill="rgba(0,0,0,0)"
                        stroke="red" //"rgba(196,92,92,1)"
                        strokeWidth={isInFullPopup ? 0.0028 : 0.0028}
                        strokeDasharray={
                          isInFullPopup ? "0.010 0.007" : "0.009 0.006"
                        }
                      />

                      {requiredMovementLabelUv && showCourtLabels && (
                        <>
                          <rect
                            x={requiredMovementLabelUv.u - 0.05}
                            y={requiredMovementLabelUv.v - 0.032}
                            width={0.1}
                            height={0.024}
                            rx={0.006}
                            fill="rgba(0,0,0,0.68)"
                          />
                          <text
                            x={requiredMovementLabelUv.u}
                            y={requiredMovementLabelUv.v - 0.019}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fill="white"
                            fontSize={isInFullPopup ? "0.0145" : "0.0135"}
                            fontWeight="700"
                          >
                            {requiredMovementDistanceLabel}
                          </text>
                        </>
                      )}
                    </g>
                  )}

                {pounceWindowVisible &&
                  playerAtOpponentImpactScreen &&
                  bestAttackScreen && (
                    <>
                      {/* <line
                        x1={playerAtOpponentImpactScreen.u}
                        y1={playerAtOpponentImpactScreen.v}
                        x2={bestAttackScreen.u}
                        y2={bestAttackScreen.v}
                        stroke="rgba(110,225,255,0.92)"
                        strokeWidth={isInFullPopup ? 0.0044 : 0.0038}
                        strokeDasharray="0.012 0.010"
                        strokeLinecap="round"
                      /> */}

                      {showCourtLabels && (
                        <rect
                          x={
                            (playerAtOpponentImpactScreen.u +
                              bestAttackScreen.u) /
                              2 -
                            0.05
                          }
                          y={
                            (playerAtOpponentImpactScreen.v +
                              bestAttackScreen.v) /
                              2 -
                            0.03
                          }
                          width={0.1}
                          height={0.026}
                          rx={0.006}
                          fill="rgba(0,0,0,0.68)"
                        />
                      )}

                      {showCourtLabels && (
                        <text
                          x={
                            (playerAtOpponentImpactScreen.u +
                              bestAttackScreen.u) /
                            2
                          }
                          y={
                            (playerAtOpponentImpactScreen.v +
                              bestAttackScreen.v) /
                              2 -
                            0.017
                          }
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fill="white"
                          fontSize={isInFullPopup ? "0.0155" : "0.014"}
                          fontWeight="700"
                        >
                          {fmtPounceOverlayFeet(
                            pounceOverlay?.movementDistanceFeet,
                          )}
                        </text>
                      )}
                    </>
                  )}

                {triggerOverlayVisible && opponentContactScreen && (
                  <g>
                    {/* <line
                      x1={opponentContactScreen.u - 0.01}
                      y1={opponentContactScreen.v - 0.01}
                      x2={opponentContactScreen.u + 0.01}
                      y2={opponentContactScreen.v + 0.01}
                      stroke="rgba(255,110,90,0.98)"
                      strokeWidth={isInFullPopup ? 0.006 : 0.005}
                      strokeLinecap="round"
                    />
                    <line
                      x1={opponentContactScreen.u + 0.01}
                      y1={opponentContactScreen.v - 0.01}
                      x2={opponentContactScreen.u - 0.01}
                      y2={opponentContactScreen.v + 0.01}
                      stroke="rgba(255,110,90,0.98)"
                      strokeWidth={isInFullPopup ? 0.006 : 0.005}
                      strokeLinecap="round"
                    /> */}

                    {showCourtLabels && (
                      <rect
                        x={opponentContactScreen.u - 0.065}
                        y={opponentContactScreen.v - 0.05}
                        width={0.13}
                        height={0.024}
                        rx={0.006}
                        fill="rgba(0,0,0,0.72)"
                      />
                    )}

                    {showCourtLabels && (
                      <text
                        x={opponentContactScreen.u}
                        y={opponentContactScreen.v - 0.037}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="white"
                        fontSize={isInFullPopup ? "0.0145" : "0.0135"}
                        fontWeight="700"
                      >
                        {`Opp P${pounceOverlay.opponentPlayerId} · S${pounceOverlay.triggerShotNumber}`}
                      </text>
                    )}
                  </g>
                )}

                {pounceWindowVisible &&
                  showCourtLabels &&
                  firstAttackableScreen && (
                    <g>
                      <circle
                        cx={firstAttackableScreen.u}
                        cy={firstAttackableScreen.v}
                        r={isInFullPopup ? 0.013 : 0.0115}
                        fill="rgba(0,0,0,0)"
                        stroke="rgba(255,235,110,0.98)"
                        strokeWidth={isInFullPopup ? 0.0055 : 0.0048}
                      />
                      <rect
                        x={firstAttackableScreen.u - 0.048}
                        y={firstAttackableScreen.v - 0.05}
                        width={0.096}
                        height={0.024}
                        rx={0.006}
                        fill="rgba(0,0,0,0.72)"
                      />
                      <text
                        x={firstAttackableScreen.u}
                        y={firstAttackableScreen.v - 0.037}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="white"
                        fontSize={isInFullPopup ? "0.0145" : "0.0135"}
                        fontWeight="700"
                      >
                        start
                      </text>
                    </g>
                  )}

                {pounceWindowVisible && bestAttackScreen && pounceWasTaken && (
                  <g>
                    <circle
                      cx={bestAttackScreen.u}
                      cy={bestAttackScreen.v}
                      r={bestAttackBaseRadius}
                      fill={"red"} //bestAttackFill}
                      stroke={bestAttackStroke}
                      strokeWidth={bestAttackStrokeWidth}
                      strokeLinecap="round"
                      // strokeDasharray={pounceWasTaken ? undefined : "0.01 0.006"}
                    />
                  </g>
                )}

                {pounceWindowVisible &&
                  bestAttackScreen &&
                  distanceMovedLabel && (
                    <g>
                      <rect
                        x={bestAttackScreen.u - 0.052}
                        y={bestAttackScreen.v - 0.069}
                        width={0.104}
                        height={0.052}
                        rx={0.006}
                        fill="rgba(20,20,24,0.76)"
                        stroke="rgba(196,92,92,0.24)"
                        strokeWidth={0.0012}
                      />

                      {typeof pounceOverlay?.triggerShotNumber ===
                        "number" && (
                        <text
                          x={bestAttackScreen.u}
                          y={bestAttackScreen.v - 0.056}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fill="rgba(255,255,255,0.92)"
                          fontSize={isInFullPopup ? "0.012" : "0.011"}
                          fontWeight={800}
                        >
                          {`Shot ${pounceOverlay.triggerShotNumber}`}
                        </text>
                      )}

                      <text
                        x={bestAttackScreen.u}
                        y={bestAttackScreen.v - 0.041}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill={
                          pounceOverlay?.pounceResult === "taken"
                            ? "rgba(120,255,160,0.98)"
                            : "rgba(255,170,170,0.98)"
                        }
                        fontSize={isInFullPopup ? "0.0105" : "0.0098"}
                        fontWeight={800}
                      >
                        {fmtPounceTakenShort(pounceOverlay?.pounceResult)}
                      </text>

                      <text
                        x={bestAttackScreen.u}
                        y={bestAttackScreen.v - 0.025}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="rgba(255,255,255,0.92)"
                        fontSize={isInFullPopup ? "0.0125" : "0.0115"}
                        fontWeight={700}
                      >
                        {distanceMovedLabel}
                      </text>
                    </g>
                  )}

                {pounceWindowVisible &&
                  showCourtLabels &&
                  peakScreen &&
                  typeof pounceOverlay?.peakHeightOverNet === "number" &&
                  Number.isFinite(pounceOverlay.peakHeightOverNet) && (
                    <g>
                      <circle
                        cx={peakScreen.u}
                        cy={peakScreen.v}
                        r={isInFullPopup ? 0.009 : 0.008}
                        fill="rgba(255,255,255,0.94)"
                        stroke="rgba(0,0,0,0.55)"
                        strokeWidth={0.0025}
                      />
                      <rect
                        x={peakScreen.u - 0.052}
                        y={peakScreen.v - 0.046}
                        width={0.104}
                        height={0.024}
                        rx={0.006}
                        fill="rgba(0,0,0,0.72)"
                      />
                      {showCourtLabels && (
                        <text
                          x={peakScreen.u}
                          y={peakScreen.v - 0.033}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fill="white"
                          fontSize={isInFullPopup ? "0.014" : "0.013"}
                          fontWeight="700"
                        >
                          {`peak ${pounceOverlay.peakHeightOverNet.toFixed(1)} ft`}
                        </text>
                      )}
                    </g>
                  )}

                {/* {pounceWindowVisible && pounceShotTubePolyline && (
                  <>
<polyline
  points={pounceShotTubePolyline}
  fill="none"
  stroke="rgba(170, 205, 225, 0.16)"
  strokeWidth={isInFullPopup ? 0.013 : 0.011}
  strokeLinecap="round"
  strokeLinejoin="round"
/>
<polyline
  points={pounceShotTubePolyline}
  fill="none"
  stroke="rgba(205, 225, 238, 0.42)"
  strokeWidth={isInFullPopup ? 0.0052 : 0.0045}
  strokeLinecap="round"
  strokeLinejoin="round"
/>
                  </>
                )} */}
                {pounceWindowVisible && pounceTriggerBallUvPolyline && (
                  <polyline
                    points={pounceTriggerBallUvPolyline}
                    fill="none"
                    //stroke="rgba(70,255,235,0.82)"
                    // stroke="rgba(120,255,245,0.78)"
                    // strokeWidth={isInFullPopup ? 0.0048 : 0.0041}
                    //                     stroke="rgba(145,225,255,0.42)"
                    // strokeWidth={isInFullPopup ? 0.0032 : 0.0028}
                    //stroke="rgba(165,215,255,0.34)"
                    // stroke="rgba(255, 235, 120, 0.32)"
                    stroke="rgba(255, 225, 90, 0.38)"
                    strokeWidth={isInFullPopup ? 0.007 : 0.006}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                )}

                {/* pounce tube goes here */}

                {false &&
                  pounceWindowVisible &&
                  pounceTriggerBallUvSamples.map((pt, idx) => (
                    <circle
                      key={`pounce-ball-uv-${idx}`}
                      cx={pt.u}
                      cy={pt.v}
                      r={
                        pt.radiusV && Number.isFinite(pt.radiusV)
                          ? Math.max(0.0025, Math.min(pt.radiusV, 0.012))
                          : 0.0036
                      }
                      fill={
                        idx === 0
                          ? "lime"
                          : idx === pounceTriggerBallUvSamples.length - 1
                            ? "red"
                            : "rgba(255,255,255,0.95)"
                      }
                      stroke="rgba(0,0,0,0.8)"
                      strokeWidth={0.0013}
                    />
                  ))}

                {/* {pounceWindowVisible && (
                  <text
                    x={0.03}
                    y={0.13}
                    fill="white"
                    fontSize="0.018"
                    fontWeight="700"
                  >
                    {`shotMs:${pounceTriggerShotStartMs ?? "—"}-${pounceTriggerShotEndMs ?? "—"} uvPts:${pounceTriggerBallUvSamples.length}`}
                  </text>
                )} */}
              </svg>
            )}

            {showTimelineTicks && videoRef && (
              <TimelineWhiteTicks
                videoRef={videoRef}
                rallyKey={`${row.rallyIndex}-${row.pointKey ?? "no-point-key"}`}
                overlays={row.shotNumberOverlays as ShotNumberOverlay[]}
                segmentStartMs={row.segmentStartMs}
                segmentEndMs={row.segmentEndMs}
                heightPx={TIMELINE_WHITE_TICKS_H}
                alignToTrackRef={
                  showLoopScrubber && loopUiOpen ? trackRef : undefined
                }
                alignWithinRef={videoOverlayRef}
                showLoopScrubber={showLoopScrubber}
                loopUiOpen={loopUiOpen}
                loopEnabled={loop.enabled}
                loopASec={loop.aSec}
                loopBSec={loop.bSec}
              />
            )}

            {showPopoutButton && !disablePopout && (
              <Tooltip
                title={isDemoRun ? "" : "Pop out video"}
                disableHoverListener={isDemoRun}
                disableFocusListener={isDemoRun}
                disableTouchListener={isDemoRun}
                slotProps={{
                  popper: {
                    sx: TRIAGE_TOOLTIP_POPPER_SX,
                  },
                }}
              >
                <IconButton
                  data-demo="active-rally-video-popout-button"
                  size="small"
                  onClick={openPopout}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    zIndex: 190,

                    width: 34,
                    height: 34,
                    borderRadius: 2,
                    bgcolor: "rgba(0,0,0,0.35)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    backdropFilter: "blur(6px)",
                    color: "white",
                    "&:hover": {
                      bgcolor: "rgba(0,0,0,0.55)",
                      border: "1px solid rgba(255,255,255,0.35)",
                    },
                  }}
                >
                  {/* <span style={{ color: "white", fontSize: 16, opacity: 0.9 }}>
                    ↗
                  </span> */}
                  <LaunchIcon sx={{ fontSize: 20, opacity: 0.92 }} />
                </IconButton>
              </Tooltip>
            )}

            {showPreviewPlayPauseButton && (
              <IconButton
                data-demo="active-rally-video-preview-play-toggle"
                size="small"
                aria-label={paused ? "Play preview" : "Pause preview"}
                title={paused ? "Play preview" : "Pause preview"}
                onPointerDown={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  setPausedUser(!paused);
                }}
                sx={{
                  position: "absolute",
                  left: 8,
                  top: 8,
                  zIndex: 190,
                  width: 30,
                  height: 30,
                  borderRadius: 2,
                  bgcolor: "rgba(0,0,0,0.48)",
                  border: "1px solid rgba(255,255,255,0.24)",
                  backdropFilter: "blur(6px)",
                  color: "white",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.35)",
                  "&:hover": {
                    bgcolor: "rgba(25,118,210,0.82)",
                    border: "1px solid rgba(255,255,255,0.4)",
                  },
                }}
              >
                {paused ? (
                  <PlayArrowIcon sx={{ fontSize: 18, ml: 0.2 }} />
                ) : (
                  <PauseIcon sx={{ fontSize: 17 }} />
                )}
              </IconButton>
            )}

            {/* Bottom-right mini dock (ONLY when scrubber is closed) */}
            {(showLoopScrubber || showAi) && !loopUiOpen && (
              <Box
                onPointerDown={(event) => event.stopPropagation()}
                onPointerMove={(event) => event.stopPropagation()}
                onPointerUp={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
                onMouseMove={(event) => event.stopPropagation()}
                onMouseUp={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
                sx={{
                  position: "absolute",
                  right: isInFullPopup ? 20 : 4, //20
                  bottom: isInFullPopup ? 30 : 22, //30
                  zIndex: 140,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.28,
                }}
              >
                {showLoopScrubber && (
                  <Tooltip
                    title="Show loop scrubber"
                    placement="top"
                    slotProps={{
                      popper: {
                        sx: TRIAGE_TOOLTIP_POPPER_SX,
                      },
                    }}
                  >
                    <IconButton
                      data-demo="active-rally-loop-open"
                      size="small"
                      onClick={() => setLoopUiOpen(true)}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 2,
                        bgcolor: "rgba(0,0,0,0.35)",
                        border:
                          !loopUiOpen && loop.enabled
                            ? "2px solid rgba(255, 191, 71, 0.72)"
                            : "1px solid rgba(255,255,255,0.16)",
                        boxShadow:
                          !loopUiOpen && loop.enabled
                            ? "0 0 0 1px rgba(255, 191, 71, 0.16)"
                            : "none",
                        backdropFilter: "blur(6px)",
                      }}
                    >
                      {/* <IconButton
                      size="small"
                      onClick={() => setLoopUiOpen(true)}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 2,
                        bgcolor: "rgba(0,0,0,0.35)",
                        border: "1px solid rgba(255,255,255,0.16)",
                        backdropFilter: "blur(6px)",
                      }}
                    > */}
                      <LoopBracketTimelineIcon
                        size={22}
                        color={"white"}
                        strokeWidth={1}
                      />
                    </IconButton>
                  </Tooltip>
                )}

                {showAi && (
                  <Tooltip
                    title={aiOpen ? "Close AI" : "Ask AI"}
                    placement="top"
                    slotProps={{
                      popper: {
                        sx: TRIAGE_TOOLTIP_POPPER_SX,
                      },
                    }}
                  >
                    <IconButton
                      ref={aiEntryButtonRef}
                      size="small"
                      onClick={toggleAiPanel} //  () => setAiOpen((v) => !v)}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 2,
                        bgcolor: aiOpen
                          ? "rgba(0,0,0,0.55)"
                          : "rgba(0,0,0,0.35)",
                        border:
                          aiTurns.length > 0
                            ? "2px solid red"
                            : "2px solid transparent",
                        backgroundColor:
                          aiTurns.length > 0 ? "rgba(0,0,0,0.35)" : undefined,
                        backdropFilter: "blur(6px)",
                      }}
                    >
                      <Box
                        sx={{
                          color: "white",
                          fontSize: "14px",
                          opacity: aiOpen ? 1 : 0.8,
                        }}
                      >
                        {"AI"}
                      </Box>
                    </IconButton>
                  </Tooltip>
                )}

                {/* {showSimilarControls && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.28,
                    pl: 0.1,
                  }}
                >
                  <Tooltip title="Open similar rallies" placement="top">
                    <Button
                      size="small"
                      onClick={() => setSimilarPopupOpen(true)}
                      sx={{
                        minWidth: 0,
                        px: 0.7,
                        height: 28,
                        borderRadius: 2,
                        bgcolor: "rgba(0,0,0,0.35)",
                        border: "1px solid rgba(255,255,255,0.16)",
                        color: "white",
                        backdropFilter: "blur(6px)",
                        fontSize: 11,
                        fontWeight: 900,
                        lineHeight: 1,
                        "&:hover": {
                          bgcolor: "rgba(0,0,0,0.50)",
                        },
                      }}
                    >
                      SIM
                    </Button>
                  </Tooltip>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.2,
                      px: 0.28,
                      py: 0.18,
                      borderRadius: 2,
                      bgcolor: "rgba(0,0,0,0.35)",
                      border: "1px solid rgba(255,255,255,0.16)",
                      backdropFilter: "blur(6px)",
                    }}
                  >
                    {(["G", "0", "1", "2", "3"] as const).map((key) => {
                      const active =
                        key === "G"
                          ? similarMode.type === "global"
                          : similarMode.type === "player" &&
                            similarMode.playerId === Number(key);

                      return (
                        <Box
                          key={key}
                          onClick={() => {
                            const nextMode =
                              key === "G"
                                ? ({ type: "global" } as SimilarityMode)
                                : ({
                                    type: "player",
                                    playerId: Number(key),
                                  } as SimilarityMode);

                            setSimilarMode(nextMode);
                            setSimilarPopupOpen(true);
                          }}
                          sx={{
                            width: 18,
                            height: 18,
                            borderRadius: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            userSelect: "none",
                            fontSize: 10,
                            fontWeight: 900,
                            color: "white",
                            bgcolor: active
                              ? "rgba(255,255,255,0.20)"
                              : "rgba(255,255,255,0.04)",
                            border: active
                              ? "1px solid rgba(255,255,255,0.30)"
                              : "1px solid rgba(255,255,255,0.08)",
                            "&:hover": {
                              bgcolor: active
                                ? "rgba(255,255,255,0.24)"
                                : "rgba(255,255,255,0.10)",
                            },
                          }}
                        >
                          {key}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
)} */}

                <Tooltip
                  title="Export HTML"
                  placement="top"
                  slotProps={{
                    popper: {
                      sx: TRIAGE_TOOLTIP_POPPER_SX,
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={exportHtml}
                    disabled={!row}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 2,
                      bgcolor: "rgba(0,0,0,0.35)",
                      border: "1px solid rgba(255,255,255,0.16)",
                      backdropFilter: "blur(6px)",
                      opacity: row ? 1 : 0.5,
                    }}
                  >
                    <FileDownloadOutlinedIcon
                      fontSize="small"
                      style={{ opacity: 0.9, color: "white" }}
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title="Export clip for social media"
                  placement="top"
                  slotProps={{
                    popper: {
                      sx: { ...TRIAGE_TOOLTIP_POPPER_SX, zIndex: 20000 },
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSocialExportError(null);
                      setSocialExportOpen(true);
                    }}
                    disabled={!row || socialExportBusy}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 2,
                      bgcolor: "rgba(0,0,0,0.35)",
                      border: "1px solid rgba(255,255,255,0.16)",
                      backdropFilter: "blur(6px)",
                      opacity: row ? 1 : 0.5,
                    }}
                  >
                    {socialExportBusy ? (
                      <CircularProgress size={15} sx={{ color: "white" }} />
                    ) : (
                      <MovieCreationOutlinedIcon
                        fontSize="small"
                        style={{ opacity: 0.9, color: "white" }}
                      />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {/* Loop scrubber overlay (optional, controlled by props + local toggle) */}
            {showLoopScrubber && loopUiOpen && (() => {
              const node = (
              <Box
                onPointerDown={(event) => {
                  onControlsInteractionChange?.(true);
                  event.stopPropagation();
                }}
                onPointerMove={(event) => event.stopPropagation()}
                onPointerUp={(event) => {
                  onControlsInteractionChange?.(false);
                  event.stopPropagation();
                }}
                onMouseDown={(event) => {
                  onControlsInteractionChange?.(true);
                  event.stopPropagation();
                }}
                onMouseMove={(event) => event.stopPropagation()}
                onMouseUp={(event) => {
                  onControlsInteractionChange?.(false);
                  event.stopPropagation();
                }}
                onClick={(event) => event.stopPropagation()}
                sx={{
                  position: "absolute",
                  left: isInFullPopup ? 20 : 4,
                  right: isInFullPopup ? 20 : 4,
                  bottom: isInFullPopup ? 55 : 25, // + (showLoopScrubber || showAi ? 42 : 0), // sit above the icon cluster
                  zIndex: 140,
                  pointerEvents: "auto",
                  borderRadius: 2,
                  bgcolor: "rgba(0,0,0,0.40)",
                  border: loop.enabled
                    ? "1px solid rgba(255,255,255,0.18)"
                    : "1px solid rgba(255,255,255,0.14)",
                  backdropFilter: "blur(8px)",
                  p: 0.8,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 0.35,
                    px: 0.25,
                    color: "rgba(255,255,255,0.72)",
                    fontSize: 11,
                    fontWeight: 850,
                    lineHeight: 1,
                    pointerEvents: "none",
                  }}
                >
                  <Box component="span">Review window</Box>
                  <Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {fmtSec(loop.aSec)} - {fmtSec(loop.bSec)}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.55,
                  }}
                >
                  {/* TRACK (thinner width because buttons live on right) */}
                  <Box
                    ref={trackRef}
                    data-happy-path="review-window-scrubber-track"
                    data-loop-enabled={loop.enabled ? "true" : "false"}
                    data-loop-start-sec={loop.aSec.toFixed(3)}
                    data-loop-end-sec={loop.bSec.toFixed(3)}
                    data-scrub-playhead-sec={scrubPlayheadSecUi.toFixed(3)}
                    onPointerDown={(e: any) => {
                      if (!durationSec) return;

                      const secRaw = getTrackSecFromClientX(e.clientX);

                      setLoop((cur) => ({ ...cur, enabled: true }));

                      const da = Math.abs(secRaw - loop.aSec);
                      const db = Math.abs(secRaw - loop.bSec);
                      const nearHandle =
                        !loop.enabled &&
                        Math.min(da, db) <
                        Math.max(0.28, (durationSec ?? 1) * 0.03);

                      beginScrubDrag(
                        e,
                        nearHandle ? (da <= db ? "a" : "b") : "p",
                        !nearHandle,
                      );
                    }}
                    onPointerMove={(e: any) => {
                      applyScrubDragClientX(e.clientX);
                      e.stopPropagation();
                    }}
                    onPointerUp={(e: any) => {
                      finishScrubDrag();
                      e.stopPropagation();
                    }}
                    onPointerCancel={(e: any) => {
                      finishScrubDrag();
                      e.stopPropagation();
                    }}
                    onLostPointerCapture={(e: any) => {
                      clearScrubPointerCapture();
                      e.stopPropagation();
                    }}
                    onMouseDown={(e: any) => {
                      if (Date.now() < suppressMouseScrubUntilRef.current) {
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                      }
                      if (!durationSec) return;

                      const secRaw = getTrackSecFromClientX(e.clientX);
                      setLoop((cur) => ({ ...cur, enabled: true }));

                      const da = Math.abs(secRaw - loop.aSec);
                      const db = Math.abs(secRaw - loop.bSec);
                      const nearHandle =
                        !loop.enabled &&
                        Math.min(da, db) <
                        Math.max(0.28, (durationSec ?? 1) * 0.03);

                      beginMouseScrubDrag(
                        e,
                        nearHandle ? (da <= db ? "a" : "b") : "p",
                        !nearHandle,
                      );
                    }}
                    sx={{
                      position: "relative",
                      height: 34, //26, // a touch slimmer
                      borderRadius: 2,
                      bgcolor: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(255,255,255,0.12)",
                      overflow: "hidden",
                      cursor: "pointer",
                      flex: "1 1 auto", // ✅ take remaining width
                      minWidth: 0, // ✅ allow shrink
                    }}
                  >
                    {/* track base */}
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        bgcolor: "rgba(255,255,255,0.04)",
                      }}
                    />

                    {/* outside region dimming */}
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: `${aPct * 100}%`,
                        bgcolor: "rgba(0,0,0,0.35)",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        left: `${bPct * 100}%`,
                        top: 0,
                        bottom: 0,
                        width: `${Math.max(0, (1 - bPct) * 100)}%`,
                        bgcolor: "rgba(0,0,0,0.35)",
                      }}
                    />

                    {/* selected region */}
                    <Box
                      sx={{
                        position: "absolute",
                        left: `${aPct * 100}%`,
                        width: `${Math.max(0, (bPct - aPct) * 100)}%`,
                        top: 0,
                        bottom: 0,
                        bgcolor: loop.enabled
                          ? "rgba(255,255,255,0.12)"
                          : "rgba(255,255,255,0.09)",
                        outline: loop.enabled
                          ? "1px solid rgba(255,255,255,0.24)"
                          : "1px solid rgba(255,255,255,0.18)",
                      }}
                    />

                    <Box
                      sx={{
                        position: "absolute",
                        left: `calc(${scrubPlayPct * 100}% - 1px)`,
                        top: 2,
                        bottom: 2,
                        width: 2,
                        borderRadius: 1,
                        bgcolor: "rgba(255,255,255,0.82)",
                        boxShadow:
                          "0 0 0 1px rgba(0,0,0,0.38), 0 0 10px rgba(255,255,255,0.34)",
                        pointerEvents: "none",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        left: `calc(${scrubPlayPct * 100}% - 5px)`,
                        top: 12,
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: "#ffffff",
                        boxShadow: "0 0 0 2px rgba(0,0,0,0.48)",
                        pointerEvents: "none",
                      }}
                    />

                    {/* left thumb (A) */}

                    {!loop.enabled ? <Box
                      onPointerDown={(e: any) => {
                        beginScrubDrag(e, "a", false);
                      }}
                      onMouseDown={(e: any) => {
                        if (Date.now() < suppressMouseScrubUntilRef.current) {
                          e.preventDefault();
                          e.stopPropagation();
                          return;
                        }
                        beginMouseScrubDrag(e, "a", false);
                      }}
                      sx={{
                        position: "absolute",
                        left: aThumbLeftCss,
                        top: 3,
                        width: THUMB_W,
                        height: THUMB_H,
                        borderRadius: 2,
                        bgcolor: "rgba(0,0,0,0.62)",
                        border: "1px solid rgba(255,255,255,0.24)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
                        cursor: "ew-resize",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <KeyboardTabIcon
                        sx={{
                          fontSize: 17,
                          color: "white",
                          opacity: 0.95,
                          transform: "scaleX(1)",
                          userSelect: "none",
                        }}
                      />
                    </Box> : null}

                    {/* right thumb (B) */}
                    {/* <Box
                      onMouseDown={(e: any) => {
                        const v = videoRef.current;
                        wasPlayingBeforeDragRef.current = !!v && !v.paused;
                        setDragging("b");
                        setPausedUser(true);
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      sx={{
                        position: "absolute",
                        left: bThumbLeftCss,
                        top: 3,
                        width: THUMB_W,
                        height: 28,
                        borderRadius: 2,
                        bgcolor: "rgba(0,0,0,0.62)",
                        border: "1px solid rgba(255,255,255,0.26)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.22)",
                        cursor: "ew-resize",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <KeyboardTabIcon
                        sx={{
                          fontSize: 16,
                          color: "white",
                          opacity: 0.95,
                          userSelect: "none",
                                                    transform: "scaleX(-1)",

                        }}
                      />
                    </Box> */}
                    {!loop.enabled ? <Box
                      onPointerDown={(e: any) => {
                        beginScrubDrag(e, "b", false);
                      }}
                      onMouseDown={(e: any) => {
                        if (Date.now() < suppressMouseScrubUntilRef.current) {
                          e.preventDefault();
                          e.stopPropagation();
                          return;
                        }
                        beginMouseScrubDrag(e, "b", false);
                      }}
                      sx={{
                        position: "absolute",
                        left: bThumbLeftCss,
                        top: 3,
                        width: THUMB_W,
                        height: THUMB_H,
                        borderRadius: 2,
                        bgcolor: "rgba(0,0,0,0.62)",
                        border: "1px solid rgba(255,255,255,0.24)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
                        cursor: "ew-resize",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <KeyboardTabIcon
                        sx={{
                          fontSize: 17,
                          color: "white",
                          opacity: 0.95,
                          userSelect: "none",
                          transform: "scaleX(-1)",
                        }}
                      />
                    </Box> : null}
                  </Box>

                  {/* BUTTONS to the RIGHT of the scrubber */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.2,
                      flex: "0 0 auto",
                    }}
                  >
                    <Tooltip title="Hide loop scrubber" placement="top">
                      <IconButton
                        size="small"
                        onClick={() => setLoopUiOpen(false)}
                        // sx={{ width: 30, height: 30, borderRadius: 2 }}
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 1.6,
                          bgcolor: "rgba(255,255,255,0.06)",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
                        }}
                      >
                        <LoopBracketTimelineIcon
                          size={18}
                          color={"white"}
                          strokeWidth={1.15}
                        />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Clear loop region" placement="top">
                      <IconButton
                        size="small"
                        onClick={clearLoop}
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 1.3,
                          bgcolor: "rgba(255,255,255,0.06)",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
                        }}
                      >
                        <CloseIcon
                          sx={{
                            fontSize: 15,
                            opacity: 0.9,
                            color: "rgba(255,255,255,0.9)",
                          }}
                        />
                      </IconButton>
                    </Tooltip>

                    {showAi && (
                      <Tooltip
                        title={aiOpen ? "Close AI" : "Ask AI"}
                        placement="top"
                      >
                        <IconButton
                          size="small"
                          onClick={() => setAiOpen((v) => !v)}
                          sx={{
                            width: 28,
                            height: 24,
                            borderRadius: 1.3,
                            color: "white",
                            bgcolor: "rgba(255,255,255,0.06)",
                            "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
                            fontSize: 16,
                            fontWeight: 500,
                          }}
                        >
                          {"AI"}
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip title="Export HTML" placement="top">
                      <IconButton
                        size="small"
                        onClick={exportHtml}
                        disabled={!row}
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 1.3,
                          bgcolor: "rgba(255,255,255,0.05)",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.10)" },
                          opacity: row ? 1 : 0.5,
                          p: 0,
                        }}
                      >
                        <FileDownloadOutlinedIcon
                          sx={{
                            fontSize: 15,
                            opacity: 0.9,
                            color: "rgba(255,255,255,0.9)",
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title="Export clip for social media"
                      placement="top"
                      slotProps={{
                        popper: {
                          sx: { ...TRIAGE_TOOLTIP_POPPER_SX, zIndex: 20000 },
                        },
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSocialExportError(null);
                          setSocialExportOpen(true);
                        }}
                        disabled={!row || socialExportBusy}
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 1.3,
                          bgcolor: "rgba(255,255,255,0.05)",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.10)" },
                          opacity: row ? 1 : 0.5,
                          p: 0,
                        }}
                      >
                        {socialExportBusy ? (
                          <CircularProgress
                            size={13}
                            sx={{ color: "rgba(255,255,255,0.9)" }}
                          />
                        ) : (
                          <MovieCreationOutlinedIcon
                            sx={{
                              fontSize: 15,
                              opacity: 0.9,
                              color: "rgba(255,255,255,0.9)",
                            }}
                          />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {dragging && (
                  <ScrubGlobalHandlers
                    onMove={(clientX) => {
                      applyScrubDragClientX(clientX);
                    }}
                    onUp={() => {
                      finishScrubDrag();
                    }}
                  />
                )}
              </Box>
              );
              const loopPortalContainer = loopControlsPortalContainer ?? controlsPortalContainer;
              if (requireLoopControlsPortal && !loopPortalContainer) return null;
              return loopPortalContainer
                ? createPortal(node, loopPortalContainer)
                : node;
            })()}

            {/* AI slideout drawer (optional; absolute; does NOT shift layout) */}
            {showAi && aiDrawerMode === "overlay" && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: "100%",
                  height: "100%",
                  zIndex: 25,
                  transform: aiOpen ? "translateX(10px)" : "translateX(-10px)",
                  opacity: aiOpen ? 1 : 0,
                  pointerEvents: aiOpen ? "auto" : "none",
                  transition: "transform 160ms ease, opacity 160ms ease",
                }}
              >
                <Paper
                  elevation={8}
                  sx={{
                    // ✅ Wider when we have turns (responses column + controls column)
                    width: aiPanelWidth, // aiTurns.length > 0 ? 760 : 360, // 400 + 360 (ish)
                    height: "97%",
                    borderRadius: 2,
                    overflow: "hidden",
                    bgcolor: "rgba(20,20,20,0.92)",
                    border: "1px solid rgba(255,255,255,0.16)", // ✅ clearer right edge
                    backdropFilter: "blur(10px)",

                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                  }}
                >
                  {/* header */}
                  <Box
                    sx={{
                      px: 1.25,
                      py: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      flex: "0 0 auto",
                      bgcolor: "rgba(0,0,0,0.30)",
                      borderBottom: "1px solid rgba(255,255,255,0.10)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 900,
                        letterSpacing: "0.2px",
                        color: "rgba(255,255,255,0.95)",
                      }}
                    >
                      AI
                    </Typography>

                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setAiOpen(false)}
                      sx={{
                        textTransform: "none",
                        color: "rgba(255,255,255,0.85)",
                        border: "1px solid rgba(255,255,255,0.18)",
                        borderRadius: 1.5,
                        px: 1,
                        py: 0.35,
                        minWidth: 0,
                        "&:hover": {
                          borderColor: "rgba(255,255,255,0.28)",
                          bgcolor: "rgba(255,255,255,0.06)",
                        },
                      }}
                    >
                      Close AI Window
                    </Button>
                  </Box>

                  {/* content */}
                  <Box
                    sx={{
                      flex: "1 1 auto",
                      minHeight: 0,
                      display: "flex",
                      overflow: "hidden",
                      fontSize: 13, // "12px !important",
                      lineHeight: 1.25,
                    }}
                  >
                    {/* LEFT: Responses (only when there are turns) */}
                    {aiTurns.length > 0 && (
                      <Box
                        sx={{
                          flex: "0 0 500px",
                          minWidth: 0,
                          borderRight: "1px solid rgba(255,255,255,0.10)",
                          display: "flex",
                          flexDirection: "column",
                          minHeight: 0,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            px: 1.2,
                            py: 0.9,
                            flex: "0 0 auto",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            bgcolor: "rgba(0,0,0,0.18)",
                            borderBottom: "1px solid rgba(255,255,255,0.08)",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 800,
                              color: "rgba(255,255,255,0.88)",
                            }}
                          >
                            Responses
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: 13,
                              opacity: 0.65,
                              color: "rgba(255,255,255,0.8)",
                            }}
                          >
                            {aiTurns.length} turn
                            {aiTurns.length === 1 ? "" : "s"}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            p: 1.1,
                            flex: "1 1 auto",
                            minHeight: 0,
                            overflow: "auto",
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            position: "relative",
                            fontSize: "13px !important",
                          }}
                        >
                          {aiTurns.map((t) => (
                            <Box
                              key={t.id}
                              sx={{
                                border: "1px solid rgba(255,255,255,0.12)",
                                borderRadius: 1.5,
                                p: 1,
                                bgcolor: "rgba(0,0,0,0.18)",
                                position: "relative",
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => deleteTurn(t.id)}
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  right: 0,
                                  color: "red",
                                  border: "1px solid transparent",
                                  "&:hover": { border: "1px solid yellow" },
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                              <Typography
                                sx={{
                                  fontSize: 13, // "12px !important",
                                  lineHeight: 1.25,
                                  color: "rgba(255,255,255,0.92)",
                                }}
                              >
                                <span style={{ fontWeight: 800 }}>Q:</span>{" "}
                                {t.question}
                              </Typography>

                              <Box sx={{}}>
                                <Typography
                                  sx={{
                                    // fontSize: 13,
                                    opacity: 0.7,
                                    fontWeight: 800,
                                  }}
                                >
                                  A:
                                </Typography>
                                <Box sx={{ mt: 0.0 }}>{renderAiAnswer(t)}</Box>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* RIGHT: Controls + question */}
                    <Box
                      sx={{
                        flex: "1 1 auto",
                        minWidth: 0,
                        minHeight: 0,
                        overflow: "auto",
                        p: 1.2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={aiRangeMode}
                        onChange={(_, v) => v && setAiRangeMode(v)}
                        sx={{
                          alignSelf: "flex-start",
                          "& .MuiToggleButton-root": {
                            color: "rgba(255,255,255,0.85)",
                            borderColor: "rgba(255,255,255,0.18)",
                            textTransform: "none",
                            px: 1.1,
                            py: 0.45,
                          },
                          "& .MuiToggleButton-root.Mui-selected": {
                            bgcolor: "rgba(255,255,255,0.14)",
                            borderColor: "rgba(255,255,255,0.28)",
                            color: "rgba(255,255,255,0.95)",
                          },
                          "& .MuiToggleButton-root.Mui-disabled": {
                            color: "rgba(255,255,255,0.35)",
                            borderColor: "rgba(255,255,255,0.10)",
                          },
                        }}
                      >
                        <ToggleButton value="loop" disabled={!loop.enabled}>
                          Use Loop
                        </ToggleButton>
                        <ToggleButton value="full">Use Full Rally</ToggleButton>
                      </ToggleButtonGroup>

                      {/* range (✅ one line, no ms parentheticals) */}
                      <Box
                        sx={{
                          fontSize: 12,
                          color: "rgba(255,255,255,0.85)",
                          border: "1px solid rgba(255,255,255,0.10)",
                          borderRadius: 1.5,
                          px: 1,
                          py: 0.85,
                          bgcolor: "rgba(0,0,0,0.16)",
                        }}
                      >
                        Range:{" "}
                        {effectiveAiRange
                          ? `${fmtMsRel(effectiveAiRange.startMs - row.segmentStartMs)} → ${fmtMsRel(
                              effectiveAiRange.endMs - row.segmentStartMs,
                            )}`
                          : "—"}
                        {!packageId && (
                          <div style={{ marginTop: 6, opacity: 0.65 }}>
                            ⚠️ No <code>packageId</code> — Ask AI is disabled.
                          </div>
                        )}
                      </Box>

                      {/* question (✅ preserved) */}
                      <TextField
                        size="small"
                        value={aiQuestionText}
                        onChange={(e) => setAiQuestionText(e.target.value)}
                        placeholder="Ask a question about this rally…"
                        multiline
                        minRows={2}
                        maxRows={6}
                        sx={{
                          "& .MuiInputBase-root": {
                            color: "rgba(255,255,255,0.90)",
                            bgcolor: "rgba(0,0,0,0.22)",
                            borderRadius: 1.5,
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(255,255,255,0.14)",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(255,255,255,0.22)",
                          },
                        }}
                      />

                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Button
                          variant="contained"
                          size="small"
                          disabled={
                            !row ||
                            !packageId ||
                            aiBusy ||
                            !aiQuestionText.trim() ||
                            !effectiveAiRange
                          }
                          onClick={askAi} // ✅ preserved
                        >
                          {aiBusy ? "Asking…" : "Ask AI"}
                        </Button>

                        {aiBusy && (
                          <CircularProgress
                            size={16}
                            sx={{ color: "rgba(255,255,255,0.75)" }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            )}
          </Box>

          {showShotLabels && (
            <ShotNumberOverlayLayer
              renderPlayerIdentity={renderPlayerIdentity}
              insightsToCvPlayerIndex={insightsToCvPlayerIndex}
              focusedFilter={focusedFilter}
              rallyIndex={row.rallyIndex}
              videoRef={videoRef}
              overlays={row.shotNumberOverlays}
              segmentStartMs={row.segmentStartMs}
              highlightedShotNumber={props.activeShotNumber || undefined}
              breakShotNumber={row.breakShotNumber}
              //highlightedExtraText={undefined}
              firefightsAll={firefightsClips}
              isFromFullPopout={isInFullPopup}
              highlightedExtraText={props.activeShotExtraText}
              persistentExtraTextForOverlay={persistentExtraTextForOverlay}
              onActiveShotNumberChange={handleActiveShotNumberChange}
              manualHighlightLabel={shotOverlayManualHighlightLabel}
            />
          )}
        </Box>

        {/* AI panel (split mode; shrinks video area instead of overflowing) */}
        {showAi && aiDrawerMode === "split" && aiOpen && (
          <Box
            sx={{
              flex: "0 0 auto",
              height: "100%",
              minHeight: 0,
              minWidth: 0,
              ml: 1.2,
            }}
          >
            <Paper
              elevation={8}
              sx={{
                width: aiPanelWidth, //aiTurns.length > 0 ? 760 : 360,
                height: "97%",
                borderRadius: 2,
                overflow: "hidden",
                bgcolor: "rgba(20,20,20,0.92)",
                border: "1px solid rgba(255,255,255,0.16)",
                backdropFilter: "blur(10px)",
                //marginBottom: "18px !important",

                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              {/* header */}
              <Box
                sx={{
                  px: 1.25,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  flex: "0 0 auto",
                  bgcolor: "rgba(0,0,0,0.30)",
                  borderBottom: "1px solid rgba(255,255,255,0.10)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 900,
                    letterSpacing: "0.2px",
                    color: "rgba(255,255,255,0.95)",
                  }}
                >
                  AI
                </Typography>

                <Button
                  size="small"
                  variant="text"
                  onClick={() => setAiOpen(false)}
                  sx={{
                    textTransform: "none",
                    color: "rgba(255,255,255,0.85)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    borderRadius: 1.5,
                    px: 1,
                    py: 0.35,
                    minWidth: 0,
                    "&:hover": {
                      borderColor: "rgba(255,255,255,0.28)",
                      bgcolor: "rgba(255,255,255,0.06)",
                    },
                  }}
                >
                  Close AI Window
                </Button>
              </Box>

              {/* content */}

              <Box
                sx={{
                  flex: "1 1 auto",
                  minHeight: 0,
                  display: "flex",
                  overflow: "hidden",
                  fontSize: 13, // "12px !important",
                  lineHeight: 1.25,
                }}
              >
                {/* LEFT: Responses (only when there are turns) */}
                {aiTurns.length > 0 && (
                  <Box
                    sx={{
                      flex: "0 0 500px",
                      minWidth: 0,
                      borderRight: "1px solid rgba(255,255,255,0.10)",
                      display: "flex",
                      flexDirection: "column",
                      minHeight: 0,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        px: 1.2,
                        py: 0.9,
                        flex: "0 0 auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        bgcolor: "rgba(0,0,0,0.18)",
                        borderBottom: "1px solid rgba(255,255,255,0.08)",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 800,
                          color: "rgba(255,255,255,0.88)",
                        }}
                      >
                        Responses
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 13,
                          opacity: 0.65,
                          color: "rgba(255,255,255,0.8)",
                        }}
                      >
                        {aiTurns.length} turn
                        {aiTurns.length === 1 ? "" : "s"}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        p: 1.1,
                        flex: "1 1 auto",
                        minHeight: 0,
                        overflow: "auto",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        position: "relative",
                        fontSize: "13px !important",
                      }}
                    >
                      {aiTurns.map((t) => (
                        <Box
                          key={t.id}
                          sx={{
                            border: "1px solid rgba(255,255,255,0.12)",
                            borderRadius: 1.5,
                            p: 1,
                            bgcolor: "rgba(0,0,0,0.18)",
                            position: "relative",
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => deleteTurn(t.id)}
                            sx={{
                              position: "relative",
                              float: "right",
                              top: 0,
                              right: 0,
                              color: "red",
                              border: "1px solid transparent",
                              "&:hover": { border: "1px solid yellow" },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                            <Box
                              sx={{
                                color: "white",
                                fontSize: "12px",
                                fontStyle: "italic",
                              }}
                            >
                              Delete AI Turn
                            </Box>
                          </IconButton>
                          <Typography
                            sx={{
                              fontSize: 13, // "12px !important",
                              lineHeight: 1.25,
                              color: "rgba(255,255,255,0.92)",
                            }}
                          >
                            <span style={{ fontWeight: 800 }}>Q:</span>{" "}
                            {t.question}
                          </Typography>

                          <Box sx={{}}>
                            <Typography
                              sx={{
                                // fontSize: 13,
                                opacity: 0.7,
                                fontWeight: 800,
                              }}
                            >
                              A:
                            </Typography>
                            <Box sx={{ mt: 0.0 }}>{renderAiAnswer(t)}</Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* RIGHT: Controls + question */}
                <Box
                  sx={{
                    flex: "1 1 auto",
                    minWidth: 0,
                    minHeight: 0,
                    overflow: "auto",
                    p: 1.2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={aiRangeMode}
                    onChange={(_, v) => v && setAiRangeMode(v)}
                    sx={{
                      alignSelf: "flex-start",
                      "& .MuiToggleButton-root": {
                        color: "rgba(255,255,255,0.85)",
                        borderColor: "rgba(255,255,255,0.18)",
                        textTransform: "none",
                        px: 1.1,
                        py: 0.45,
                      },
                      "& .MuiToggleButton-root.Mui-selected": {
                        bgcolor: "rgba(255,255,255,0.14)",
                        borderColor: "rgba(255,255,255,0.28)",
                        color: "rgba(255,255,255,0.95)",
                      },
                      "& .MuiToggleButton-root.Mui-disabled": {
                        color: "rgba(255,255,255,0.35)",
                        borderColor: "rgba(255,255,255,0.10)",
                      },
                    }}
                  >
                    <ToggleButton value="loop" disabled={!loop.enabled}>
                      Use Loop
                    </ToggleButton>
                    <ToggleButton value="full">Use Full Rally</ToggleButton>
                  </ToggleButtonGroup>

                  {/* range (✅ one line, no ms parentheticals) */}
                  <Box
                    sx={{
                      fontSize: 12,
                      color: "rgba(255,255,255,0.85)",
                      border: "1px solid rgba(255,255,255,0.10)",
                      borderRadius: 1.5,
                      px: 1,
                      py: 0.85,
                      bgcolor: "rgba(0,0,0,0.16)",
                    }}
                  >
                    Range:{" "}
                    {effectiveAiRange
                      ? `${fmtMsRel(effectiveAiRange.startMs - row.segmentStartMs)} → ${fmtMsRel(
                          effectiveAiRange.endMs - row.segmentStartMs,
                        )}`
                      : "—"}
                    {!packageId && (
                      <div style={{ marginTop: 6, opacity: 0.65 }}>
                        ⚠️ No <code>packageId</code> — Ask AI is disabled.
                      </div>
                    )}
                  </Box>

                  {/* question (✅ preserved) */}
                  <TextField
                    size="small"
                    value={aiQuestionText}
                    onChange={(e) => setAiQuestionText(e.target.value)}
                    placeholder="Ask a question about this rally…"
                    multiline
                    minRows={2}
                    maxRows={6}
                    sx={{
                      "& .MuiInputBase-root": {
                        color: "rgba(255,255,255,0.90)",
                        bgcolor: "rgba(0,0,0,0.22)",
                        borderRadius: 1.5,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255,255,255,0.14)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255,255,255,0.22)",
                      },
                    }}
                  />

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={
                        !row ||
                        !packageId ||
                        aiBusy ||
                        !aiQuestionText.trim() ||
                        !effectiveAiRange
                      }
                      onClick={askAi} // ✅ preserved
                    >
                      {aiBusy ? "Asking…" : "Ask AI"}
                    </Button>

                    {aiBusy && (
                      <CircularProgress
                        size={16}
                        sx={{ color: "rgba(255,255,255,0.75)" }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {!disablePopout && row && (
        <VideoPopout
          renderPlayerIdentity={renderPlayerIdentity}
          resolvePlayerAvatarSrc={resolvePlayerAvatarSrc}
          titleNode={titleNode}
          pounceOverlay={pounceOverlay}
          focusedPlayerId={focusedPlayerId}
          insightsToCvPlayerIndex={insightsToCvPlayerIndex}
          cvJson={cvJson}
          aiContextLabel={aiContextLabel}
          focusedFilter={focusedFilter}
          open={popoutOpen}
          title={`Rally ${row.rallyIndex + 1}`}
          seedTimeSec={popoutSeedRef.current?.t ?? 0}
          seedPaused={popoutSeedRef.current?.paused ?? paused}
          seedLoopRegion={
            popoutSeedRef.current
              ? {
                  enabled: popoutSeedRef.current.loop.enabled,
                  aSec: popoutSeedRef.current.loop.aSec,
                  bSec: popoutSeedRef.current.loop.bSec,
                  nonce: popoutSeedRef.current.nonce,
                }
              : undefined
          }
          onClose={(t, p, loopFromPopout, nextBallReadFromPopout) =>
            closePopout(t, p, loopFromPopout, nextBallReadFromPopout)
          }
          // onClose={(t, p) => closePopout(t, p)}
          packageId={packageId}
          row={row}
          rows={rows}
          activeShotNumber={activeShotNumber}
          beginningScore={beginningScore}
          endingScore={endingScore}
          showCondensedTimeline={false}
          fontColor={fontColor}
          controlsPlacement={controlsPlacement}
          firefightsClips={firefightsClips}
          firefightRallySet={firefightRallySet}
          showRateControls
          showUltraSlowRate={props.showUltraSlowRate}
          showTitle
          showScoreEtc
          autoPlayOnRallyChange={autoPlayOnRallyChange}
          showAi={true} // always let it be available in the popout showAi}
          showLoopScrubber
          defaultLoopUiOpen
          disableShotStepControls={disableShotStepControls}
          showShotSpaceButton={showShotSpaceButton}
          onOpenShotSpace={onOpenShotSpace}
          rallyLensMoments={rallyLensMoments}
          initialRallyLens={popoutInitialRallyLens ?? activeRallyLens}
          initialRallyLensMomentKey={
            popoutSeedRef.current?.rallyLensMomentKey ??
            initialRallyLensMomentKey ??
            null
          }
          showRallyLensControls={showRallyLensControls}
          initialNextBallReadSeed={
            popoutSeedRef.current?.nextBallRead
              ? {
                  ...popoutSeedRef.current.nextBallRead,
                  nonce: popoutSeedRef.current.nonce,
                }
              : undefined
          }
          onOpenRallyFromSimilarPopup={onOpenRallyFromSimilarPopup}
          // //onAskAi={onAskAi}
        />
      )}

      <Dialog
        open={socialExportOpen}
        onClose={() => {
          if (!socialExportBusy) setSocialExportOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 30000 }}
        BackdropProps={{
          sx: { zIndex: 29999 },
        }}
        PaperProps={{
          sx: {
            bgcolor: "#111",
            color: "rgba(255,255,255,0.92)",
            border: "1px solid rgba(255,255,255,0.16)",
            position: "relative",
            zIndex: 30001,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 900, pb: 1 }}>
          Export clip for social media
        </DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <Typography
            sx={{
              mb: 1.25,
              fontSize: 13,
              color: "rgba(255,255,255,0.62)",
            }}
          >
            9:16 MP4 - 1080x1920
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            maxRows={5}
            label="Caption text (optional)"
            value={socialExportCaption}
            onChange={(event) => setSocialExportCaption(event.target.value)}
            inputProps={{ maxLength: 180 }}
            disabled={socialExportBusy}
            sx={{
              "& .MuiInputBase-root": {
                color: "rgba(255,255,255,0.92)",
                bgcolor: "rgba(255,255,255,0.04)",
              },
              "& .MuiInputLabel-root": {
                color: "rgba(255,255,255,0.62)",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255,255,255,0.18)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255,255,255,0.30)",
              },
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={socialExportIncludeShotLabels}
                onChange={(event) =>
                  setSocialExportIncludeShotLabels(event.target.checked)
                }
                disabled={socialExportBusy}
                sx={{
                  color: "rgba(255,255,255,0.54)",
                  "&.Mui-checked": { color: "#66b3ff" },
                }}
              />
            }
            label="Include shot labels"
            sx={{
              mt: 1.1,
              color: "rgba(255,255,255,0.82)",
              "& .MuiFormControlLabel-label": {
                fontSize: 13,
                fontWeight: 700,
              },
            }}
          />
          {socialExportError && (
            <Typography sx={{ mt: 1, color: "#ff8a80", fontSize: 13 }}>
              {socialExportError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setSocialExportOpen(false)}
            disabled={socialExportBusy}
            sx={{ color: "rgba(255,255,255,0.75)", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={exportSocialMp4}
            disabled={!row || socialExportBusy}
            startIcon={
              socialExportBusy ? (
                <CircularProgress size={16} sx={{ color: "white" }} />
              ) : (
                <MovieCreationOutlinedIcon fontSize="small" />
              )
            }
            sx={{ textTransform: "none", fontWeight: 900 }}
          >
            {socialExportBusy ? "Exporting..." : "Export clip"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* <SimilarRalliesPopup
        insightsToCvPlayerIndex={insightsToCvPlayerIndex}
        open={similarPopupOpen}
        rect={similarPopupRect}
        onChangeRect={setSimilarPopupRect}
        onClose={() => setSimilarPopupOpen(false)}
        sourceRallyNumber={row?.rallyIndex != null ? row.rallyIndex + 1 : null}
        sourceFingerprint={
          row?.rallyIndex != null
            ? (similarRallyFingerprints.find(
                (f) => f.rallyIndex === row.rallyIndex,
              ) ?? null)
            : null
        }
        mode={similarMode}
        results={similarResults}
        rows={rows}
        packageId={packageId}
        aiContextLabel={aiContextLabel}
        fontColor={fontColor}
        controlsPlacement={controlsPlacement}
        firefightsClips={firefightsClips}
        firefightRallySet={firefightRallySet}
        focusedFilter={focusedFilter}
        onOpenRally={
          onOpenRallyFromSimilarPopup
            ? (rallyIndex) => {
                onOpenRallyFromSimilarPopup(rallyIndex);
              }
            : undefined
        }
      /> */}
    </Box>
  );
});

// function sourceRallySummaryBits(fp: RallyFingerprint | null): string[] {
//   if (!fp) return [];

//   const bits: string[] = [];
//   const g = fp.global;

//   bits.push(`${g.rallyLength} shots`);

//   if (g.endingPattern) {
//     bits.push(g.endingPattern.replace(/_/g, " "));
//   } else if (g.endingShotType) {
//     bits.push(g.endingShotType);
//   }

//   if (g.hasFirefight) bits.push("firefight");
//   if (g.containsFastReaction) bits.push("fast reaction");

//   if (g.pressureBucket !== "unknown") {
//     bits.push(`${g.pressureBucket} pressure`);
//   }

//   if (g.firstKitchenTeam != null) {
//     bits.push(`kitchen first: team ${g.firstKitchenTeam}`);
//   }

//   if (g.bothTeamsReachedKitchen) {
//     bits.push("both teams reached kitchen");
//   }

//   return bits;
// }

// function SimilarRalliesPopup(props: {
//   open: boolean;
//   rect: { x: number; y: number; w: number; h: number };
//   onChangeRect: (next: { x: number; y: number; w: number; h: number }) => void;
//   onClose: () => void;

//   sourceRallyNumber: number | null;
//   sourceFingerprint: RallyFingerprint | null;

//   mode: SimilarityMode;
//   results: SimilarRallyResult[];

//   rows: KitchenArrivalRow[];
//   packageId: string;
//   aiContextLabel: string;
//   fontColor: string;
//   controlsPlacement: "top" | "bottom" | "left";
//   firefightsClips: FirefightClip[];
//   firefightRallySet: Set<number>;
//   focusedFilter: FocusedReasonStakeForTeam | null;

//   onOpenRally?: (rallyIndex: number) => void;
//   forceMuted?: boolean;
//   previewMode?: boolean;
//   insightsToCvPlayerIndex: PlayerIndexMap;
// }) {
//   const {
//     open,
//     rect,
//     onChangeRect,
//     onClose,
//     sourceRallyNumber,
//     mode,
//     results,
//     onOpenRally,
//     rows,
//     packageId,
//     aiContextLabel,
//     fontColor,
//     controlsPlacement,
//     firefightsClips,
//     firefightRallySet,
//     focusedFilter,
//     sourceFingerprint,
//     insightsToCvPlayerIndex,
//   } = props;

//   const draggingRef = React.useRef<null | { dx: number; dy: number }>(null);

//   React.useEffect(() => {
//     if (!open) return;

//     const onMove = (e: MouseEvent) => {
//       const d = draggingRef.current;
//       if (!d) return;
//       onChangeRect({
//         ...rect,
//         x: e.clientX - d.dx,
//         y: e.clientY - d.dy,
//       });
//     };

//     const onUp = () => {
//       draggingRef.current = null;
//     };

//     window.addEventListener("mousemove", onMove);
//     window.addEventListener("mouseup", onUp);

//     return () => {
//       window.removeEventListener("mousemove", onMove);
//       window.removeEventListener("mouseup", onUp);
//     };
//   }, [open, onChangeRect, rect]);

//   if (!open) return null;

//   // main return
//   return ReactDOM.createPortal(
//     <Box
//       sx={{
//         position: "fixed",
//         left: rect.x,
//         top: rect.y,
//         width: rect.w,
//         height: rect.h,
//         zIndex: 9999,
//         bgcolor: "rgba(10,10,10,0.92)",
//         border: "1px solid rgba(255,255,255,0.16)",
//         borderRadius: 2,
//         boxShadow: "0 20px 60px rgba(0,0,0,0.55)",
//         overflow: "hidden",
//         resize: "both",
//       }}
//     >
//       <Box
//         onMouseDown={(e: any) => {
//           if (e.button !== 0) return;
//           draggingRef.current = {
//             dx: e.clientX - rect.x,
//             dy: e.clientY - rect.y,
//           };
//           e.preventDefault();
//           e.stopPropagation();
//         }}
//         sx={{
//           height: 40,
//           px: 1,
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "space-between",
//           cursor: "move",
//           userSelect: "none",
//           bgcolor: "rgba(0,0,0,0.45)",
//           borderBottom: "1px solid rgba(255,255,255,0.10)",
//         }}
//       >
//         <Typography
//           sx={{ fontSize: 13, opacity: 0.9, color: "rgba(255,255,255,0.9)" }}
//         >
//           Similar Rallies - {similarModeLabel(mode)}
//         </Typography>

//         <Button
//           size="small"
//           variant="outlined"
//           onClick={onClose}
//           sx={{
//             minWidth: 0,
//             px: 1.2,
//             py: 0.3,
//             borderColor: "rgba(255,255,255,0.25)",
//             color: "rgba(255,255,255,0.9)",
//           }}
//         >
//           Close
//         </Button>
//       </Box>

//       <Box
//         sx={{
//           height: "calc(100% - 40px)",
//           display: "flex",
//           flexDirection: "column",
//           minHeight: 0,
//           bgcolor: "rgba(245,245,245,0.98)",
//         }}
//       >
//         <Box
//           sx={{
//             px: 1.2,
//             py: 0.9,
//             borderBottom: "1px solid rgba(0,0,0,0.10)",
//             bgcolor: "rgba(0,0,0,0.03)",
//             display: "flex",
//             flexDirection: "column",
//             gap: 0.5,
//           }}
//         >
//           <Box
//             sx={{ fontSize: 14, fontWeight: 900, color: "rgba(0,0,0,0.84)" }}
//           >
//             Source rally: {sourceRallyNumber ?? "—"}
//           </Box>

//           <Box sx={{ fontSize: 12, color: "rgba(0,0,0,0.62)" }}>
//             Mode: {similarModeLabel(mode)} : Top {results.length} matching
//             rallies
//           </Box>

//           <Box
//             sx={{
//               display: "flex",
//               flexWrap: "wrap",
//               gap: 0.45,
//             }}
//           >
//             {sourceRallySummaryBits(sourceFingerprint).map((bit) => (
//               <Box
//                 key={bit}
//                 sx={{
//                   px: 0.7,
//                   py: 0.24,
//                   borderRadius: 999,
//                   fontSize: 11,
//                   fontWeight: 700,
//                   color: "rgba(0,0,0,0.74)",
//                   bgcolor: "rgba(0,0,0,0.05)",
//                   border: "1px solid rgba(0,0,0,0.08)",
//                   whiteSpace: "nowrap",
//                 }}
//               >
//                 {bit}
//               </Box>
//             ))}
//           </Box>
//         </Box>

//         <Box
//           sx={{
//             flex: "1 1 auto",
//             minHeight: 0,
//             overflow: "auto",
//             p: 1,
//             display: "flex",
//             flexDirection: "column",
//             gap: 1,
//             bgcolor: "rgba(248,248,248,0.98)",
//           }}
//         >
//           {results.length === 0 ? (
//             <Box sx={{ fontSize: 12, color: "rgba(0,0,0,0.62)", p: 1 }}>
//               No similar rallies found.
//             </Box>
//           ) : (
//             results.map((result) => {
//               const resultRow =
//                 rows.find((r) => r.rallyIndex === result.rallyIndex) ?? null;

//               return (
//                 <Box
//                   key={result.rallyIndex}
//                   sx={{
//                     borderRadius: 2,
//                     border: "1px solid rgba(0,0,0,0.10)",
//                     bgcolor: "white",
//                     overflow: "hidden",
//                     boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
//                   }}
//                 >
//                   <Box
//                     sx={{
//                       display: "flex",
//                       alignItems: "stretch",
//                       minHeight: 300,
//                     }}
//                   >
//                     <Box
//                       sx={{
//                         width: 200,
//                         minWidth: 200,
//                         maxWidth: 200,
//                         borderRight: "1px solid rgba(0,0,0,0.08)",
//                         bgcolor: "rgba(0,0,0,0.02)",
//                         p: 1,
//                         display: "flex",
//                         flexDirection: "column",
//                         gap: 0.75,
//                       }}
//                     >
//                       <Box>
//                         <Box
//                           sx={{
//                             fontSize: 13,
//                             fontWeight: 900,
//                             color: "rgba(0,0,0,0.88)",
//                             lineHeight: 1.1,
//                           }}
//                         >
//                           Rally {result.rallyNumber}
//                         </Box>
//                         {/* <Box
//                           sx={{
//                             mt: 0.15,
//                             fontSize: 11,
//                             color: "rgba(0,0,0,0.60)",
//                           }}
//                         >
//                           score {result.score.toFixed(1)}
//                         </Box> */}
//                       </Box>

//                       <Box
//                         sx={{
//                           display: "flex",
//                           flexWrap: "wrap",
//                           gap: 0.45,
//                         }}
//                       >
//                         {result.reasons.slice(0, 6).map((reason) => (
//                           <Box
//                             key={reason}
//                             sx={{
//                               px: 0.65,
//                               py: 0.22,
//                               borderRadius: 999,
//                               fontSize: 9,
//                               fontWeight: 700,
//                               color: "rgba(0,0,0,0.74)",
//                               bgcolor: "rgba(0,0,0,0.05)",
//                               border: "1px solid rgba(0,0,0,0.08)",
//                               whiteSpace: "nowrap",
//                             }}
//                           >
//                             {similarityReasonLabel(reason)}
//                           </Box>
//                         ))}
//                       </Box>

//                       {!!onOpenRally && (
//                         <Box sx={{ pt: 0.25 }}>
//                           <Button
//                             size="small"
//                             variant="outlined"
//                             onClick={() => onOpenRally(result.rallyIndex)}
//                             sx={{
//                               minWidth: 0,
//                               px: 1,
//                               height: 26,
//                               textTransform: "none",
//                               fontWeight: 800,
//                             }}
//                           >
//                             Open Main
//                           </Button>
//                         </Box>
//                       )}
//                     </Box>

//                     <Box
//                       sx={{
//                         flex: "1 1 auto",
//                         minWidth: 0,
//                         width: 0,
//                         height: 300,
//                         minHeight: 300,
//                         bgcolor: "black",
//                         position: "relative",
//                       }}
//                     >
//                       {resultRow ? (
//                         <ActiveRallyVideo
//                           insightsToCvPlayerIndex={insightsToCvPlayerIndex}
//                           key={`similar-inline-${result.rallyIndex}`}
//                           focusedFilter={focusedFilter}
//                           packageId={packageId}
//                           aiContextLabel={`${aiContextLabel} / similar-rallies-inline`}
//                           row={resultRow}
//                           rows={rows}
//                           activeShotNumber={null}
//                           beginningScore={
//                             (resultRow as any).beginningScore ?? null
//                           }
//                           endingScore={(resultRow as any).endingScore ?? null}
//                           showCondensedTimeline={false}
//                           fontColor={fontColor}
//                           controlsPlacement={controlsPlacement}
//                           firefightsClips={firefightsClips}
//                           firefightRallySet={firefightRallySet}
//                           showRateControls={false}
//                           showTitle={false}
//                           showScoreEtc={false}
//                           autoPlayOnRallyChange={false}
//                           showAi={false}
//                           showLoopScrubber={true}
//                           showSimilarControls={false}
//                           defaultLoopUiOpen={false}
//                           disablePopout={true}
//                           showPopoutButton={false}
//                           forceMuted={true}
//                           previewMode={true}
//                         />
//                       ) : (
//                         <Box
//                           sx={{
//                             height: "100%",
//                             display: "flex",
//                             alignItems: "center",
//                             justifyContent: "center",
//                             color: "rgba(255,255,255,0.65)",
//                             fontSize: 14,
//                           }}
//                         >
//                           Rally not found
//                         </Box>
//                       )}
//                     </Box>
//                   </Box>
//                 </Box>
//               );
//             })
//           )}
//         </Box>
//       </Box>
//     </Box>,
//     document.body,
//   );
// }
